/* MANILAI — core behaviours. Animations use GSAP + ScrollTrigger for performance: the old scroll handler wrote CSS variables to every word on every frame, triggering style recalculation and repainting text plus shadows. ScrollTrigger uses one page-wide loop, batches geometry reads and writes only changes. More importantly, animate opacity rather than color: recoloring repaints shadows approaching a 180px radius, while an element's own layer can composite opacity on the GPU. Sections only declare attributes, without knowing about GSAP: data-mnl-reveal = block entrance (data-mnl-stagger="90" for stagger); data-mnl-scrolltext = word-by-word reveal; data-mnl-nosplit = treat a scrolltext descendant as one word; data-mnl-drag = draggable horizontal carousel; data-mnl-track = horizontal track driven by vertical scroll; data-mnl-popup = expanded card over a scrim; data-mnl-scrub = Pen video frame selected by a list item; data-mnl-app = Application arrow slider; data-mnl-steps = active-item highlighting, with data-mnl-steps-scope; data-mnl-clock = header clock; data-mnl-video = play only while visible, with "once" playing once and holding the last frame; data-mnl-loop = CSS loop active only while visible; data-mnl-capture-note = handwritten lines unblur and repeat after leaving the card; data-mnl-header = header, with data-mnl-scheme="dark|light" on sections. data-mnl-smooth on <body> enables smooth wheel scrolling; its value is the percentage of the remaining distance covered per frame. If GSAP fails to load, all content remains static and visible. */
(function () {
  'use strict';

  function syncCookieSettingsAccess() {
    var cmp = document.querySelector('pandectes-cmp');
    if (!cmp) return false;
    cmp.inert = !document.documentElement.classList.contains('mnl-cookie-settings-open');
    return true;
  }
  if (!syncCookieSettingsAccess()) {
    var cookieSettingsObserver = new MutationObserver(function () {
      if (syncCookieSettingsAccess()) cookieSettingsObserver.disconnect();
    });
    cookieSettingsObserver.observe(document.documentElement, { childList:true, subtree:true });
    window.setTimeout(function () { cookieSettingsObserver.disconnect(); }, 30000);
  }

  var cookieSettingsRetry = null;
  // Reveal the existing CMP before its native reopen-link handler runs.
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || link.hash !== '#reopenBanner') return;
    document.documentElement.classList.add('mnl-cookie-settings-open');
    syncCookieSettingsAccess();
    window.clearTimeout(cookieSettingsRetry);
    if (typeof link.onclick === 'function') return;
    event.preventDefault();
    var attempts = 0;
    function waitForCookieSettings() {
      if (!link.isConnected) return;
      if (typeof link.onclick === 'function') {
        link.click();
      } else if (++attempts < 300) {
        cookieSettingsRetry = window.setTimeout(waitForCookieSettings, 100);
      }
    }
    cookieSettingsRetry = window.setTimeout(waitForCookieSettings, 100);
  }, true);

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var animated = !!(gsap && ScrollTrigger) && !reduced;

  if (gsap && ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });
  }

  // Set in initHeaderScheme. Needed by sections that change their scene
  // during scrolling without crossing section boundaries, which would otherwise
  // leave the header's trigger inactive.
  var syncHeader = function () {};
  document.documentElement.classList.toggle('mnl-anim', animated);

  // Number of step controllers currently holding page scrolling.
  // While nonzero, smooth scrolling must not handle the wheel:
  // the step controller consumes events itself, and two handlers on one wheel
  // would cause a jump.
  var scrollCaptured = 0;
  // Cancel smooth-scroll interpolation and settle at the actual page position.
  // Assigned in initSmoothScroll; a no-op until then.
  var smoothStop = function () {};

  /* 1. Block entrances */
  function initReveal(root) {
    var nodes = root.querySelectorAll('[data-mnl-reveal]:not([data-mnl-on])');
    Array.prototype.forEach.call(nodes, function (n, i) {
      n.setAttribute('data-mnl-on', '1');
      // Mobile hero copy has its own immediate 300ms CSS entrance.
      if (window.matchMedia('(max-width: 767px)').matches && n.closest('.mnl-hero, .mnl-exp-hero, .mnl-hiw-hero, .mnl-ins-hero, .mnl-mem-hero')) return;
      if (!animated) return;
      var step = parseInt(n.getAttribute('data-mnl-stagger') || '0', 10);
      gsap.from(n, {
        opacity: 0,
        y: 22,
        duration: 0.9,
        delay: (step ? (i * step) / 1000 : 0),
        ease: 'power2.out',
        scrollTrigger: { trigger: n, start: 'top 88%', once: true }
      });
    });
  }

  /* 2. Word-by-word text reveal. Words receive --mnl-p (their fraction along the phrase), which the section uses to position the gradient. ScrollTrigger scrubs opacity. */
  function splitWords(el) {
    if (el.dataset.mnlSplit === 'done') return;
    var tmp = document.createElement('div');
    tmp.innerHTML = el.innerHTML;

    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (child) {
        if (child.nodeType === 3) {
          var frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            var s = document.createElement('span');
            s.className = 'mnl-word';
            s.textContent = part;
            frag.appendChild(s);
          });
          node.replaceChild(frag, child);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          // Treat an element with data-mnl-nosplit as one complete word;
          // otherwise its gradient fill would be split apart.
          if (child.hasAttribute && child.hasAttribute('data-mnl-nosplit')) {
            child.className += (child.className ? ' ' : '') + 'mnl-word';
          } else {
            walk(child);
          }
        }
      });
    })(tmp);

    el.innerHTML = tmp.innerHTML;
    el.dataset.mnlSplit = 'done';
  }

  function initScrollText(root) {
    var nodes = root.querySelectorAll('[data-mnl-scrolltext]:not([data-mnl-on])');
    Array.prototype.forEach.call(nodes, function (el) {
      el.setAttribute('data-mnl-on', '1');
      splitWords(el);
      var words = el.querySelectorAll('.mnl-word');
      if (!words.length) return;

      for (var k = 0; k < words.length; k++) {
        words[k].style.setProperty('--mnl-p', (k / Math.max(1, words.length - 1)).toFixed(4));
      }

      /* Continuous fill across words. The reveal animates opacity, which applies to the element painting itself, so the fill must belong to each word, not the paragraph. A separate glyph-clipped gradient on each word would restart repeatedly instead of spanning the phrase. Give each word its fractional position within the box and offset its background accordingly. Fractions are independent of page zoom, whereas getBoundingClientRect returns scaled pixels. Only paint changes, not layout, so rule 12 is preserved. */
      if (el.hasAttribute('data-mnl-scrolltext-fill')) {
        var box = el.getBoundingClientRect();
        if (box.width > 0) {
          for (var m = 0; m < words.length; m++) {
            var wr = words[m].getBoundingClientRect();
            words[m].style.setProperty('--mnl-wxf', ((wr.left - box.left) / box.width).toFixed(5));
          }
          /* Use the same class as initFill: continuous word fill starts only after measurement. data-mnl-on is shared across handlers (rule 96), so a separate data-mnl-fill on this paragraph would not run. */
          el.classList.add('is-split');
        }
      }

      if (!animated) return;

      var dim = parseFloat(getComputedStyle(el).getPropertyValue('--mnl-dim')) || 0.3;
      gsap.fromTo(
        words,
        { opacity: dim },
        {
          opacity: 1,
          ease: 'none',
          stagger: { each: 0.08, from: 'start' },
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            end: 'bottom 55%',
            scrub: 0.4
          }
        }
      );
    });
  }

  /* 3. Track driven by page scrolling. The scene exceeds the viewport (1016 versus roughly 820 on a laptop), so moving only while the section passes would expose complete cards for about 370px of scroll, but they must travel 790px. Hold the scene using CSS position: sticky on .__stage. ScrollTrigger pin previously caused a jump because it changed position to fixed and inserted a spacer, forcing layout in that frame. Native sticky changes neither and avoids a frame cost at attachment/release. JavaScript computes --mnl-stage-h (scene height; hold oversized scenes at the bottom edge) and --mnl-runway (extra section height consumed by the track). Travel equals the track's overflow width, allowing arbitrary card counts and widths. Below 768px use native horizontal scrolling: swiping is natural on phones, so holding the scene is unnecessary. */
  function initTrack(root) {
    root.querySelectorAll('[data-mnl-track]:not([data-mnl-on])').forEach(function (track) {
      track.setAttribute('data-mnl-on', '1');
      var view = track.parentElement;
      if (!view || !animated) return;

      var scene = track.closest('[data-mnl-track-scene]');
      var stage = scene && scene.querySelector('[data-mnl-track-stage]');
      if (!scene || !stage) return;

      var speed = parseFloat(track.getAttribute('data-mnl-track-speed') || '100') / 100;
      if (!(speed > 0)) speed = 1;
      // Extra hold after the track arrives, so scene release does not coincide
      // with the cards' final movement.
      var tail = 120;

      function travel() {
        return Math.max(0, track.scrollWidth - view.clientWidth);
      }

      // Liquid already calculated the runway from the design width. Refine it
      // using the actual viewport width and measured heading height.
      function measure() {
        scene.style.setProperty('--mnl-stage-h', stage.offsetHeight + 'px');
        scene.style.setProperty('--mnl-runway', (Math.round(travel() * speed) + tail) + 'px');
      }

      var mq = window.matchMedia('(min-width: 768px)');
      var tween = null;

      function build() {
        if (tween) return;
        measure();
        tween = gsap.to(track, {
          x: function () { return -travel(); },
          ease: 'none',
          scrollTrigger: {
            // Use the section as the trigger, not the sticky stage: its
            // getBoundingClientRect is already displaced by the sticky offset during
            // recalculation, which would produce incorrect boundaries.
            // The section retains reliable coordinates.
            trigger: scene,
            start: function () { return 'top bottom-=' + stage.offsetHeight; },
            // Exactly the section's extra height beyond the scene, minus the tail.
            end: function () {
              return '+=' + Math.max(1, scene.offsetHeight - stage.offsetHeight - tail);
            },
            scrub: 0.4,
            invalidateOnRefresh: true
          }
        });
      }

      function teardown() {
        if (!tween) return;
        if (tween.scrollTrigger) tween.scrollTrigger.kill();
        tween.kill();
        tween = null;
        gsap.set(track, { x: 0 });
        scene.style.removeProperty('--mnl-stage-h');
        scene.style.removeProperty('--mnl-runway');
      }

      function sync() {
        if (mq.matches) build();
        else teardown();
      }

      sync();
      mq.addEventListener('change', sync);
      // refreshInit runs before boundary measurement: update runway length first,
      // then let ScrollTrigger read the coordinates.
      ScrollTrigger.addEventListener('refreshInit', function () {
        if (mq.matches) measure();
      });
    });
  }

  /* 4. Expanded card. The card moves forward, its backdrop blurs, and a progress bar runs across the top. Text columns turn white sequentially as the bar reaches them. Column thresholds are measured on opening as fractional positions along the bar, allowing arbitrary column counts and widths. Text is stored in each card's data attributes and copied on opening: one dialog per section, not per card. */
  function initPopup(root) {
    root.querySelectorAll('[data-mnl-popup]:not([data-mnl-on])').forEach(function (modal) {
      modal.setAttribute('data-mnl-on', '1');

      var scope = modal.closest('[data-mnl-popup-scope]') || document;
      var box = modal.querySelector('[data-mnl-popup-card]');
      var scrim = modal.querySelector('[data-mnl-popup-scrim]');
      var plate = modal.querySelector('[data-mnl-popup-plate]');
      var bar = modal.querySelector('[data-mnl-popup-bar]');
      var cols = Array.prototype.slice.call(modal.querySelectorAll('[data-mnl-popup-col]'));
      var dur = parseFloat(modal.getAttribute('data-mnl-popup-duration') || '2') || 2;
      var opener = null;
      var tl = null;

      // Fill the bar by clipping its right edge; scaling would compress the gradient.
      // This preserves its positions. Leave extra space above, below and left
      // to avoid clipping blurred halos.
      var BAR_EMPTY = 'inset(-24px 100% -24px -24px)';
      var BAR_FULL = 'inset(-24px 0% -24px -24px)';

      function light(col) { col.classList.add('is-lit'); }

      function run() {
        cols.forEach(function (col) { col.classList.remove('is-lit'); });
        if (tl) tl.kill();

        if (!animated) {
          cols.forEach(light);
          if (bar) bar.style.clipPath = BAR_FULL;
          return;
        }

        tl = gsap.timeline();
        if (scrim) tl.fromTo(scrim, { opacity: 0 }, { opacity: 1, duration: 0.3, ease: 'none' }, 0);
        if (box) {
          // Move slightly forward: the card comes out of depth and settles into place.
          tl.fromTo(
            box,
            { scale: 0.92, y: 14, opacity: 0 },
            { scale: 1, y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' },
            0
          );
        }
        if (!bar) return;

        tl.fromTo(
          bar,
          { clipPath: BAR_EMPTY },
          { clipPath: BAR_FULL, duration: dur, ease: 'none' },
          0
        );

        var track = bar.parentElement.getBoundingClientRect();
        cols.forEach(function (col) {
          var at = track.width ? (col.getBoundingClientRect().left - track.left) / track.width : 0;
          if (at < 0) at = 0;
          if (at > 1) at = 1;
          tl.call(light, [col], dur * at);
        });
      }

      function fill(card) {
        /* The expanded card is the card itself, not a screenshot. It has the same size as in the track, and rebuilding the cards as live layers removed the complete-card image (the last transparent card never had one). Clone the clicked card into the dialog. Remove the open-dialog and already-initialized markers so the clone does not receive handlers or intercept interactions. */
        if (plate && plate.tagName === 'IMG') {
          var src = card.getAttribute('data-mnl-popup-src');
          if (src) {
            plate.setAttribute('src', src);
            plate.setAttribute('alt', card.getAttribute('data-mnl-popup-alt') || '');
          }
        } else if (plate) {
          var copy = card.cloneNode(true);
          copy.removeAttribute('data-mnl-popup-open');
          copy.removeAttribute('data-mnl-on');
          copy.removeAttribute('id');
          copy.setAttribute('tabindex', '-1');
          copy.setAttribute('aria-hidden', 'true');
          plate.textContent = '';
          plate.appendChild(copy);
        }
        cols.forEach(function (col) {
          var key = col.getAttribute('data-mnl-popup-col');
          var slot = col.querySelector('[data-mnl-popup-text]');
          if (slot) slot.textContent = card.getAttribute('data-mnl-popup-' + key) || '';
        });
      }

      function open(card) {
        opener = card;
        fill(card);
        modal.hidden = false;
        document.documentElement.classList.add('mnl-locked');
        var close = modal.querySelector('[data-mnl-popup-close]');
        if (close) close.focus();
        run();
      }

      function close() {
        if (modal.hidden) return;
        if (tl) { tl.kill(); tl = null; }
        modal.hidden = true;
        document.documentElement.classList.remove('mnl-locked');
        if (opener) { opener.focus(); opener = null; }
      }

      scope.querySelectorAll('[data-mnl-popup-open]').forEach(function (card) {
        card.addEventListener('click', function () { open(card); });
      });

      modal.querySelectorAll('[data-mnl-popup-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });

      // Any click within the dialog closes it, not just the close icon or backdrop
      // (owner request, 30 September 2026). Child clicks bubble here too.
      modal.addEventListener('click', close);

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });
    });
  }

  /* 5. Move elements without repeated layout. Record their old positions, apply the new layout immediately, translate them back visually, then release the transforms. This produces a smooth move using compositor layers without layout recalculation during animation. Animating height or spacing is expensive because layout movement repaints elements, including glyph-clipped fills and shadows. A measured Pen transition dropped from 400ms and 21 layouts to 182ms and 2 layouts after this change. */
  function flipMove(nodes, scope, mutate) {
    var i;
    var before = null;
    if (!reduced && nodes.length > 1) {
      before = [];
      for (i = 0; i < nodes.length; i++) {
        var r0 = nodes[i].getBoundingClientRect();
        before.push(r0.height ? r0.top : null);
      }
    }

    mutate();
    if (!before) return;

    // Do not move elements hidden in either the old or new state:
    // their zero-sized rectangles produce meaningless offsets,
    // making an item fly across the whole screen.
    var shift = [];
    for (i = 0; i < nodes.length; i++) {
      var r = nodes[i].getBoundingClientRect();
      shift.push(r.height && before[i] !== null ? before[i] - r.top : 0);
    }
    for (i = 0; i < nodes.length; i++) {
      nodes[i].style.transition = 'none';
      nodes[i].style.transform = shift[i] ? 'translateY(' + shift[i] + 'px)' : 'none';
    }
    // Apply the offset before restoring the transition; otherwise the browser
    // coalesces both assignments and there is no visible movement.
    void scope.offsetHeight;
    for (i = 0; i < nodes.length; i++) {
      nodes[i].style.transition = '';
      nodes[i].style.transform = '';
    }
  }

  /* 6. Scroll-controlled step sequence. A viewport-height section reaches the top and holds. Each wheel gesture advances a stop while page scrolling is locked. Callers define each transition: Pen seeks video frames; Application changes screens. This controller handles only scroll capture and the stop queue. opts.count = number of stops; opts.ready = whether capture is allowed (video readiness for Pen); opts.forwardOnly = preserve the step when scrolling backward; opts.run(from, to, done) = start a transition and call done when finished. Established safeguards: the entering gesture must not advance a stop, or trackpad inertia skips the first one; leaving an edge also requires a separate gesture, or the tail of the return-to-start gesture ejects the page into the preceding section mid-transition; while captured, consume every event including weak inertia tails, or the page drifts a few pixels and snaps back; never release if there is no room to leave, such as at the last section. */
  function stepStage(scene, opts) {
    var lastStop = opts.count - 1;
    var EDGE = 2;         // Tolerance for fractional document height, px

    /* How far above the viewport the scene's top should sit. Zero aligns the scene to the viewport top. An oversized scene (Seven Days is 1006px in an approximately 810px viewport) would otherwise lose its bottom, where its interactive content sits, because the controller prevents further scrolling. Its equal top/bottom margins allow vertical centering, limited by the always-fixed header so content cannot slide underneath. The section supplies this limit. */
    function offs() {
      if (!opts.lift) return 0;
      var h = scene.getBoundingClientRect().height;
      return Math.min(Math.max(0, (h - window.innerHeight) / 2), opts.lift());
    }

    /* Native mobile scrolling avoids fighting Safari's asynchronous momentum
       with repeated scrollTo calls. Only this scene opts into the sticky track. */
    if (opts.nativeMobile && window.matchMedia('(max-width: 767px)').matches) {
      var track = document.createElement('div');
      track.className = 'mnl-native-step-track full-width';
      scene.parentNode.insertBefore(track, scene);
      track.appendChild(scene);
      var originalStyle = scene.getAttribute('style');
      var currentStep = 0;
      var requestedStep = 0;
      var transitioning = false;
      var frame = 0;
      var destroyed = false;
      var distance = 0;
      var lift = 0;
      var viewportWidth = window.innerWidth;

      function advanceNative() {
        if (destroyed || transitioning || currentStep === requestedStep) return;
        var from = currentStep;
        currentStep += requestedStep > currentStep ? 1 : -1;
        transitioning = true;
        opts.run(from, currentStep, function () {
          transitioning = false;
          advanceNative();
        });
      }
      function updateNative() {
        frame = 0;
        if (destroyed) return;
        var progress = (-track.getBoundingClientRect().top - lift) / distance;
        var target = Math.max(0, Math.min(lastStop, Math.floor(progress + 0.15)));
        requestedStep = opts.forwardOnly ? Math.max(requestedStep, target) : target;
        advanceNative();
      }
      function queueNative() {
        if (!frame) frame = requestAnimationFrame(updateNative);
      }
      function sizeNative() {
        lift = offs();
        distance = Math.max(360, window.innerHeight * 0.8);
        track.style.height = (scene.offsetHeight + distance * (lastStop + 1)) + 'px';
        scene.style.position = 'sticky';
        scene.style.top = -lift + 'px';
        queueNative();
      }
      function resizeNative() {
        // Safari's collapsing address bar must not change the scroll thresholds.
        if (window.innerWidth === viewportWidth) return;
        viewportWidth = window.innerWidth;
        sizeNative();
      }
      sizeNative();
      window.addEventListener('scroll', queueNative, { passive: true });
      window.addEventListener('resize', resizeNative);
      var nativeRoot = scene.closest('.shopify-section') || track;
      nativeRoot.addEventListener('shopify:section:unload', function () {
        destroyed = true;
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', queueNative);
        window.removeEventListener('resize', resizeNative);
        if (originalStyle === null) scene.removeAttribute('style');
        else scene.setAttribute('style', originalStyle);
        track.replaceWith(scene);
      }, { once: true });
      return;
    }

    var idx = 0;          // Current stop
    var busy = false;     // Transition in progress
    var engaged = false;  // Scroll is captured
    var freeUntil = 0;    // Do not immediately recapture after release
    var readyUntil = 0;   // Pause between gestures
    var wheelAt = 0;      // Time of the previous wheel event
    var wheelAbs = 0;     // Magnitude of that event
    var gateFrom = 0;     // Starting point for waiting for the current gesture to end
    var armed = false;    // The current gesture may advance a stop
    var prevTop = null;
    var touchY = 0;
    var touchUsed = false;
    // Optional distance-based input for scenes used with gentle trackpad gestures.
    var wheelDistance = opts.wheelDistance || 0;
    var wheelTravel = 0;
    var wheelDir = 0;

    function now() { return Date.now(); }
    function forwardOnly() { return !!opts.forwardOnly; }
    function releaseBackward(dir) {
      if (dir >= 0 || !forwardOnly()) return false;
      release();
      return true;
    }
    function scrollY() { return window.pageYOffset || document.documentElement.scrollTop || 0; }
    function maxScroll() {
      return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    }

    function snap() {
      var y = scrollY() + scene.getBoundingClientRect().top + offs();
      y = Math.round(Math.min(Math.max(y, 0), maxScroll()));
      // The threshold stops repeated attempts when reaching zero is impossible
      // because the section is at the bottom of the page.
      if (Math.abs(y - scrollY()) < 1) return;
      window.scrollTo(0, y);
    }

    function engage() {
      if (engaged || (opts.ready && !opts.ready())) return;
      engaged = true;
      scrollCaptured++;
      // Smooth-scroll interpolation may still be running. Cancel it before
      // settling at this position, or it will immediately pull the page back.
      smoothStop();
      snap();
      prevTop = 0;
      armed = false;
      touchUsed = true;
      wheelAt = now();
      wheelAbs = 1e9;   // Any weaker subsequent impulse is inertia
      gateFrom = now();
      readyUntil = now() + 220;
    }

    function release(dir) {
      if (!engaged) return;
      if (dir) {
        var y = scrollY();
        if (dir > 0 && y >= maxScroll() - 1) return;
        if (dir < 0 && y <= 1) return;
      }
      engaged = false;
      scrollCaptured--;
      smoothStop();
      freeUntil = now() + 450;
      prevTop = scene.getBoundingClientRect().top + offs();
    }

    function done() {
      busy = false;
      // Treat the end of a transition like a fresh capture: the current gesture
      // is already consumed. Otherwise a short transition finishes
      // before inertia subsides, and its tail advances a second step.
      armed = false;
      gateFrom = now();
      wheelTravel = 0;
      readyUntil = now() + (wheelDistance ? 60 : 320);
    }

    // Return true if the event must be consumed because capture is still active.
    function step(dir) {
      if (releaseBackward(dir)) return false;
      var next = idx + dir;
      if (next < 0 || next > lastStop) {
        /* Leaving an edge need not be immediate: a section may play an exit frame before releasing scrolling. Keep capture active until it finishes, or the page moves during the animation. */
        if (opts.outro && !busy) {
          busy = true;
          gateFrom = now();
          var took = opts.outro(dir, function () {
            busy = false;
            release(dir);
            armed = false;
            gateFrom = now();
            readyUntil = now() + 320;
          });
          if (took) return true;
          busy = false;
        }
        release(dir);
        return engaged;
      }
      var from = idx;
      idx = next;
      busy = true;
      gateFrom = now();
      opts.run(from, next, done);
      return true;
    }

    /* Distinguish a new gesture from trackpad inertia. On macOS, decaying events continue for 1–1.5 seconds after fingers are lifted. A new gesture starts after events stop completely, or when an impulse becomes significantly stronger than the previous one, which decay cannot do. The third condition handles continuous finger dragging. PUSH is the minimum real-gesture threshold: inertia tails have tiny, increasingly sparse events, whose gaps eventually exceed 140ms and could falsely count as a new gesture. Only a meaningful impulse may advance a step, but update the last-event timestamp for every event so a continuing tail does not count as idle time. */
    var PUSH = 12;
    function armGesture(t, abs) {
      if (abs < PUSH) return;
      if (t - wheelAt > 140) armed = true;
      else if (abs > wheelAbs * 1.5 + 4) armed = true;
      else if (t - gateFrom > 2500) armed = true;
    }

    function onWheel(e) {
      if (!engaged) return;
      var t = now();
      var delta = e.deltaY * (e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? window.innerHeight : 1));
      var abs = Math.abs(delta);
      var dir = delta > 0 ? 1 : (delta < 0 ? -1 : 0);
      // Let upward input leave immediately, even during a forward transition.
      if (releaseBackward(dir)) return;

      if (wheelDistance) {
        // Sum small pixel deltas: a slow gesture need not contain a single 12px event.
        // A pause, direction change, or renewed acceleration starts a fresh gesture.
        // Decaying momentum cannot spend the same gesture on another slide.
        var fresh = t - wheelAt > 120 || (dir && dir !== wheelDir) || abs > wheelAbs * 1.5 + 0.8;
        if (busy || t < readyUntil) {
          armed = false;
          wheelTravel = 0;
        } else {
          if (fresh) { armed = true; wheelTravel = 0; }
          if (armed) wheelTravel += abs;
        }
      } else {
        armGesture(t, abs);
      }
      wheelAt = t;
      wheelAbs = abs;
      wheelDir = dir;

      if (armed && !busy && dir !== 0 && (wheelDistance ? wheelTravel >= wheelDistance : abs >= PUSH) && t >= readyUntil) {
        armed = false;
        wheelTravel = 0;
        // On the boundary the same event continues normal page scrolling.
        if (!step(dir)) return;
      }
      e.preventDefault();
    }

    function onTouchStart(e) {
      touchY = e.touches[0].clientY;
      touchUsed = false;
    }

    function onTouchMove(e) {
      if (!engaged) return;
      var dy = touchY - e.touches[0].clientY;
      var dir = dy > 0 ? 1 : -1;
      if (dy && releaseBackward(dir)) return;
      if (!touchUsed && !busy && now() >= readyUntil && Math.abs(dy) >= 36) {
        touchUsed = true;
        if (!step(dir)) return;
      }
      e.preventDefault();
    }

    var KEYS = { ArrowDown: 1, PageDown: 1, ' ': 1, Spacebar: 1, ArrowUp: -1, PageUp: -1 };
    function onKey(e) {
      if (!engaged) return;
      var tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)) return;
      var dir = KEYS[e.key];
      if (!dir) return;
      if (releaseBackward(dir)) return;
      // A keypress is a separate gesture, so inertia detection is unnecessary;
      // only prevent repeated advances during a transition.
      if (!busy && now() >= readyUntil && !step(dir)) return;
      e.preventDefault();
    }

    function onScroll() {
      var top = scene.getBoundingClientRect().top + offs();

      if (engaged) {
        if (top > EDGE && forwardOnly()) { release(); return; }
        // Scrolling may come from a scrollbar or find-in-page rather than the wheel.
        if (Math.abs(top) > EDGE) snap();
        return;
      }

      if (prevTop === null || now() < freeUntil) { prevTop = top; return; }
      // The section's top reaches the viewport top, from either direction.
      // Compare with a tolerance rather than zero: document height is fractional,
      // but the browser rounds its maximum scroll down. At the last section,
      // the top may stop at 0.23 and never reach zero.
      if (prevTop > EDGE && top <= EDGE && idx < lastStop) engage();
      else if (prevTop < -EDGE && top >= -EDGE && idx > 0 && !forwardOnly()) engage();
      else prevTop = top;
    }

    // Focus moving outside the section (Tab through links) must not be blocked
    // by captured scrolling.
    function onFocusIn(e) {
      if (engaged && e.target && !scene.contains(e.target)) release();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { if (engaged) snap(); });
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    onScroll();
  }

  /* 7. Pen: select video frames by clicking list items. Previously scrolling controlled frames: a step controller held the section and each wheel gesture played to the next stop. The owner's 28 September 2026 decision removed scroll capture entirely: the page scrolls through normally and list clicks select the same frames. Clicking a gray item immediately turns it black, dims the previous item, and moves the video to its frame. Forward movement uses normal sequential playback without seeking, the smoothest decoding path. Backward movement uses currentTime seeking, waiting for the previous seek to finish (video.seeking) before requesting another. This adapts to decoder speed instead of issuing 60 requests per second. The video was rebuilt with frequent keyframes because reverse seek cost depends on distance to the preceding keyframe. */
  function initScrub(root) {
    root.querySelectorAll('[data-mnl-scrub]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');

      var stage = scene.querySelector('[data-mnl-scrub-stage]');
      var video = scene.querySelector('[data-mnl-scrub-video]');
      var steps = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-scrub-step]'));
      if (!stage || !steps.length) return;

      var fps = parseFloat(scene.getAttribute('data-mnl-scrub-fps')) || 30;
      /* Playback speed. One is the source file's natural speed, requested by the owner on 30 September 2026. Previously 2 made both directions visibly too fast. */
      var rate = parseFloat(scene.getAttribute('data-mnl-scrub-rate')) || 1;
      var rev = scene.querySelector('[data-mnl-scrub-rev]');
      var mobileMedia = window.matchMedia('(max-width: 767px)').matches;
      function mediaSource(el, desktop, mobile) {
        return (mobileMedia && el.getAttribute(mobile)) || el.getAttribute(desktop);
      }
      if (video && mobileMedia && video.hasAttribute('data-mnl-scrub-mobile-poster')) {
        video.poster = video.getAttribute('data-mnl-scrub-mobile-poster');
      }

      /* Autoplay pass, requested on 30 September 2026: when the section becomes visible, play from beginning to end and update the list automatically. A click permanently takes control (auto=false); only clicks move the frame thereafter. After a completed section leaves view, reset to the start so its next entrance plays again. */
      var auto = !reduced;  // Highlight follows the video until a click takes control
      var seen = false;     // Section is visible
      var begun = false;    // Autoplay pass is running and not paused

      var darkAt = parseFloat(scene.getAttribute('data-mnl-scrub-dark'));
      if (isNaN(darkAt)) darkAt = 101;
      darkAt = darkAt / 100;

      // Fraction of the video duration for this item's frame.
      var at = steps.map(function (el) {
        return Math.min(1, Math.max(0, (parseFloat(el.getAttribute('data-mnl-scrub-at')) || 0) / 100));
      });

      var cur = 0;          // Current video fraction
      var raf = 0;
      var gen = 0;          // Loop generation: stale frames must not finish their calculations
      var shownStep = -1;
      var shownDark = null;
      var pending = -1;     // Item clicked before the file finished loading

      function duration() { return video && video.duration ? video.duration : 0; }

      /* During autoplay, highlighting follows the frame: crossing the second item's frame activates that item. After a click, highlighting follows the selection instead; otherwise moving toward the third item would briefly activate the second on the way. */
      function setStep(i) {
        if (i === shownStep) return;
        shownStep = i;
        for (var k = 0; k < steps.length; k++) {
          var on = k === i;
          steps[k].classList.toggle('is-active', on);
          steps[k].setAttribute('aria-pressed', on ? 'true' : 'false');
        }
      }

      function render(p) {
        cur = p;
        var dark = p >= darkAt;
        if (dark !== shownDark) {
          shownDark = dark;
          scene.classList.toggle('is-dark', dark);
          // The scene changes inside the section, so the header's own trigger
          // does not fire here; request its recalculation explicitly.
          scene.setAttribute('data-mnl-scheme', dark ? 'dark' : 'light');
          syncHeader();
        }
      }

      /* Skip the frame until the browser reports completion of the previous seek. A queue of seeks is more expensive than one skipped frame. */
      function seek(p) {
        var d = duration();
        if (!d || video.seeking) return;
        var t = p * d;
        if (Math.abs(t - video.currentTime) < 0.5 / fps) return;
        try { video.currentTime = t; } catch (e) {}
      }

      function stopLoop() {
        gen++;
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
      }

      /* Settle on the target frame. A single currentTime assignment is insufficient: if the previous seek is still in flight, the browser may discard the new one and freeze at the interrupted frame (resetting to zero previously left it at 0.3s). Wait for seeked and retry until settled, with a small attempt limit to prevent an infinite loop. */
      function settle(to, tries) {
        var d = duration();
        if (!d || tries <= 0) return;
        var t = Math.min(to * d, d - 0.001);
        if (Math.abs(video.currentTime - t) < 0.5 / fps) return;
        function again() {
          video.removeEventListener('seeked', again);
          settle(to, tries - 1);
        }
        video.addEventListener('seeked', again);
        if (!video.seeking) { try { video.currentTime = t; } catch (e) {} }
      }

      function finish(to) {
        stopLoop();
        if (video) {
          video.pause();
          settle(to, 6);
        }
        render(to);
        primeRev(to);
      }

      /* Pre-position the reversed video at the mirrored frame. Seeking causes one hitch (measured page frame: 267ms), so perform it while idle, not when the user clicks backward. This also warms the decoder (rule 52). */
      function primeRev(to) {
        if (!rev || !rev.duration) return;
        var rd = rev.duration;
        var t = Math.min(Math.max((1 - to) * rd, 0), rd - 0.001);
        if (Math.abs(rev.currentTime - t) < 0.5 / fps) return;
        if (rev.seeking) return;
        try { rev.currentTime = t; } catch (e) {}
      }

      /* Reverse movement through normal playback. The reversed file is the same footage backward, so forward position p maps to 1 − p in the reverse file. There is no seeking and no decoder catch-up. The former currentTime-seeking approach, over nine seconds, displayed 141 of 270 possible frames, with jumps up to 0.194s and 11 page frames longer than 32ms. */
      function revReady() {
        return rev && rev.duration && rev.readyState >= 2;
      }

      function playBack(to) {
        var d = duration();
        var rd = rev.duration;
        var from = cur;
        var g = gen;
        var start = Math.min(Math.max((1 - from) * rd, 0), rd - 0.001);
        var target = Math.min(Math.max((1 - to) * rd, 0), rd - 0.001);

        function run() {
          if (g !== gen) return;
          scene.classList.add('is-rev');
          rev.playbackRate = Math.min(4, Math.max(0.25, rate));
          var pr = rev.play();
          if (pr && pr.catch) pr.catch(function () {
            if (g === gen) { scene.classList.remove('is-rev'); rewindSeek(to, Math.abs(to - from) * d / rate); }
          });
          function frame() {
            if (g !== gen) return;
            if (rev.currentTime >= target - 0.004) { done(); return; }
            render(1 - rev.currentTime / rd);
            raf = requestAnimationFrame(frame);
          }
          raf = requestAnimationFrame(frame);
        }

        /* Remove the swapped video only after the forward video reaches the correct frame; otherwise the transition flashes the frame we left. */
        function done() {
          stopLoop();
          rev.pause();
          render(to);
          var t = Math.min(to * d, d - 0.001);
          function back() {
            video.removeEventListener('seeked', back);
            scene.classList.remove('is-rev');
          }
          if (Math.abs(video.currentTime - t) < 0.5 / fps) { scene.classList.remove('is-rev'); return; }
          video.addEventListener('seeked', back);
          setTimeout(back, 400);
          try { video.currentTime = t; } catch (e) {}
        }

        // Position the reverse video's first frame before swapping it in.
        if (Math.abs(rev.currentTime - start) < 0.5 / fps) { run(); return; }
        function ready() {
          rev.removeEventListener('seeked', ready);
          run();
        }
        rev.addEventListener('seeked', ready);
        setTimeout(function () { if (g === gen && !scene.classList.contains('is-rev')) { rev.removeEventListener('seeked', ready); run(); } }, 500);
        try { rev.currentTime = start; } catch (e) {}
      }

      /* Fallback reverse movement: seek with eased start and finish when no reversed file is available. */
      function rewindSeek(to, secs) {
        var from = cur;
        var g = gen;
        var t0 = performance.now();
        function frame(t) {
          if (g !== gen) return;
          var k = Math.min(1, (t - t0) / (secs * 1000));
          var e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
          var p = from + (to - from) * e;
          render(p);
          seek(p);
          if (k >= 1) { finish(to); return; }
          raf = requestAnimationFrame(frame);
        }
        raf = requestAnimationFrame(frame);
      }

      /* Forward movement: normal playback at the configured rate. Check each frame for arrival at the target. If playback is rejected by strict autoplay settings, fall back to seeking, which remains available. */
      function playTo(to, secs) {
        var d = duration();
        var target = Math.min(to * d, d - 0.001);
        var span = target - video.currentTime;
        if (span <= 0) { finish(to); return; }

        var g = gen;
        /* Use the configured playback speed directly, not a speed inferred from duration. The previous five-second duration cap accelerated long segments (the first covers nine seconds of footage), regardless of the setting. */
        video.playbackRate = Math.min(4, Math.max(0.25, rate));
        var started = performance.now();
        var fellBack = false;
        var play = video.play();
        if (play && play.catch) {
          play.catch(function () {
            if (g === gen && !fellBack) { fellBack = true; rewindSeek(to, secs); }
          });
        }

        function frame(t) {
          if (g !== gen) return;
          if (video.currentTime >= target - 0.004) { finish(to); return; }
          render(video.currentTime / d);
          if (!fellBack && video.paused && t - started > 350) {
            fellBack = true;
            rewindSeek(to, secs);
            return;
          }
          raf = requestAnimationFrame(frame);
        }
        raf = requestAnimationFrame(frame);
      }

      /* Click: change highlighting immediately, then move the frame. Derive duration from video segment length divided by playback speed, so changing item frame positions does not disturb timing. */
      function go(i) {
        auto = false;
        setStep(i);
        var d = duration();
        if (!d) { pending = i; return; }
        pending = -1;
        stopLoop();
        var p = at[i];
        if (reduced) { finish(p); return; }
        /* Reverse duration equals the segment's actual duration at the configured rate. Deliberately omit an upper cap: it was what accelerated reverse playback (30 September 2026 note). */
        var secs = Math.min(20, Math.max(0.25, Math.abs(p - cur) * d / rate));
        if (p > cur) playTo(p, secs);
        else if (p < cur) { if (revReady()) playBack(p); else rewindSeek(p, secs); }
        else finish(p);
      }

      steps.forEach(function (el, i) {
        el.addEventListener('click', function () { go(i); });
      });

      /* Arrows 3284:20446 navigate the same list without wrapping. Playing backward from the end to the beginning takes nine seconds, so stepping beyond either edge does nothing. */
      var prev = scene.querySelector('[data-mnl-scrub-prev]');
      var next = scene.querySelector('[data-mnl-scrub-next]');
      function shift(dir) {
        var i = Math.min(steps.length - 1, Math.max(0, shownStep + dir));
        if (i !== shownStep) go(i);
      }
      if (prev) prev.addEventListener('click', function () { shift(-1); });
      if (next) next.addEventListener('click', function () { shift(1); });

      /* Last item whose frame the autoplay pass has reached. */
      function stepByFrac(p) {
        var i = 0;
        for (var k = 0; k < at.length; k++) if (p >= at[k] - 0.004) i = k;
        return i;
      }

      /* Autoplay pass: normal playback to the file's end, with highlighting following the frame. Also resumes after a pause from the paused position. */
      function playAll() {
        var d = duration();
        if (!d || !auto || begun) return;
        begun = true;
        stopLoop();
        var g = gen;
        video.playbackRate = Math.min(4, Math.max(0.25, rate));
        var pr = video.play();
        // Browser autoplay settings rejected playback; retain click control.
        if (pr && pr.catch) pr.catch(function () { if (g === gen) begun = false; });
        function frame() {
          if (g !== gen) return;
          var p = video.currentTime / d;
          if (video.ended || video.currentTime >= d - 0.05) {
            setStep(steps.length - 1);
            finish(1);
            return;
          }
          render(p);
          setStep(stepByFrac(p));
          raf = requestAnimationFrame(frame);
        }
        raf = requestAnimationFrame(frame);
      }

      function maybePlay() {
        if (seen && duration()) playAll();
      }

      /* Reset a completed section while offscreen. The seek is invisible and the next entrance replays from the start. */
      function reset() {
        stopLoop();
        if (video) video.pause();
        begun = false;
        auto = !reduced;
        setStep(0);
        settle(0, 6);
        render(0);
        primeRev(0);
      }

      if (window.IntersectionObserver) {
        /* Entrance threshold: almost the entire available section is visible. Use a fraction of its maximum possible visibility because a section taller than the viewport (rule 87) would never meet a fixed 0.9 threshold. Use a fine threshold grid so the effective threshold can be calculated dynamically. */
        var thrGrid = [];
        for (var ti = 0; ti <= 20; ti++) thrGrid.push(ti / 20);
        var onBlock = false;
        var io = new IntersectionObserver(function (entries) {
          // Use the last entry, not the first (rule 115).
          var e = entries[entries.length - 1];
          var h = e.boundingClientRect.height || 1;
          var most = Math.min(1, (e.rootBounds ? e.rootBounds.height : window.innerHeight) / h);
          var thr = most * 0.9;
          if (e.isIntersecting && e.intersectionRatio >= thr && !onBlock) {
            onBlock = true;
            seen = true;
            // Load the reversed file only here: it is unnecessary before reaching the section
            // and weighs as much as the forward file.
            if (rev) grab(rev, mediaSource(rev, 'data-mnl-scrub-rev-src', 'data-mnl-scrub-mobile-rev-src'));
            maybePlay();
          } else if (onBlock && (!e.isIntersecting || e.intersectionRatio <= 0.15)) {
            onBlock = false;
            seen = false;
            if (!reduced && cur >= 0.999) {
              reset();
            } else if (auto && begun) {
              // Leaving during autoplay pauses it; returning resumes playback.
              stopLoop();
              video.pause();
              begun = false;
            }
          }
        }, { threshold: thrGrid });
        io.observe(scene);
      }

      /* Fetch the whole file and use it from memory (reason below). Apply the same method to forward and reversed videos. */
      function grab(el, src) {
        if (!el || !src || el.src) return;
        if (window.fetch && window.URL && URL.createObjectURL) {
          fetch(src)
            .then(function (r) { return r.ok ? r.blob() : Promise.reject(r.status); })
            .then(function (blob) {
              el.src = URL.createObjectURL(blob);
              el.load();
            })
            .catch(function () {
              el.preload = 'auto';
              el.src = src;
            });
        } else {
          el.preload = 'auto';
          el.src = src;
        }
      }

      /* File loading. Fetch the complete file and use it from memory. With a normal src the browser fetches chunks for individual seek requests: readyState can remain at metadata-only while currentTime increases and the first frame stays onscreen. Show the poster while downloading. */
      if (video) {
        video.addEventListener('loadedmetadata', function () {
          if (pending >= 0) { var i = pending; pending = -1; go(i); return; }
          maybePlay();
        });
        grab(video, mediaSource(video, 'data-mnl-scrub-src', 'data-mnl-scrub-mobile-src'));
      }

      setStep(0);
      render(at[0]);
    });
  }

  /* 8. Application: screens and text at each stop. The same controller drives a simpler transition: no seeking, only state changes. The old panel moves upward and blurs; the next enters from below and unblurs, with exit and entrance sequential rather than overlapping. GSAP drives it; without GSAP, panels switch classes. Absolutely positioned panels overlap, so state changes do not affect layout and are excluded from flipMove. Only lower-list items use FLIP because they really move when an item rises. Blur itself is cheap; shadows are expensive: the heading has a 180px shadow radius and list items 175px, repainted in the blurred layer each frame. Three transitions measured 546ms with shadows versus 290ms without (320ms with shadows but no blur). During a transition, is-swapping removes shadows: their 10% black on a dark background is not visibly missed for 700ms. The panel is pre-promoted to a layer through section will-change; remove blur completely at the end so normal unfiltered text remains. */
  function initApp(root) {
    root.querySelectorAll('[data-mnl-app]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');

      var panels = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-app-panel]'));
      var screens = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-app-screen]'));
      var cards = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-app-card]'));
      var prev = scene.querySelector('[data-mnl-app-prev]');
      var next = scene.querySelector('[data-mnl-app-next]');
      if (panels.length < 2) return;

      var swap = parseFloat(scene.getAttribute('data-mnl-app-swap')) || 700;
      var blur = parseFloat(scene.getAttribute('data-mnl-app-blur'));
      if (isNaN(blur)) blur = 8;
      var moves = animated && !!gsap;
      var shown = -1;
      var tl = null;

      // Heading exit and entrance. The text does NOT move: the old heading
      // blurs and fades in place, and the new one appears from blur at the same point,
      // as requested on 29 September 2026; previously it exited upward and entered below.
      // The halves run strictly sequentially (rule 84), because overlapping them
      // would superimpose two texts
      // at exactly the same position.
      // Fractions of total duration: 0.42 exit, 0.58 entrance.
      // wasOpacity is the outgoing panel's opacity measured BEFORE switching
      // classes: is-active has already been removed, so GSAP would otherwise
      // start from zero and the old heading would disappear immediately.
      function swapPanels(from, to, wasOpacity) {
        var out = from >= 0 ? panels[from] : null, inn = panels[to];
        if (tl) { tl.kill(); tl = null; rest(); }
        // Return anything outside this transition to class-controlled styling:
        // an interrupted transition can leave
        // a panel at an intermediate opacity.
        panels.forEach(function (el) { if (el !== inn && el !== out) clearPanel(el); });
        if (!moves || !out) {
          if (out) clearPanel(out);
          clearPanel(inn);
          return;
        }
        scene.classList.add('is-swapping');
        tl = gsap.timeline({ onComplete: rest });
        if (out !== inn) {
          // Only restore opacity forcibly; movement and blur
          // continue from their interrupted positions.
          gsap.set(out, { opacity: wasOpacity });
          tl.to(out, {
            filter: 'blur(' + blur + 'px)',
            opacity: 0,
            duration: (swap / 1000) * 0.42,
            ease: 'power2.in',
            onComplete: function () { out.style.filter = ''; }
          });
        }
        tl.fromTo(inn,
          { filter: 'blur(' + blur + 'px)', opacity: 0 },
          {
            filter: 'blur(0px)',
            opacity: 1,
            duration: (swap / 1000) * 0.58,
            ease: 'power2.out',
            // Remove blur entirely: even blur(0px) keeps
            // text rendering through a filter.
            onComplete: function () { inn.style.filter = ''; }
          },
          out !== inn ? '>' : 0
        );
      }

      // Transition complete: restore shadows.
      function rest() {
        scene.classList.remove('is-swapping');
      }

      // Remove all GSAP-applied panel styles and return control to classes.
      function clearPanel(el) {
        if (moves) gsap.set(el, { clearProps: 'all' });
        el.style.opacity = '';
        el.style.transform = '';
        el.style.filter = '';
      }

      /* Each slide has its own phone video: the active one plays while the others remain on the first frame. Load each file only when its slide is first needed, otherwise all four videos would start loading together on page load. */
      function playScreen(i) {
        screens.forEach(function (el, k) {
          if (el.tagName !== 'VIDEO') return;
          if (k === i) {
            var src = el.getAttribute('data-mnl-app-src');
            if (src && !el.src) { el.src = src; el.load(); }
            if (!reduced) { var p = el.play(); if (p && p.catch) p.catch(function () {}); }
          } else if (!el.paused) {
            el.pause();
          }
        });
      }

      function show(i) {
        if (i === shown) return;
        var was = shown;
        var wasOpacity = was >= 0 ? parseFloat(getComputedStyle(panels[was]).opacity) : 1;
        if (isNaN(wasOpacity)) wasOpacity = 1;
        shown = i;
        panels.forEach(function (el, k) { el.classList.toggle('is-active', k === i); });
        screens.forEach(function (el, k) { el.classList.toggle('is-active', k === i); });
        cards.forEach(function (el, k) { el.classList.toggle('is-active', k === i); });
        scene.setAttribute('data-mnl-app-at', String(i));
        playScreen(i);
        swapPanels(was, i, wasOpacity);
      }

      /* Navigation uses arrow clicks, per the owner's decision on 29.09.2026. Previously this used a step-based scroll controller that intercepted the wheel. The track loops: advancing from the last slide returns to the first, so arrows never have a disabled state, which the design does not specify either. */
      function step(dir) {
        show((shown + dir + panels.length) % panels.length);
      }

      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      // Mobile horizontal swipes share the arrow navigation; vertical gestures
      // remain native page scrolling (touch-action: pan-y pinch-zoom).
      var swipeMedia = window.matchMedia('(max-width: 767px)');
      var swipe = null;
      scene.addEventListener('pointerdown', function (event) {
        if (!swipeMedia.matches || !event.isPrimary || event.button !== 0 ||
            event.target.closest('a, button, input, textarea, select')) return;
        swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
        scene.setPointerCapture(event.pointerId);
      });
      scene.addEventListener('pointerup', function (event) {
        if (!swipe || swipe.id !== event.pointerId) return;
        var dx = event.clientX - swipe.x, dy = event.clientY - swipe.y;
        swipe = null;
        if (scene.hasPointerCapture(event.pointerId)) scene.releasePointerCapture(event.pointerId);
        if (swipeMedia.matches && Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          step(dx < 0 ? 1 : -1);
        }
      });
      function cancelSwipe() { swipe = null; }
      scene.addEventListener('pointercancel', cancelSwipe);
      scene.addEventListener('lostpointercapture', cancelSwipe);

      show(0);

      /* The phone video plays only while the section is near the viewport. The 600px margin lets the file load and decoder initialize before the section becomes visible, keeping the expensive first playback frame offscreen (item 52). Without this margin, measurement showed a 1366 ms frame exactly when entering the section. */
      if ('IntersectionObserver' in window) {
        /* Two observers are necessary (the same pair of roles as in item 62): the 600px margin starts playback early and hides the expensive first frame offscreen (item 52), but is unsuitable for pausing. With it, playback continued for another 600px after leaving the section and the transition to the next section lagged. Measurement on 30.09.2026: scrolling past a playing video produced 75 dropped frames versus 26 without it. Pause immediately outside the viewport. */
        var near = false;
        new IntersectionObserver(function (entries) {
          /* Use the last entry, not the first: a callback may contain several, and the first is the oldest (item 115). */
          var now = entries[entries.length - 1].isIntersecting;
          if (now === near) return;
          near = now;
          if (now) playScreen(shown);
        }, { rootMargin: '600px 0px', threshold: 0 }).observe(scene);

        new IntersectionObserver(function (entries) {
          var on = entries[entries.length - 1].isIntersecting;
          if (!on) {
            screens.forEach(function (el) {
              if (el.tagName === 'VIDEO' && !el.paused) el.pause();
            });
          } else if (near) {
            playScreen(shown);
          }
        }, { threshold: 0 }).observe(scene);
      }
    });
  }

  /* --- 8a. Expandable list items ---
Items advance automatically on a timer, per the owner's decision on 27.09.2026. Previously this used a step-based controller intercepting scroll, like Pen and Application. Now scrolling is untouched: the page passes through the section, and the expanded item stays open for `data-mnl-stepper-hold` milliseconds before the next opens. The last loops back to the first.
On each step:
1. The left bar fills from top to bottom while the item is open, counting down to the next item. Owner clarification on 27.09.2026: previously a thumb traveled along the entire track instead.
2. Once full, the open item collapses and the next expands. FLIP animates neighboring items: layout changes immediately while transforms produce the visible movement. Track height changes through scaleY without jumps. Descriptions remain rendered and fade smoothly.
3. The new item's bar starts growing from zero immediately, with neighboring movement overlapping it.
There is no reverse traversal: the sequence only moves forward.
Grow the bar using scaleY from its top edge, not height: height affects layout and requires main-thread recalculation every frame, whereas transforms run on the compositor.
Do not start the timer while the section is offscreen; otherwise layout changes would continue on an invisible part of the page, like looping animations. */
  function initMobileRitual(root) {
    root.querySelectorAll('.mnl-exp-ritual__cards:not([data-ritual-ready])').forEach(function (track) {
      track.setAttribute('data-ritual-ready', '1');
      var cards = Array.prototype.slice.call(track.children);
      var mobile = window.matchMedia('(max-width: 767px)');
      var frame = 0;
      function update() {
        frame = 0;
        var bounds = track.getBoundingClientRect();
        var center = bounds.left + bounds.width / 2;
        var nearest = 0, distance = Infinity;
        cards.forEach(function (card, i) {
          var rect = card.getBoundingClientRect();
          var delta = Math.abs(rect.left + rect.width / 2 - center);
          if (delta < distance) { distance = delta; nearest = i; }
        });
        cards.forEach(function (card, i) { card.classList.toggle('is-current', i === nearest); });
      }
      function setup() {
        if (!mobile.matches) return;
        var selected = cards[Math.min(1, cards.length - 1)];
        if (!selected) return;
        var rect = selected.getBoundingClientRect();
        var bounds = track.getBoundingClientRect();
        track.scrollLeft += rect.left + rect.width / 2 - bounds.left - bounds.width / 2;
        update();
      }
      track.addEventListener('scroll', function () { if (!frame) frame = requestAnimationFrame(update); }, { passive: true });
      mobile.addEventListener('change', setup);
      requestAnimationFrame(setup);
    });
  }

  function initStepper(root) {
    root.querySelectorAll('[data-mnl-stepper]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');
      var items = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-step]'));
      if (items.length < 2) return;

      function apply(i) {
        scene.querySelectorAll('[data-mnl-step-video]').forEach(function (video) {
          var active = Number(video.getAttribute('data-mnl-step-video')) === i;
          video.classList.toggle('is-hidden', !active);
          video.pause();
          if (active) video.currentTime = 0;
        });
        for (var k = 0; k < items.length; k++) {
          items[k].classList.toggle('is-active', k === i);
          var trigger = items[k].querySelector('[data-mnl-step-trigger]');
          if (trigger) trigger.setAttribute('aria-expanded', k === i ? 'true' : 'false');
          var extra = items[k].querySelector('[data-mnl-step-extra]');
          if (extra) extra.setAttribute('aria-hidden', k === i ? 'false' : 'true');
        }
      }
      apply(0);

      var swap = parseInt(scene.getAttribute('data-mnl-stepper-swap') || '900', 10);
      /* `hold` covers the entire interval between item changes, not just the pause. The default interval is 6 seconds, per the owner's request on 02.10.2026. */
      var mobileWriting = scene.classList.contains('mnl-exp-writing') && window.matchMedia('(max-width: 767px)').matches;
      var hold = mobileWriting ? 5000 : parseInt(scene.getAttribute('data-mnl-stepper-hold') || '6000', 10);
      var auto = animated && scene.getAttribute('data-mnl-stepper-auto') !== 'false' && hold > 0;
      function bar(i) { return items[i].querySelector('[data-mnl-step-fill]'); }
      function stepVideo(i) { return scene.querySelector('[data-mnl-step-video="' + i + '"]'); }
      scene.querySelectorAll('[data-mnl-step-video]').forEach(function (video) {
        var index = Number(video.getAttribute('data-mnl-step-video'));
        var fill = bar(index);
        var progressFrame = 0;
        function syncVideoProgress() {
          if (fill && Number.isFinite(video.duration) && video.duration > 0) {
            fill.style.transform = 'scaleY(' + Math.min(1, video.currentTime / video.duration) + ')';
          }
        }
        // timeupdate fires only a few times per second. Paint on each
        // display frame while playing, using the media clock as the source.
        function stopVideoProgress() {
          cancelAnimationFrame(progressFrame);
          progressFrame = 0;
          syncVideoProgress();
        }
        function paintVideoProgress() {
          progressFrame = 0;
          syncVideoProgress();
          if (!video.paused && !video.ended && scene.isConnected) {
            progressFrame = requestAnimationFrame(paintVideoProgress);
          }
        }
        function startVideoProgress() {
          if (!progressFrame) paintVideoProgress();
        }
        video.addEventListener('playing', startVideoProgress);
        video.addEventListener('pause', stopVideoProgress);
        video.addEventListener('ended', stopVideoProgress);
        video.addEventListener('seeked', syncVideoProgress);
        video.addEventListener('loadedmetadata', syncVideoProgress);
        video.addEventListener('ended', function () {
          syncVideoProgress();
          if (at === index && shown && auto) step();
        });
      });
      var tracks = items.map(function (item) { return item.querySelector('.mnl-exp-writing__bar'); });
      var progress = [];
      var at = 0;
      var timer = null;
      var shown = false;
      var phaseTimer = null;
      var changing = false;
      var target = 0;
      var queued = null;
      var fadeTime = mobileWriting ? 250 : swap * 0.25;
      var moveTime = mobileWriting ? 0 : swap * 0.5;

      // The native transform animation runs the countdown on the compositor.
      // Bar opacity changes separately, so restarting the countdown
      // does not interrupt its fade-in or fade-out.
      function fillUp(i) {
        var el = bar(i);
        if (!el) return;
        if (progress[i]) progress[i].cancel();
        progress[i] = el.animate([
          { transform: 'scaleY(0)' },
          { transform: 'scaleY(1)' }
        ], { duration: hold, easing: 'linear', fill: 'forwards' });
      }

      function stop() {
        if (timer) { clearTimeout(timer); timer = null; }
        progress.forEach(function (animation) { if (animation) animation.cancel(); });
        progress = [];
        if (phaseTimer) { clearTimeout(phaseTimer); phaseTimer = null; }
        if (changing) {
          at = queued !== null ? queued : target;
          apply(at);
        }
        changing = false;
        queued = null;
        scene.classList.remove('is-changing-step');
        scene.querySelectorAll('[data-mnl-step-video]').forEach(function (video) { video.pause(); });
      }

      function plan() {
        if (timer) clearTimeout(timer);
        if (!shown) return;
        var video = stepVideo(at);
        if (video) {
          if (video.ended) video.currentTime = 0;
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
          return; // Media ended, not a wall-clock timer, advances this step.
        }
        if (!auto) return;
        fillUp(at);
        timer = setTimeout(step, Math.max(0, hold - fadeTime));
      }

      function moveTo(to) {
        if (mobileWriting) { apply(to); return; }
        var before = items.map(function (item) { return item.getBoundingClientRect().top; });
        var heights = tracks.map(function (track) { return track.getBoundingClientRect().height; });
        // getBoundingClientRect already includes the page's overall zoom,
        // while translateY uses the section's own coordinates.
        var zoom = scene.getBoundingClientRect().width / scene.offsetWidth || 1;
        // On rapid repeat clicks, start from the current position,
        // but measure the new layout without unfinished transforms.
        items.forEach(function (item, i) {
          item.style.transition = 'none';
          item.style.transform = '';
          tracks[i].style.transition = 'none';
          tracks[i].style.transform = '';
        });
        apply(to);
        var after = items.map(function (item) { return item.getBoundingClientRect().top; });
        var nextHeights = tracks.map(function (track) { return track.getBoundingClientRect().height; });
        items.forEach(function (item, i) {
          item.style.transition = 'none';
          item.style.transform = 'translateY(' + ((before[i] - after[i]) / zoom) + 'px)';
          tracks[i].style.transition = 'none';
          tracks[i].style.transform = 'scaleY(' + (heights[i] / nextHeights[i]) + ')';
        });
        void scene.offsetHeight;
        items.forEach(function (item, i) {
          item.style.transition = '';
          item.style.transform = '';
          tracks[i].style.transition = '';
          tracks[i].style.transform = '';
        });
      }

      function select(to) {
        if (timer) { clearTimeout(timer); timer = null; }
        if (changing) {
          // Rapid clicks retain the last selection: start the next layout change
          // after the current one completes, preventing text overlap.
          queued = to;
          return;
        }
        if (to === at) { plan(); return; }
        if (!animated) {
          apply(to);
          at = to;
          plan();
          return;
        }
        changing = true;
        target = to;
        scene.classList.add('is-changing-step');
        // Keep the old description's space in flow while it fades out.
        // Start rearranging only after it has completely disappeared.
        phaseTimer = setTimeout(function () {
          moveTo(target);
          at = target;
          plan();
          phaseTimer = setTimeout(function () {
            // Reveal the new description once the headings reach their destinations.
            scene.classList.remove('is-changing-step');
            phaseTimer = setTimeout(function () {
              phaseTimer = null;
              changing = false;
              if (queued !== null) {
                var next = queued;
                queued = null;
                select(next);
              }
            }, fadeTime);
          }, moveTime);
        }, fadeTime);
      }

      function step() {
        timer = null;
        if (shown) select((at + 1) % items.length);
      }

      items.forEach(function (item, i) {
        item.addEventListener('click', function () { select(i); });
      });

      if (!('IntersectionObserver' in window)) {
        shown = true;
        plan();
        return;
      }
      new IntersectionObserver(function (entries) {
        /* Use the last entry, not the first: the observer may deliver several entries per callback, and the first is the oldest. If the section quickly left and reentered the viewport, the first entry reported it as invisible and stopped the sequence permanently. */
        var now = entries[entries.length - 1].isIntersecting;
        if (now === shown) return;
        shown = now;
        if (!shown) stop(); else plan();
      }, { rootMargin: '0px', threshold: 0 }).observe(scene);
    });
  }

  /* --- 8c. Experience story: Figma 3800:2398 → 3812:9795 ------------
Scroll fills the timeline, fades “never”, rolls “any” into “every”,
then introduces the two-line becoming statement. Initial blurred
cards approach in perspective while pre-rendered focus surfaces crossfade.
------------------------------------------------------------------------ */
  function initTelling(root) {
    root.querySelectorAll('[data-mnl-telling]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');

      var marks = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-telling-mark]'));
      var crosses = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-telling-cross]'));
      var links = Array.prototype.slice.call(scene.querySelectorAll('.mnl-exp-telling__link'));
      var roll = scene.querySelector('[data-mnl-telling-roll]');
      var wordFrom = scene.querySelector('[data-mnl-telling-from]');
      var wordTo = scene.querySelector('[data-mnl-telling-to]');
      var storyKicker = scene.querySelector('[data-mnl-telling-kicker]');
      var originalKicker = storyKicker ? storyKicker.textContent : '';
      var movers = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-telling-move]'));
      var stageA = scene.querySelector('[data-mnl-telling-stage-a]');
      var stageB = scene.querySelector('[data-mnl-telling-stage-b]');
      var flyA = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-telling-fly-a]'));
      var flyB = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-telling-fly-b]'));
      var cardEls = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-telling-card]'));
      var hazeEls = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-telling-haze]'));
      if (marks.length < 2) return;

      var last = marks.length - 1;
      /* The second screen adds a stop beyond the timeline: all markers have already been passed, and only the phrase changes. */
      var hasB = !!(stageA && stageB && flyB.length);
      var lastStop = last + (hasB ? 1 : 0);
      var swap = parseInt(scene.getAttribute('data-mnl-telling-swap') || '900', 10);
      /* The text transition lasts longer than the rest of the step: its halves are separated in time, and each needs as long as both previously took when overlapping. */
      var textSwap = parseInt(scene.getAttribute('data-mnl-telling-textswap') || '1700', 10);

      var infinityCanvas = scene.querySelector('[data-mnl-telling-infinity]');
      var infinity = infinityCanvas && window.InfinityDots3D ? window.InfinityDots3D(infinityCanvas, { interactive: 0 }) : null;
      var finalVisible = false;
      function syncInfinity() {
        if (infinity) infinity[finalVisible && !document.hidden ? 'play' : 'pause']();
      }
      document.addEventListener('visibilitychange', syncInfinity);
      var sectionRoot = scene.closest('.shopify-section') || scene;
      sectionRoot.addEventListener('shopify:section:unload', function () {
        document.removeEventListener('visibilitychange', syncInfinity);
        if (infinity) infinity.destroy();
      }, { once: true });

      function cardsAt(i) {
        for (var n = 0; n < cardEls.length; n++) {
          var card = cardEls[n];
          var at = parseInt(card.getAttribute('data-mnl-telling-enter') || '0', 10);
          var index = n % (cardEls.length / 2);
          var reveal = at || Math.min(index + 1, 3);
          var focus = Math.max(0, Math.min(1, (i - reveal + 1) / 1.5));
          card.style.setProperty('--focus', focus.toFixed(3));
          var depth = at ? (i < at ? -400 : 0) : -180 * (1 - i / lastStop);
          // Equivalent apparent size without 3D layer re-rasterization on reverse.
          card.style.setProperty('--card-scale', (1000 / (1000 - depth)).toFixed(6));
          card.classList.toggle('is-far', at > i || (at > 0 && i === lastStop));
        }
      }

      /* Which scene occupies the stop. Hide using visibility, not opacity: parent opacity would override the per-letter reveal inside it (item 85). */
      function showPhrase(second) {
        if (!hasB) return;
        finalVisible = second;
        syncInfinity();
        stageA.classList.toggle('is-gone', second);
        stageB.classList.toggle('is-live', second);
        stageA.setAttribute('aria-hidden', second ? 'true' : 'false');
        stageB.setAttribute('aria-hidden', second ? 'false' : 'true');
        /* The second screen's light arrives with its phrase. Each layer has its own opacity: an opacity wrapper would isolate additive blending from the background (item 36). */
        for (var n = 0; n < hazeEls.length; n++) hazeEls[n].classList.toggle('is-live', second);
      }

      function camera(p) {
        links.forEach(function (link, index) { link.classList.toggle('is-done', index < Math.round(p * lastStop)); });
      }

      function letters(w) {
        return w ? Array.prototype.slice.call(w.querySelectorAll('.mnl-exp-telling__ch')) : [];
      }

      /* Which word appears at stop i. It changes only at the last stop: 'never' fades earlier, and 'every' is the final state in the new design. */
      function wantTo(i) { return i >= last; }

      function setStoryKicker(i) {
        if (storyKicker) storyKicker.textContent = wantTo(i)
          ? storyKicker.getAttribute('data-actions-kicker') : originalKicker;
      }

      function swapStoryKicker(i, tl) {
        if (!storyKicker) return;
        // Change the string only while fully hidden; both directions share the transition.
        tl.to(storyKicker, {
          opacity: 0, filter: 'blur(8px)', duration: wdur * 0.35, ease: 'power2.in'
        }, 0);
        tl.add(function () { setStoryKicker(i); }, wdur * 0.4);
        tl.to(storyKicker, {
          opacity: 1, filter: 'blur(0px)', duration: wdur * 0.45, ease: 'power2.out'
        }, wdur * 0.45);
        tl.add(function () {
          gsap.set(storyKicker, { clearProps: 'opacity,filter' });
        }, wdur * 0.9);
      }

      function setWord(toIsNow) {
        if (!wordFrom || !wordTo) return;
        wordFrom.classList.toggle('is-now', !toIsNow);
        wordTo.classList.toggle('is-now', toIsNow);
        wordFrom.style.opacity = toIsNow ? '0' : '';
        wordTo.style.opacity = toIsNow ? '' : '0';
        wordFrom.setAttribute('aria-hidden', toIsNow ? 'true' : 'false');
        wordTo.setAttribute('aria-hidden', toIsNow ? 'false' : 'true');
      }

      function apply(i) {
        for (var k = 0; k < marks.length; k++) marks[k].classList.toggle('is-done', k <= i);
        for (var c = 0; c < crosses.length; c++) crosses[c].classList.toggle('is-crossed', i >= c + 1);
        setStoryKicker(i);
        setWord(wantTo(i));
        showPhrase(i > last);
        cardsAt(i);
        camera(lastStop ? i / lastStop : 0);
      }
      apply(0);

      if (!animated || scene.getAttribute('data-mnl-telling-lock') === '0') return;

      var dur = swap / 1000;
      var wdur = Math.max(dur, textSwap / 1000);

      /* Changing the word changes the window width and shifts 'on' and 'thing.'. Do not animate width (item 22); apply layout immediately and animate the visible movement with a translation. */
      function swapWord(toIsNow, tl) {
        if (!roll || !wordFrom || !wordTo) return;
        var before = movers.map(function (el) { return el.getBoundingClientRect().left; });
        var out = toIsNow ? wordFrom : wordTo;
        var inn = toIsNow ? wordTo : wordFrom;

        setWord(toIsNow);
        /* Both copies remain visible during the roll: GSAP fades them letter by letter, while the words themselves stay opaque. Previously the incoming word stayed at zero opacity until 0.9 of the step: its letters faded in correctly, but the parent's zero opacity hid them and the word appeared abruptly at the very end. */
        out.style.opacity = '1';
        inn.style.opacity = '1';

        /* Neighboring words stay still while the old word exits, then shift into the empty line as the new word arrives. Moving them immediately would drag the outgoing word sideways with them. */
        movers.forEach(function (el, n) {
          var dx = before[n] - el.getBoundingClientRect().left;
          if (dx) {
            tl.fromTo(el, { x: dx },
              { x: 0, duration: wdur * 0.5, ease: 'power2.inOut' }, wdur * 0.38);
          }
        });

        var away = toIsNow ? 62 : -62;
        /* Strictly sequential: the old word exits completely before the new one enters, per the owner's request on 28.09.2026. Previously the halves overlapped, because both words are visible in the design, but during motion 'every' and 'any' overlapped throughout the middle of the step. Calculate letter stagger so that the final letter of each half arrives exactly at its end; otherwise the halves overlap again. */
        var OUT_END = 0.44;   // Fraction of the step used for exit
        var IN_FROM = 0.52;   // Fraction at which the new word starts entering
        function span(els, len) {
          var st = els.length > 1 ? Math.min(wdur * 0.05, (len * 0.5) / (els.length - 1)) : 0;
          return { st: st, d: Math.max(0.12, len - st * Math.max(0, els.length - 1)) };
        }
        var lo = letters(out);
        var li = letters(inn);
        var o = span(lo, wdur * OUT_END);
        var i = span(li, wdur * (1 - IN_FROM));
        /* Movement and opacity intentionally use separate curves: with one tween, the letter fades before it has moved far enough. */
        tl.to(lo, { y: away, duration: o.d, ease: 'power2.in', stagger: o.st }, 0);
        tl.to(lo, { opacity: 0, duration: o.d, ease: 'power2.in', stagger: o.st }, 0);
        tl.fromTo(li, { y: -away },
          { y: 0, duration: i.d, ease: 'power2.out', stagger: i.st }, wdur * IN_FROM);
        tl.fromTo(li, { opacity: 0 },
          { opacity: 1, duration: i.d, ease: 'power2.out', stagger: i.st }, wdur * IN_FROM);
        tl.add(function () {
          out.style.opacity = '0';
          gsap.set(lo.concat(li), { clearProps: 'y,opacity' });
          out.style.opacity = '0';
          inn.style.opacity = '';
        }, wdur);
      }

      /* Step five: the entire phrase exits downward word by word while the second arrives from above. This uses the same technique as the drum, but the unit is a word rather than a letter: movement and opacity use separate curves, with outgoing and incoming phases overlapping to avoid an empty midpoint (item 84). Reverse traversal mirrors this. Animate opacity on the words themselves, not the scene: the scene only toggles visibility (item 85). */
      function flyPhrase(toB, tl) {
        if (!hasB) return;
        finalVisible = toB;
        syncInfinity();
        var out = toB ? flyA : flyB;
        var inn = toB ? flyB : flyA;
        stageA.classList.remove('is-gone');
        stageB.classList.add('is-live');
        for (var h = 0; h < hazeEls.length; h++) hazeEls[h].classList.toggle('is-live', toB);

        var away = toB ? 62 : -62;
        /* Strictly sequential: the entire old phrase exits before the new one enters. Drum-style overlap is unsuitable here: there one word changes within a line; here two phrases occupy the same position and become illegible midway, with 'You've' over 'follow' and the strikethrough crossing the new heading. Calculate stagger so that the last part of each half arrives exactly at its end, keeping the step's original total duration. Extending it would lose the next wheel movement because the controller waits for transition completion (item 84). */
        var gone = wdur * 0.46;
        var comes = wdur * 0.54;
        var so = out.length > 1 ? (gone * 0.45) / (out.length - 1) : 0;
        var si = inn.length > 1 ? ((wdur - comes) * 0.4) / (inn.length - 1) : 0;
        var eo = gone - so * Math.max(0, out.length - 1);
        var ei = (wdur - comes) - si * Math.max(0, inn.length - 1);

        gsap.set(out, { opacity: 1 });
        gsap.set(inn, { opacity: 0 });
        tl.to(out, { y: away, duration: eo, ease: 'power2.in', stagger: so }, 0);
        tl.to(out, { opacity: 0, duration: eo, ease: 'power2.in', stagger: so }, 0);
        tl.fromTo(inn, { y: -away },
          { y: 0, duration: ei, ease: 'power2.out', stagger: si }, comes);
        tl.fromTo(inn, { opacity: 0 },
          { opacity: 1, duration: ei, ease: 'power2.out', stagger: si }, comes);
        tl.add(function () {
          gsap.set(out.concat(inn), { clearProps: 'transform,opacity' });
          showPhrase(toB);
        }, wdur);
      }

      stepStage(scene, {
        nativeMobile: true,
        forwardOnly: true,
        count: lastStop + 1,
        wheelDistance: 10,
        // On phones lift the tall scene until its timeline sits about 44px
        // from the viewport top. Desktop keeps its original capture position.
        lift: function () {
          return window.matchMedia('(max-width: 767px)').matches ? 66 : 0;
        },
        run: function (from, to, done) {
          var tl = gsap.timeline({ onComplete: done });

          /* Markers and strikethrough use CSS transitions: apply the class immediately, then the compositor handles them. */
          for (var k = 0; k < marks.length; k++) marks[k].classList.toggle('is-done', k <= to);
          for (var c = 0; c < crosses.length; c++) crosses[c].classList.toggle('is-crossed', to >= c + 1);

          if (wantTo(to) !== wantTo(from)) swapStoryKicker(to, tl);

          var textStep = false;
          if (wantTo(to) !== wantTo(from)) { swapWord(wantTo(to), tl); textStep = true; }
          if ((to > last) !== (from > last)) { flyPhrase(to > last, tl); textStep = true; }

          /* Incoming cards and camera approach: one write per step, followed by CSS transitions. */
          cardsAt(to);
          camera(lastStop ? to / lastStop : 0);

          /* Keep the duration equal to the transition so the next gesture cannot arrive before the cards finish moving. Position 0 is required: without it GSAP appends the hold, making word-changing steps twice as long as the others. The controller then waits another 320 ms after the transition, swallowing the next wheel movement during that pause. */
          tl.to({}, { duration: textStep ? wdur : dur }, 0);

          /* No extra time at the end: the roll and approach finish together, and the controller manages the pause between gestures. An extra 0.15 of the duration swallowed one gesture on reverse traversal because the transition finished after the visitor released the wheel. */
        }
      });
    });
  }

  /* --- 9. Header clock: 06:41 · FIRST LIGHT --- */
  function phase(h) {
    if (h < 5)  return 'NIGHT';
    if (h < 9)  return 'FIRST LIGHT';
    if (h < 12) return 'MORNING';
    if (h < 17) return 'DAYLIGHT';
    if (h < 21) return 'GOLDEN HOUR';
    return 'NIGHTFALL';
  }
  function initClock(root) {
    root.querySelectorAll('[data-mnl-clock]').forEach(function (el) {
      if (el.dataset.mnlClockOn) return;
      el.dataset.mnlClockOn = '1';
      var tick = function () {
        var d = new Date();
        var t = el.querySelector('[data-mnl-clock-time]');
        var p = el.querySelector('[data-mnl-clock-phase]');
        if (t) t.textContent = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        if (p) p.textContent = phase(d.getHours());
      };
      tick();
      setInterval(tick, 20000);
    });
  }

  /* --- 10. Draggable horizontal carousels --- */
  function initDrag(root) {
    root.querySelectorAll('[data-mnl-drag]').forEach(function (track) {
      if (track.dataset.mnlDragOn) return;
      track.dataset.mnlDragOn = '1';
      var id = null, startX = 0, startScroll = 0, moved = false;
      // The clipped preview at the right edge is soft; revealed cards are sharp.
      var previewCards = track.querySelectorAll('.mnl-system__card');
      if (previewCards.length) {
        var previewFrame = 0;
        function updatePreviewBlur() {
          previewFrame = 0;
          var edge = track.getBoundingClientRect().right;
          previewCards.forEach(function (card) {
            var box = card.getBoundingClientRect();
            card.classList.toggle('is-edge-preview', box.left < edge && box.right > edge + 1);
          });
        }
        function queuePreviewBlur() {
          if (!previewFrame) previewFrame = requestAnimationFrame(updatePreviewBlur);
        }
        track.addEventListener('scroll', queuePreviewBlur, { passive: true });
        window.addEventListener('resize', queuePreviewBlur, { passive: true });
        updatePreviewBlur();
      }


      track.addEventListener('pointerdown', function (e) {
        moved = false;
        // Leave touch input alone: native scrolling works better there.
        if (e.pointerType === 'touch' || e.button !== 0) return;
        id = e.pointerId;
        startX = e.clientX;
        startScroll = track.scrollLeft;
      });

      track.addEventListener('pointermove', function (e) {
        if (id === null || e.pointerId !== id) return;
        var dx = e.clientX - startX;
        if (!moved) {
          if (Math.abs(dx) <= 4) return;
          moved = true;
          /* Capture the pointer only here, once dragging has started. Capturing on pointerdown makes the browser dispatch click to the capturing element rather than the clicked target, preventing normal card clicks from reaching the card. */
          try { track.setPointerCapture(id); } catch (err) {}
          track.style.cursor = 'grabbing';
        }
        track.scrollLeft = startScroll - dx;
      });

      function release(e) {
        if (id === null) return;
        if (e && e.pointerId !== undefined && e.pointerId !== id) return;
        try { track.releasePointerCapture(id); } catch (err) {}
        id = null;
        track.style.cursor = '';
      }
      ['pointerup', 'pointercancel'].forEach(function (ev) {
        track.addEventListener(ev, release);
      });
      // Before capture, leaving the element simply cancels the pending gesture.
      track.addEventListener('pointerleave', function (e) {
        if (!moved) release(e);
      });

      // A click after dragging ends the gesture; it is not a card click.
      track.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
    });
  }

  /* --- 11. List highlighted on scroll --- */
  function initSteps(root) {
    root.querySelectorAll('[data-mnl-steps]:not([data-mnl-on])').forEach(function (list) {
      list.setAttribute('data-mnl-on', '1');
      var items = list.querySelectorAll('[data-mnl-step]');
      if (!items.length) return;
      if (!animated) { items[0].classList.add('is-active'); return; }
      var host = list.closest('[data-mnl-steps-scope]') || list;
      ScrollTrigger.create({
        trigger: host,
        start: 'top 75%',
        end: 'bottom 60%',
        onUpdate: function (self) {
          var active = Math.min(items.length - 1, Math.floor(self.progress * items.length));
          for (var i = 0; i < items.length; i++) items[i].classList.toggle('is-active', i === active);
        }
      });
    });
  }

  /* --- 12. Header theme follows the section beneath it ---
Determine the section beneath the header from current coordinates, not the callback order of adjacent sections. At a light/dark boundary both edges coincide, and callback order is not guaranteed; this previously left a light header above a dark section. Triggers now only request recalculation at crossings, not every frame. */
  function initHeaderScheme() {
    var header = document.querySelector('[data-mnl-header]');
    if (!header) return;
    var base = header.getAttribute('data-mnl-header') || 'dark';
    var probe = header.offsetHeight / 2 || 28;
    var scenes = [];

    function resolve() {
      if (header.classList.contains('is-menu-open')) return;
      var scheme = base;
      var inner = window.scrollY > 0;
      probe = header.getBoundingClientRect().height / 2 || 28;
      scenes = Array.prototype.map.call(document.querySelectorAll('[data-mnl-scheme]'), function (el) { return { el: el }; });
      for (var i = 0; i < scenes.length; i++) {
        var r = scenes[i].el.getBoundingClientRect();
        if (r.top <= probe && r.bottom > probe) {
          // Read the attribute each time: the section may have changed scenes.
          scheme = scenes[i].el.getAttribute('data-mnl-scheme') || base;
          inner = !Array.prototype.some.call(scenes[i].el.classList, function (name) { return /^mnl-(?:[a-z]+-)?hero$/.test(name); });
        }
      }
      header.classList.toggle('is-light', scheme === 'light');
      header.classList.toggle('is-inner', inner);
    }

    document.querySelectorAll('[data-mnl-scheme]').forEach(function (sec) {
      scenes.push({ el: sec });
    });
    syncHeader = resolve;
    var headerFrame = 0;
    window.addEventListener('scroll', function () {
      if (headerFrame) return;
      headerFrame = requestAnimationFrame(function () { headerFrame = 0; resolve(); });
    }, { passive: true });
    window.addEventListener('resize', resolve);
    resolve();

    if (!(gsap && ScrollTrigger)) return;
    scenes.forEach(function (scene) {
      ScrollTrigger.create({
        trigger: scene.el,
        start: 'top ' + probe + 'px',
        end: 'bottom ' + probe + 'px',
        // Initialize below the pinned track: let it insert its spacer first,
        // then calculate scene boundaries.
        refreshPriority: -1,
        onToggle: resolve
      });
    });
    ScrollTrigger.addEventListener('refresh', resolve);
  }

  /* --- 12b. Header hides when scrolling down ---
Owner request on 28.09.2026: scrolling down hides the menu; scrolling up restores it.
Three necessary conditions:
- Always show the header at the very top, otherwise it flashes on the first wheel tick.
- Do nothing while the step controller owns scrolling. It uses scrollTo to position the section, a downward movement that would otherwise hide the header whenever a visitor enters a section.
- Keep the header in place while the menu overlay is open.
A threshold of a few points filters jitter: fine scrolling moves the page by fractions of a pixel, and without a threshold the class would toggle every frame. A CSS transition handles movement; the script changes the class once per direction change (item 82). */
  function initHeaderHide() {
    var header = document.querySelector('[data-mnl-header-hide]');
    if (!header) return;
    /* With reduced motion, never hide the header: the theme's global rule reduces all transitions to 0.001 ms, which would make it flash instead of slide. */
    if (reduced) return;
    var h = header.offsetHeight || 56;
    var STEP = 6;       // Movement below this threshold counts as jitter.
    var last = window.pageYOffset || 0;
    var hidden = false;
    var moments = Array.from(document.querySelectorAll('[data-mnl-moment]'));
    var mobileHeader = window.matchMedia('(max-width: 767px)');

    function show(v) {
      if (v === hidden) return;
      hidden = v;
      header.classList.toggle('is-hidden', v);
    }

    function onScroll() {
      var y = window.pageYOffset || 0;
      var dy = y - last;
      if (Math.abs(dy) < STEP) return;
      last = y;
      if (mobileHeader.matches && moments.some(function (scene) {
        var box = scene.getBoundingClientRect();
        return box.top <= h && box.bottom >= window.innerHeight;
      })) { show(false); return; }
      if (scrollCaptured) return;
      var burger = header.querySelector('[aria-expanded]');
      if (burger && burger.getAttribute('aria-expanded') === 'true') { show(false); return; }
      if (y <= h) { show(false); return; }
      show(dy > 0);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* --- 12c. Insights card quotes ---
Owner request on 28.09.2026: quotes change automatically. The upper quote exits upward word by word with blur; the lower quote springs upward as a whole, and the next quote appears below in its place. The blue selection appears after the line settles. The lower line always contains the next quote in the loop, making the card read as a continuous sequence.
CSS transitions handle movement, with each word's delay in the markup. The script only changes classes, one write per change (item 82).
The highlighted fragment uses separate strips, like text selected on a phone. These are separate elements rather than the fragment's background: a wrapped background would have a gap between pieces, and would move with the exiting words. Read the fragment's getClientRects once per quote change, receiving one rectangle per line. */
  function initQuotes(root) {
    root.querySelectorAll('[data-mnl-quotes]:not([data-mnl-on])').forEach(function (box) {
      box.setAttribute('data-mnl-on', '1');

      var slots = Array.prototype.slice.call(box.querySelectorAll('.mnl-hero__slot'));
      if (!slots.length) return;
      var lists = slots.map(function (s) {
        return Array.prototype.slice.call(s.querySelectorAll('.mnl-hero__quote'));
      });
      var count = lists[0].length;
      if (count < 2) return;

      var card = box.closest('.mnl-hero__card') || box.parentNode;
      var bars = Array.prototype.slice.call(card.querySelectorAll('[data-mnl-quote-bar]'));
      var caretA = card.querySelector('[data-mnl-quote-caret="a"]');
      var caretB = card.querySelector('[data-mnl-quote-caret="b"]');

      var hold = parseInt(box.getAttribute('data-mnl-quotes-hold') || '5000', 10);
      var step = parseInt(box.getAttribute('data-mnl-quotes-step') || '45', 10);
      var swap = parseInt(box.getAttribute('data-mnl-quotes-swap') || '560', 10);
      /* The strip extends slightly beyond the letters on both sides, matching the design, which starts 2 points before the first letter. */
      var pad = parseFloat(box.getAttribute('data-mnl-quotes-pad') || '2') || 0;
      var riseDur = parseInt(box.getAttribute('data-mnl-quotes-rise') || '620', 10);
      var selDelay = parseInt(box.getAttribute('data-mnl-quotes-seldelay') || '220', 10);
      var selLead = parseInt(box.getAttribute('data-mnl-quotes-sellead') || '240', 10);

      /* The exit lasts until the last word of the upper line finishes: longer quotes have more words and must not be rushed. The lower line moves as a whole, not word by word. */
      function leaveMs(i) {
        var n = lists[0][i].querySelectorAll('.mnl-hero__w').length;
        return swap + step * Math.max(0, n - 1);
      }

      /* Position strips in card coordinates. The whole page scales through zoom (item 55), while getBoundingClientRect returns scaled values. Divide by the scale or the strip drifts increasingly as the viewport narrows. */
      function zoomOf() {
        var z = parseFloat(getComputedStyle(document.body).zoom);
        return z && z > 0 ? z : 1;
      }

      function marks(i) {
        var mark = lists[0][i].querySelector('[data-mnl-quote-mark]');
        if (!mark) return [];
        var base = card.getBoundingClientRect();
        var z = zoomOf();
        var out = [];
        var rects = mark.getClientRects();
        for (var r = 0; r < rects.length; r++) {
          if (rects[r].width < 1) continue;
          out.push({
            left: (rects[r].left - base.left) / z,
            top: (rects[r].top - base.top) / z,
            width: rects[r].width / z,
            height: rects[r].height / z
          });
        }
        return out;
      }

      var barH = bars.length ? parseFloat(getComputedStyle(bars[0]).height) || 24.8 : 24.8;

      function paint(i) {
        var rs = marks(i);
        for (var b = 0; b < bars.length; b++) {
          var r = rs[b];
          if (!r) { bars[b].classList.remove('is-live'); continue; }
          // Center the design-height strip vertically on the text line.
          bars[b].style.left = (r.left - pad).toFixed(2) + 'px';
          bars[b].style.top = (r.top + (r.height - barH) / 2).toFixed(2) + 'px';
          bars[b].style.width = (r.width + pad * 2).toFixed(2) + 'px';
          bars[b].classList.add('is-live');
        }
        if (!rs.length) {
          if (caretA) caretA.classList.remove('is-live');
          if (caretB) caretB.classList.remove('is-live');
          return;
        }
        var first = rs[0];
        var last = rs[rs.length - 1];
        /* Selection handles sit just outside the selection: the upper handle left of the start and the lower handle right of the end. Design values: 3.093 and 2.43. */
        if (caretA) {
          caretA.style.left = (first.left - pad - 1.093).toFixed(2) + 'px';
          caretA.style.top = (first.top + (first.height - barH) / 2 + 1.024).toFixed(2) + 'px';
          caretA.classList.add('is-live');
        }
        if (caretB) {
          caretB.style.left = (last.left + last.width + pad - 2.43).toFixed(2) + 'px';
          caretB.style.top = (last.top + (last.height - barH) / 2 - 2.143).toFixed(2) + 'px';
          caretB.classList.add('is-live');
        }
      }

      function dim() {
        for (var b = 0; b < bars.length; b++) bars[b].classList.remove('is-live');
        if (caretA) caretA.classList.remove('is-live');
        if (caretB) caretB.classList.remove('is-live');
      }

      var at = 0;
      var timer = null;
      var shown = false;
      paint(0);
      /* Before the font loads, fallback-font measurements misalign strips with words. Recalculate once the font is ready. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { paint(at); });
      }

      var early = null;
      var transitions = [];
      function later(fn, delay) {
        var id = setTimeout(function () {
          transitions = transitions.filter(function (pending) { return pending !== id; });
          if (shown) fn();
        }, delay);
        transitions.push(id);
      }
      function restore() {
        lists.forEach(function (list) {
          list.forEach(function (quote, i) {
            quote.classList.remove('is-out', 'is-rise', 'is-dark');
            quote.classList.toggle('is-live', i === at);
          });
        });
        paint(at);
      }

      function stop() {
        transitions.forEach(clearTimeout);
        transitions = [];
        if (timer) { clearTimeout(timer); timer = null; }
        if (early) { clearTimeout(early); early = null; }
      }

      function plan() {
        stop();
        if (!shown) return;
        /* The selection disappears before the text, per the owner's request on 28.09.2026. Start fading it selLead before the first word moves; use the same fade duration so it is gone when exit begins. */
        early = setTimeout(function () { early = null; dim(); }, Math.max(0, hold - selLead));
        timer = setTimeout(step2, hold);
      }

      /* Quote transition as specified by the owner on 28.09.2026:
- The upper quote exits upward word by word with increasing delay and blur.
- The lower quote springs upward as a whole, naturally brightening as it enters the clear part of the viewport mask.
- The next quote appears below in its place.
- The blue selection appears once everything settles.
Begin entry before exit completes, otherwise the card would be empty midway through the change. */
      function step2() {
        timer = null;
        if (!shown) return;
        var from = at;
        var to = (at + 1) % count;

        var lead = lists[0];
        var next = lists[1] || lists[0];
        var gone = leaveMs(from);
        /* When the lower line starts moving. Originally 0.35 of the exit duration caused the incoming quote to overlap the outgoing one: the spring covers three quarters of the distance in the first quarter of the time, so 'slightly early' effectively means 'already there'. At 0.7 the final word has about 15% opacity remaining. There is no empty interval: the new line is moving up while the old one finishes disappearing. */
        var riseAt = Math.round(gone * 0.7);

        dim();   // Usually already hidden by the timer above, but ensure it here.
        lead[from].classList.add('is-out');

        // The lower quote, now `to`, moves upward.
        later(function () {
          if (!shown) return;
          next[from].classList.add('is-rise');
        }, riseAt);

        /* Reveal the new lower quote before the upward motion ends; otherwise the card would contain only one line during entry. */
        later(function () {
          if (!shown) return;
          var below = next[to];
          below.classList.add('is-dark');
          below.classList.add('is-live');
          void box.offsetHeight;
          below.classList.remove('is-dark');
        }, riseAt + Math.round(riseDur * 0.4));

        later(function () {
          if (!shown) return;
          /* Swap copies: hide the copy that moved and replace it with the identical one from the upper slot. They match and occupy the same position, making the swap invisible. */
          lead[from].classList.remove('is-live', 'is-out');
          next[from].classList.remove('is-live', 'is-rise');
          lead[to].classList.add('is-live');
          at = to;
          plan();
          later(function () { paint(at); }, selDelay);
        }, riseAt + riseDur);


      }

      function watch(v) {
        if (v === shown) return;
        shown = v;
        stop();
        restore();
        if (shown) plan();
      }

      if (!animated) return;
      if (!('IntersectionObserver' in window)) { watch(true); return; }
      new IntersectionObserver(function (entries) {
        watch(entries[entries.length - 1].isIntersecting);
      }, { threshold: 0 }).observe(box);

      /* Viewport width changed, changing page scale: reposition strips because they use explicit coordinates rather than document flow. */
      window.addEventListener('resize', function () { paint(at); });
    });
  }

  /* --- 13. Video plays only while visible ---
The footer appears on every page, so its video must not load on every page load. It uses preload=none and loads/plays only when its panel enters the viewport, pausing when it leaves to avoid offscreen decoding.
With prefers-reduced-motion, it never starts and the poster remains. Browser refusal to play, such as gesture requirements or battery saving, is not a failure: the poster is already visible and no error needs to be thrown. */
  // Scroll distance directly controls the paused video timeline in both directions.
  // North Star: replay from the beginning after each full section exit.
  function initNorthVideo(root) {
    root.querySelectorAll('[data-mnl-north-video]').forEach(function (video) {
      if (video._mnlNorthCleanup) return;
      var raf = 0, disposed = false, started = false, pending = false;
      var section = video.closest('section');
      var heading = section.querySelector('.mnl-hiw-north__head');
      var frame = null, lastContour = -1, previousStartDistance = null;
      function syncContour(time) {
        var contours = window.mnlNorthContours || [];
        var index = Math.min(contours.length - 1, Math.max(0, Math.round(time * 30)));
        if (index === lastContour || !contours[index]) return;
        video.style.clipPath = contours[index]; lastContour = index;
      }
      function trackFrame(now, metadata) {
        frame = null;
        if (disposed) return;
        syncContour(metadata ? metadata.mediaTime : video.currentTime);
        if (!video.paused && !video.ended) startTracking();
      }
      function startTracking() {
        if (frame !== null || disposed) return;
        frame = video.requestVideoFrameCallback
          ? video.requestVideoFrameCallback(trackFrame) : requestAnimationFrame(trackFrame);
      }
      function syncStill() { syncContour(video.currentTime); }
      video.addEventListener('play', startTracking);
      // Local diagnostic: measure UI frame pacing while the new media is playing.
      if (location.hostname === '127.0.0.1' && new URLSearchParams(location.search).has('north_perf')) {
        video.addEventListener('playing', function () {
          var start = performance.now(), last = start, times = [];
          requestAnimationFrame(function measure(now) {
            if (disposed || video.paused) return;
            times.push(now - last); last = now;
            if (now - start < 3000) requestAnimationFrame(measure);
            else {
              times.sort(function (a, b) { return a - b; });
              console.debug('North video frames', JSON.stringify({ count: times.length,
                p95: times[Math.floor(times.length * .95)], over32: times.filter(function (n) { return n > 32; }).length }));
            }
          });
        });
      }
      video.addEventListener('loadeddata', syncStill);
      video.addEventListener('seeked', syncStill);
      video.addEventListener('ended', syncStill);
      syncContour(0);
      function update() {
        raf = 0;
        if (disposed) return;
        var r = video.getBoundingClientRect();
        var sectionRect = section.getBoundingClientRect();
        // Reference screenshot: heading reaches 24 design pixels from the top.
        var startDistance = heading.getBoundingClientRect().top - sectionRect.width / 1440 * 24;
        var crossedStart = previousStartDistance !== null &&
          ((previousStartDistance > 0 && startDistance <= 0) ||
           (previousStartDistance < 0 && startDistance >= 0));
        previousStartDistance = startDistance;
        if (sectionRect.bottom <= 0 || sectionRect.top >= innerHeight) {
          started = false;
          video.pause();
          if (frame !== null) {
            if (video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(frame);
            else cancelAnimationFrame(frame);
            frame = null;
          }
          if (video.readyState > 0 && video.currentTime !== 0) video.currentTime = 0;
          syncContour(0);
          return;
        }
        var visible = r.bottom > 0 && r.top < innerHeight && !document.hidden;
        // Latch the crossing in either scroll direction, including fast scrolls.
        if (!started && visible && (crossedStart || Math.abs(startDistance) <= 1 || (window.matchMedia('(max-width: 767px)').matches && sectionRect.top < innerHeight * .5))) started = true;
        if (!visible || !started) { video.pause(); return; }
        if (video.paused && !video.ended && !pending) {
          pending = true;
          video.play().catch(function () {}).finally(function () {
            pending = false;
            if (disposed || !started || document.hidden) video.pause();
          });
        }
      }
      function queue() { if (!raf && !disposed) raf = requestAnimationFrame(update); }
      var observer = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        video.preload = 'auto'; video.load(); queue();
      }, { rootMargin: '600px' });
      observer.observe(video);
      video.addEventListener('canplay', queue);
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue);
      document.addEventListener('visibilitychange', queue);
      video._mnlNorthCleanup = function () {
        disposed = true; observer.disconnect(); cancelAnimationFrame(raf); video.pause();
        window.removeEventListener('scroll', queue); window.removeEventListener('resize', queue);
        document.removeEventListener('visibilitychange', queue);
        video.removeEventListener('canplay', queue);
        video.removeEventListener('play', startTracking);
        video.removeEventListener('loadeddata', syncStill);
        video.removeEventListener('seeked', syncStill);
        video.removeEventListener('ended', syncStill);
        if (frame !== null) {
          if (video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(frame);
          else cancelAnimationFrame(frame);
        }
        delete video._mnlNorthCleanup;
      };
    });
  }

  function initVideoStops(root) {
    root.querySelectorAll('[data-mnl-video-stops]').forEach(function (video) {
      if (video._mnlNorthCleanup) video._mnlNorthCleanup();
      if (video._mnlStopsCleanup) return;
      var scene = video.closest('section');
      var target = 0, raf = 0, disposed = false, unavailable = false, ready = false;
      var touchY = null, blobURL = null, controller = new AbortController();
      var pixelsPerSecond = 240;
      video.pause();
      function endTime() { return Math.max(0, video.duration - 1 / 30); }
      function seek() {
        raf = 0;
        if (!ready || disposed || video.seeking) return;
        var time = Math.min(endTime(), Math.round(target * 30) / 30);
        if (Math.abs(video.currentTime - time) > 1 / 60) video.currentTime = time;
      }
      function queue() { if (!raf && !disposed) raf = requestAnimationFrame(seek); }
      // Keep only the latest target while the decoder completes its current seek.
      video.addEventListener('seeked', queue);
      // A blob also supports seeking through the local Shopify proxy without Range.
      fetch(video.src, { signal: controller.signal }).then(function (response) {
        if (!response.ok) throw new Error('Video unavailable');
        return response.blob();
      }).then(function (blob) {
        if (disposed) return;
        blobURL = URL.createObjectURL(blob);
        video.addEventListener('loadedmetadata', loaded, { once: true });
        video.src = blobURL; video.load();
      }).catch(function () { if (!disposed) unavailable = true; });
      function loaded() { ready = true; queue(); }
      function inHero() {
        var rect = scene.getBoundingClientRect();
        return rect.top >= -2 && rect.top < window.innerHeight * .25 && rect.bottom > window.innerHeight * .5;
      }
      function canHandle(delta) {
        return !unavailable && inHero() && (!ready || (delta > 0 ? target < endTime() : target > 0));
      }
      function scrub(delta) {
        smoothStop();
        target = Math.max(0, Math.min(ready ? endTime() : 17.0667, target + delta / pixelsPerSecond));
        scene.classList.toggle('is-video-started', target > 1 / 60);
        queue();
      }
      function wheel(event) {
        if (event.ctrlKey || !event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        var delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1);
        if (!canHandle(delta)) return;
        event.preventDefault(); event.stopImmediatePropagation(); scrub(delta);
      }
      function key(event) {
        if (event.target.closest('input,textarea,select,button,a,[contenteditable]')) return;
        var delta = event.key === 'ArrowDown' ? 40 : event.key === 'ArrowUp' ? -40 :
          event.key === 'PageDown' ? window.innerHeight : event.key === 'PageUp' ? -window.innerHeight :
          event.key === ' ' ? window.innerHeight * (event.shiftKey ? -1 : 1) : 0;
        if (!delta || !canHandle(delta)) return;
        event.preventDefault(); event.stopImmediatePropagation(); scrub(delta);
      }
      function touchStart(event) { touchY = event.touches.length === 1 ? event.touches[0].clientY : null; }
      function touchMove(event) {
        if (touchY === null || event.touches.length !== 1) return;
        var y = event.touches[0].clientY, delta = touchY - y; touchY = y;
        if (!delta || !canHandle(delta)) return;
        event.preventDefault(); event.stopImmediatePropagation(); scrub(delta);
      }
      window.addEventListener('wheel', wheel, { passive: false, capture: true });
      window.addEventListener('keydown', key, true);
      window.addEventListener('touchstart', touchStart, { passive: true, capture: true });
      window.addEventListener('touchmove', touchMove, { passive: false, capture: true });
      video._mnlStopsCleanup = function () {
        disposed = true; controller.abort(); cancelAnimationFrame(raf); video.pause();
        video.removeEventListener('seeked', queue); video.removeEventListener('loadedmetadata', loaded);
        if (blobURL) URL.revokeObjectURL(blobURL);
        window.removeEventListener('wheel', wheel, true);
        window.removeEventListener('keydown', key, true);
        window.removeEventListener('touchstart', touchStart, true);
        window.removeEventListener('touchmove', touchMove, true);
      };
    });
  }

  function initVideo(root) {
    root.querySelectorAll('[data-mnl-video]:not([data-mnl-on])').forEach(function (video) {
      video.setAttribute('data-mnl-on', '1');
      var userPaused = false;
      var toggle = video.closest('[data-mnl-video-toggle]');
      if (toggle) {
        if (toggle.querySelector('[data-mnl-video-poster]')) {
          var posterFrame = null;
          function showVideoPoster() {
            toggle.classList.remove('is-video-ready');
            if (posterFrame !== null) {
              if (video.cancelVideoFrameCallback) video.cancelVideoFrameCallback(posterFrame);
              else cancelAnimationFrame(posterFrame);
              posterFrame = null;
            }
          }
          function revealVideoFrame() {
            posterFrame = null;
            if (video.readyState >= 2 && !video.error) toggle.classList.add('is-video-ready');
          }
          function waitForVideoFrame() {
            if (posterFrame !== null || toggle.classList.contains('is-video-ready')) return;
            posterFrame = video.requestVideoFrameCallback
              ? video.requestVideoFrameCallback(revealVideoFrame)
              : requestAnimationFrame(revealVideoFrame);
          }
          video.addEventListener('playing', waitForVideoFrame);
          video.addEventListener('loadstart', showVideoPoster);
          video.addEventListener('emptied', showVideoPoster);
          video.addEventListener('error', showVideoPoster);
          if (!video.paused && video.readyState >= 2) waitForVideoFrame();
        }
        function syncVideoLabel() {
          toggle.setAttribute('aria-label', video.paused ? 'Play MANIMIND video' : 'Pause MANIMIND video');
        }
        toggle.addEventListener('click', function () {
          userPaused = !video.paused;
          if (userPaused) video.pause();
          else {
            if (video.preload === 'none') video.preload = 'auto';
            var playback = video.play();
            if (playback && playback.catch) playback.catch(syncVideoLabel);
          }
          syncVideoLabel();
        });
        video.addEventListener('play', syncVideoLabel);
        video.addEventListener('pause', syncVideoLabel);
        syncVideoLabel();
      }
      if (reduced || !('IntersectionObserver' in window)) return;

      // Safari may reject autoplay until a user gesture (for example in
      // Low Power Mode). Retry synchronously inside the next gesture.
      video.muted = true;
      video.playsInline = true;
      var waitingForGesture = false;
      function clearGestureRetry() {
        waitingForGesture = false;
        document.removeEventListener('touchend', retryVideo, true);
        document.removeEventListener('click', retryVideo, true);
        document.removeEventListener('keydown', retryVideo, true);
      }
      function retryVideo(event) {
        if (!video.isConnected) { clearGestureRetry(); return; }
        if (userPaused || document.hidden || (toggle && toggle.contains(event.target))) return;
        var bounds = video.getBoundingClientRect();
        if (bounds.bottom <= 0 || bounds.top >= window.innerHeight || bounds.right <= 0 || bounds.left >= window.innerWidth) return;
        var retry = video.play();
        if (retry && retry.catch) retry.catch(handleAutoplayFailure);
      }
      function handleAutoplayFailure(error) {
        if (!error || error.name !== 'NotAllowedError' || waitingForGesture) return;
        waitingForGesture = true;
        document.addEventListener('touchend', retryVideo, true);
        document.addEventListener('click', retryVideo, true);
        document.addEventListener('keydown', retryVideo, true);
      }
      video.addEventListener('playing', clearGestureRetry);

      // data-mnl-video=once plays the video once when the section
      // appears, then holds its final frame. Owner decision
      // on 25.09.2026. Two observers are essential: a 600px margin
      // works for preloading the file, but not for starting playback,
      // otherwise the video finishes offscreen and the visitor
      // sees only its final frame. Preload ahead of time, then play
      // exactly when the section appears.
      if (video.getAttribute('data-mnl-video') === 'once') {
        video.loop = false;

        var warm = new IntersectionObserver(function (entries) {
          for (var w = 0; w < entries.length; w++) {
            if (entries[w].isIntersecting && video.preload === 'none') {
              video.preload = 'auto';
              video.load();
            }
          }
        }, { rootMargin: '600px 0px' });
        warm.observe(video);

        var spent = false;
        var started = false;
        function play() {
          if (video.preload === 'none') video.preload = 'auto';
          var go = video.play();
          if (go && go.catch) go.catch(handleAutoplayFailure);
        }

        /* Playback start point.
Do not start when the video frame enters: the Pen frame is 1477 tall within an 862-tall section, so its top enters long before the section itself and playback finishes before visitors scroll there. Observe the section instead of the frame, waiting until its top reaches the specified fraction of the viewport.
Implement this with a negative bottom margin: fraction N reduces the observation window to the upper (1 − N), and the section intersects precisely when its top passes that mark. Measure the top rather than the fraction of the section visible: a section can be shorter than the viewport (862 versus 820 on a laptop, with even taller viewports on large monitors), so a visible-area threshold could never be reached. Owner request on 25.09.2026. */
        var startEl = video.closest('[data-mnl-video-scope]') || video;
        var risen = parseFloat(video.getAttribute('data-mnl-video-in'));
        var startMargin = risen > 0 && risen < 100 ? '0px 0px -' + risen + '% 0px' : '0px';

        var startObs = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              started = true;
              play();
              startObs.disconnect();
              return;
            }
          }
        }, { rootMargin: startMargin });
        startObs.observe(startEl);

        // Pause if the frame leaves before playback ends, then
        // resume from the same position. Do not restart:
        // once means once per visit.
        var hold = new IntersectionObserver(function (entries) {
          if (spent || !started) return;
          for (var k = 0; k < entries.length; k++) {
            if (entries[k].isIntersecting) play();
            else if (!video.paused) video.pause();
          }
        }, { rootMargin: '0px' });
        hold.observe(video);

        video.addEventListener('ended', function () {
          spent = true;
          startObs.disconnect();
          hold.disconnect();
          warm.disconnect();
        });
        return;
      }

      // The large startup margin is intentional. The first frame after playback starts
      // costs more than subsequent frames: the browser must build the video layer
      // with its blend mode. Measurements show exactly one roughly 33 ms frame
      // caused by blending (absent without screen). The blend mode is required
      // to remove the video's black background, so move that frame offscreen:
      // the layer is ready by the time the panel becomes visible.
      // Pause immediately outside the viewport: one observer handling both roles
      // kept playback running for another 600px and made section boundaries lag
      // (measurement on 30.09.2026, matching the Application videos).
      new IntersectionObserver(function (entries) {
        if (entries[entries.length - 1].isIntersecting && !userPaused) {
          if (video.preload === 'none') video.preload = 'auto';
          var started = video.play();
          if (started && started.catch) started.catch(handleAutoplayFailure);
        }
      }, { rootMargin: '600px 0px' }).observe(video);

      new IntersectionObserver(function (entries) {
        if (!entries[entries.length - 1].isIntersecting) {
          if (!video.paused) video.pause();
        } else if (!userPaused) {
          var again = video.play();
          if (again && again.catch) again.catch(handleAutoplayFailure);
        }
      }, { rootMargin: '0px' }).observe(video);
    });
  }

  /* --- Looping animation ---
The animation lives in section CSS, using transform and opacity keyframes, the cheapest approach without per-frame script work. The script only prevents it from running while the section is offscreen. Set a pause attribute on the scene and let CSS pause its children.
If the script fails to load, no attribute is set and the animation runs continuously: a costlier but functional fallback. With prefers-reduced-motion, the section's media query disables animation entirely, so do not create the observer either. */
  function initLoop(root) {
    root.querySelectorAll('[data-mnl-loop]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');
      if (reduced || !('IntersectionObserver' in window)) return;

      scene.setAttribute('data-mnl-paused', '');
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) scene.removeAttribute('data-mnl-paused');
          else scene.setAttribute('data-mnl-paused', '');
        }
      }, { rootMargin: '200px 0px' });

      io.observe(scene);
    });
  }

  /* --- Smooth mouse-wheel scrolling ---
Mouse wheels move pages in jumps: one tick advances over a hundred pixels. Intercept the event, accumulate a target, then move the page toward it by a fraction of the remaining distance each frame.
Move the actual window scroll position, not a transformed wrapper. This is essential: transforms break the site's position:sticky in System, ScrollTrigger boundaries, and the step controller's getBoundingClientRect calculations. Native scroll positioning leaves all of these intact.
Intentionally leave these untouched:
- Wheel input while Pen or Application owns scrolling (scrollCaptured); their gesture logic must remain independent.
- Ctrl+wheel, which zooms the page, and horizontal scrolling.
- Scrollbar dragging, keyboard navigation, find-in-page and anchors: keep native behavior and adopt the resulting position.
- Phone touch input, which already has inertia that interception would destroy.
- prefers-reduced-motion.
Do not add CSS scroll-behavior:smooth here. It smooths every programmatic scrollTo, including section positioning by the step controller and this per-frame interpolation, producing animation layered on animation. */
  function initSmoothScroll() {
    var attr = document.body && document.body.getAttribute('data-mnl-smooth');
    if (reduced || attr === null || attr === undefined) return;

    // Percentage of remaining distance covered per frame. Higher values feel sharper.
    var ease = parseInt(attr, 10) / 100;
    if (!(ease > 0) || ease >= 1) ease = 0.15;

    var target = window.pageYOffset;
    var cur = target;
    var running = false;
    var frameId = 0;
    var ours = false;   // This scroll event is our own, not an external one.

    function maxY() {
      return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    }

    function frame() {
      frameId = 0;
      if (!running) return;
      target = Math.min(Math.max(target, 0), maxY());
      var d = target - cur;
      // Half a pixel is the stopping threshold: the browser rounds
      // the scroll position anyway.
      if (Math.abs(d) < 0.5) {
        cur = target;
        running = false;
      } else {
        cur += d * ease;
        frameId = requestAnimationFrame(frame);
      }
      ours = true;
      window.scrollTo(0, Math.round(cur));
    }

    smoothStop = function () {
      running = false;
      cancelAnimationFrame(frameId);
      frameId = 0;
      ours = false;
      cur = target = window.pageYOffset;
    };

    function onWheel(e) {
      if (scrollCaptured) { smoothStop(); return; }
      if (e.ctrlKey || e.defaultPrevented) return;
      if (document.documentElement.classList.contains('mnl-locked')) return;
      // Leave sideways scrolling alone for horizontal tracks and trackpads.
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      // deltaMode: 0 = pixels, 1 = lines, 2 = pages.
      var d = e.deltaY;
      if (e.deltaMode === 1) d *= 16;
      else if (e.deltaMode === 2) d *= window.innerHeight;
      if (!d) return;

      // Drop the old target immediately when the gesture reverses.
      if (running && d * (target - cur) < 0) smoothStop();
      var lim = maxY();
      // At document edges, return the event to the browser to preserve
      // overscroll bounce and parent scrolling.
      if ((d < 0 && target <= 0) || (d > 0 && target >= lim)) {
        // Do not hand the wheel back while an older RAF can move us away
        // from the boundary on its next frame.
        if (running) {
          e.preventDefault();
          var edge = d < 0 ? 0 : lim;
          smoothStop();
          cur = target = edge;
          window.scrollTo(0, edge);
        }
        return;
      }

      e.preventDefault();
      target = Math.min(Math.max(target + d, 0), lim);
      if (!running) {
        running = true;
        cur = window.pageYOffset;
        frameId = requestAnimationFrame(frame);
      }
    }

    // For non-wheel scrolling via scrollbar, keys or anchors, adopt
    // the new position so the next wheel tick starts from it.
    function onScroll() {
      if (ours) { ours = false; return; }
      if (running) return;
      cur = target = window.pageYOffset;
    }

    function onResize() {
      target = Math.min(target, maxY());
    }

    window.addEventListener('touchstart', smoothStop, { passive: true });
    window.addEventListener('pointerdown', smoothStop, { passive: true });
    window.addEventListener('keydown', function (event) {
      if (/^(ArrowUp|ArrowDown|PageUp|PageDown|Home|End| )$/.test(event.key)) smoothStop();
    });
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
  }

  /* --- 8d. It begins with awareness: seven-day wheel ---
Uses the same step controller. Each step rotates the wheel by one day and reveals more of the lower phrase. The design shows day four and exactly 12 of 21 words, or 4/7: the phrase follows the day number, not page scroll.
Day positions and scales use CSS transform transitions, one composited layer per row; word opacity also uses transitions. GSAP remains responsible for the progress indicator because it must traverse the track before the day changes, which a single transition cannot express.
------------------------------------------------------------------------ */
  function initInsightSlider(root) {
    root.querySelectorAll('[data-mnl-insight-slider]').forEach(function (track) {
      if (track.dataset.ready) return;
      track.dataset.ready = 'true';
      var cards = Array.from(track.children), mobile = matchMedia('(max-width: 767px)');
      var frame = 0;
      function center(index, smooth) {
        var a = track.getBoundingClientRect(), b = cards[index].getBoundingClientRect();
        track.scrollTo({ left: track.scrollLeft + b.left + b.width / 2 - a.left - a.width / 2, behavior: smooth ? 'smooth' : 'instant' });
      }
      function update() {
        frame = 0;
        var box = track.getBoundingClientRect(), middle = box.left + box.width / 2;
        var selected = cards.reduce(function (best, card, i) {
          var r = card.getBoundingClientRect(), distance = Math.abs(r.left + r.width / 2 - middle);
          return distance < best.distance ? { index: i, distance: distance } : best;
        }, { index: 1, distance: Infinity }).index;
        cards.forEach(function (card, i) {
          card.classList.toggle('is-centered', i === selected);
          card.classList.toggle('is-before', i < selected);
          card.classList.toggle('is-after', i > selected);
        });
      }
      function setup() {
        if (!mobile.matches) return;
        center(1, false); update();
      }
      track.addEventListener('scroll', function () { if (!frame) frame = requestAnimationFrame(update); }, { passive: true });
      track.addEventListener('keydown', function (event) {
        if (!mobile.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        var index = cards.findIndex(function (card) { return card.classList.contains('is-centered'); });
        center(Math.max(0, Math.min(cards.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1))), true);
      });
      mobile.addEventListener('change', setup);
      setup();
    });
  }

  function initDays(root) {
    root.querySelectorAll('[data-mnl-days]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');

      var rows = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-day]'));
      var lead = scene.querySelector('[data-mnl-days-lead]');
      if (rows.length < 2) return;

      /* Neighbor positions and scales come from the design; see the section comment. Beyond the third neighbor they are invisible because the wheel mask is already fully transparent. */
      var YS = [0, 83.6265, 145.5325, 198.906, 243.9, 281.9, 313.9];
      var SS = [1, 0.664818, 0.599614, 0.469182, 0.37, 0.29, 0.23];
      function slotY(d) { return (d < 0 ? -1 : 1) * (YS[Math.min(Math.abs(d), YS.length - 1)]); }
      function slotS(d) { return SS[Math.min(Math.abs(d), SS.length - 1)]; }

      var dim = parseFloat(getComputedStyle(rows[0]).getPropertyValue('--mnl-o'));
      if (isNaN(dim) || dim === 1) dim = 0.3;

      /* Lower phrase: the fill runs across words, so assign each word its fraction of the box width. Use fractions rather than pixels because zoom scales the entire page. Words are already split in markup: before the first script frame, show the complete phrase rather than a paragraph with clipped tails. splitWords is only a fallback for markup without spans. The is-split class enables the continuous fill only after width fractions are measured, avoiding a comb-like pattern. */
      var words = [];
      if (lead) {
        if (!lead.querySelector('.mnl-word')) splitWords(lead);
        words = Array.prototype.slice.call(lead.querySelectorAll('.mnl-word'));
        var box = lead.getBoundingClientRect();
        for (var w = 0; w < words.length; w++) {
          var wr = words[w].getBoundingClientRect();
          if (box.width > 0) {
            words[w].style.setProperty('--mnl-wxf', ((wr.left - box.left) / box.width).toFixed(5));
          }
          words[w].style.setProperty('--mnl-p', (w / Math.max(1, words.length - 1)).toFixed(4));
        }
        if (box.width > 0) lead.classList.add('is-split');
      }

      function apply(i) {
        for (var k = 0; k < rows.length; k++) {
          var d = k - i;
          rows[k].classList.toggle('is-active', d === 0);
          rows[k].style.setProperty('--mnl-y', slotY(d).toFixed(4) + 'px');
          rows[k].style.setProperty('--mnl-s', slotS(d).toFixed(6));
          rows[k].style.setProperty('--mnl-o', d === 0 ? '1' : String(dim));
        }
        var lit = Math.round((words.length * (i + 1)) / rows.length);
        for (var m = 0; m < words.length; m++) words[m].classList.toggle('is-lit', m < lit);
      }
      apply(0);

      var panels = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-day-panel]'));
      var dayImages = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-day-image]'));

      var current = 0, timer = 0, pending = 0, progress = null, visible = false;
      var interval = parseInt(scene.getAttribute('data-mnl-days-interval'), 10) || 6000;
      var effects = [];
      function stop() {
        clearTimeout(timer);
        if (progress) { progress.cancel(); progress = null; }
      }
      function schedule() {
        stop();
        if (!visible || document.hidden) return;
        var thumb = rows[current].querySelector('[data-mnl-day-thumb]');
        if (thumb && animated) progress = thumb.animate(
          [{ transform: window.matchMedia('(max-width: 767px)').matches ? 'scaleX(0)' : 'scaleY(0)' }, { transform: window.matchMedia('(max-width: 767px)').matches ? 'scaleX(1)' : 'scaleY(1)' }],
          { duration: interval, easing: 'linear', fill: 'forwards' }
        );
        timer = setTimeout(function () { select((current + 1) % rows.length); }, interval);
      }
      function select(index) {
        if (index === current) { schedule(); return; }
        clearTimeout(pending);
        effects.forEach(function (effect) { effect.cancel(); });
        effects = [];
        var outgoing = panels.filter(function (panel) { return !panel.hidden; });
        current = index;
        apply(index);
        rows.forEach(function (row, i) { row.setAttribute('aria-pressed', String(i === index)); });
        outgoing.forEach(function (panel) {
          panel.setAttribute('aria-hidden', 'true');
          if (animated) effects.push(panel.animate(
            [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(12px)' }],
            { duration: 180, fill: 'forwards', easing: 'ease-in' }
          ));
        });
        function reveal() {
          effects.forEach(function (effect) { effect.cancel(); });
          effects = [];
          dayImages.forEach(function (image, i) {
            image.classList.toggle('is-active', i === index);
            image.setAttribute('aria-hidden', String(i !== index));
          });
          panels.forEach(function (panel, i) {
            panel.hidden = i !== index;
            panel.setAttribute('aria-hidden', String(i !== index));
          });
          if (animated && panels[index]) Array.prototype.forEach.call(panels[index].children, function (child, i) {
            effects.push(child.animate(
              [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }],
              { duration: 800, delay: i * 100, fill: 'backwards', easing: 'cubic-bezier(.22,1,.36,1)' }
            ));
          });
        }
        if (animated) pending = setTimeout(reveal, 180); else reveal();
        schedule();
      }
      rows.forEach(function (row, i) {
        row.addEventListener('click', function () { select(i); });
        row.addEventListener('keydown', function (event) {
          var next = event.key === 'ArrowDown' ? (i + 1) % rows.length :
            event.key === 'ArrowUp' ? (i + rows.length - 1) % rows.length :
            event.key === 'Home' ? 0 : event.key === 'End' ? rows.length - 1 : -1;
          if (next < 0) return;
          event.preventDefault(); select(next); rows[next].focus({ preventScroll: true });
        });
      });
      var observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.35;
        schedule();
      }, { threshold: [0, 0.35] });
      observer.observe(scene.querySelector('.mnl-hiw-days__stage') || scene);
      var readingObserver = new IntersectionObserver(function (entries) {
        scene.classList.toggle('is-reading', entries[0].isIntersecting && entries[0].intersectionRatio >= 0.35);
      }, { threshold: [0, 0.35] });
      readingObserver.observe(scene);
      document.addEventListener('visibilitychange', schedule);
      function dispose(event) {
        if (!event.target.contains(scene)) return;
        stop(); clearTimeout(pending); observer.disconnect(); readingObserver.disconnect();
        effects.forEach(function (effect) { effect.cancel(); });
        document.removeEventListener('visibilitychange', schedule);
        document.removeEventListener('shopify:section:unload', dispose);
      }
      document.addEventListener('shopify:section:unload', dispose);
    });
  }

  /* Continuous fill across words: the script only measures width fractions. Words are split in markup, so the finished phrase is visible before the first script frame. Enable the fill with is-split only after measurement; earlier, each word would crop from its own left edge (item 72). */
  function initFill(root) {
    root.querySelectorAll('[data-mnl-fill]:not([data-mnl-on])').forEach(function (el) {
      el.setAttribute('data-mnl-on', '1');
      var box = el.getBoundingClientRect();
      if (!box.width) return;
      // Use fractions, not pixels: zoom scales the entire page.
      el.querySelectorAll('.mnl-word').forEach(function (w) {
        var r = w.getBoundingClientRect();
        w.style.setProperty('--mnl-wxf', ((r.left - box.left) / box.width).toFixed(5));
      });
      el.classList.add('is-split');
    });
  }

  /* Arrow-controlled decks: the laws card in North Star and frame captions in Intelligent Action Engine. Owner decision on 26.09.2026: navigate only with arrows, without intercepting scroll or advancing automatically. CSS transform and opacity transitions handle motion; the script sets the target once per click (item 82).
Each item has a narrow progress segment, with a separate wide segment above the active one. Hide the base segment underneath it so resting geometry stays accurate without stretching rounded corners. Markup provides step size and extra width as variables; the script only calculates the index. */
  function initEvidenceWheel(root) {
    root.querySelectorAll('[data-mnl-evidence-wheel]').forEach(function (scene) {
      if (scene._mnlEvidenceCleanup) return;
      var words = Array.from(scene.querySelectorAll('[data-mnl-evidence-word]'));
      var month = scene.querySelector('[data-mnl-evidence-month]');
      var message = scene.querySelector('[data-mnl-evidence-message]');
      var active = 0;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      var animations = [];
      var mobile = window.matchMedia("(max-width: 749px)");
      var pointer = null, suppressClick = false;
      function rotationFor(i) {
        var offset = i - active;
        if (mobile.matches) {
          if (offset > words.length / 2) offset -= words.length;
          if (offset < -words.length / 2) offset += words.length;
        }
        return "rotate(" + offset * (mobile.matches ? 24 : 11) + "deg)";
      }
      function updateMessage() {
        if (!message) return;
        var value = words[active].dataset.message || '';
        message.textContent = value;
          var parts = value.split('\n');
          var heading = parts.shift();
          var breaks = ['You’re starting to ', 'You’re moving forward ', 'You’re taking charge ', 'You’re spending less '];
          breaks.forEach(function (prefix) {
            if (heading.indexOf(prefix) === 0) heading = prefix.trim() + '\n' + heading.slice(prefix.length);
          });
          var title = document.createElement('span');
          title.className = 'mnl-ins-evidence__message-title';
          title.textContent = heading;
          var body = document.createElement('span');
          body.className = 'mnl-ins-evidence__message-body';
          body.textContent = parts.join('\n');
          message.replaceChildren(title, body);
      }
      function layout() { updateMessage(); words.forEach(function(word, i) { word.style.transform = rotationFor(i); }); }
      layout();
      mobile.addEventListener("change", layout);
      function select(index) {
        if (index === active) return;
        // Read the current rotation before canceling, so rapid clicks remain smooth.
        var transforms = words.map(function (word) { return getComputedStyle(word).transform; });
        animations.forEach(function (animation) { animation.cancel(); });
        animations = [];
        var previous = active;
        active = index;
        if (month) month.textContent = words[index].dataset.month || '';
        updateMessage();
        words.forEach(function (word, i) {
          var rotation = rotationFor(i);
          word.style.transform = rotation;
          word.setAttribute('aria-pressed', String(i === active));
          // Recycled offscreen labels must never sweep across the visible arc.
          var oldOffset = i - previous, newOffset = i - active;
          if (oldOffset > words.length / 2) oldOffset -= words.length;
          if (oldOffset < -words.length / 2) oldOffset += words.length;
          if (newOffset > words.length / 2) newOffset -= words.length;
          if (newOffset < -words.length / 2) newOffset += words.length;
          if (!reduced.matches && (!mobile.matches || Math.abs(newOffset - oldOffset) <= 1)) animations.push(word.animate(
            [{ transform: transforms[i] }, { transform: rotation }],
            { duration: 800, easing: 'cubic-bezier(.22,.61,.36,1)' }
          ));
        });
        scene.dataset.activeEvidence = words[index].querySelector('.mnl-ins-evidence__straight').textContent.trim();
      }
      function click(event) {
        if (suppressClick) { suppressClick = false; return; }
        var word = event.target.closest('[data-mnl-evidence-word]');
        if (word) select(words.indexOf(word));
      }
      function down(event) {
        if (!mobile.matches || !event.isPrimary || event.button !== 0) return;
        suppressClick = false;
        pointer = { x: event.clientX, y: event.clientY };
      }
      function up(event) {
        if (!pointer) return;
        var dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
        pointer = null;
        if (Math.abs(dx) < 35 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        suppressClick = true;
        select((active + (dx < 0 ? 1 : words.length - 1)) % words.length);
      }
      function cancel() { pointer = null; }
      function key(event) {
        if (!mobile.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        select((active + (event.key === 'ArrowRight' ? 1 : words.length - 1)) % words.length);
      }
      scene.addEventListener('pointerdown', down);
      scene.addEventListener('pointerup', up);
      scene.addEventListener('pointercancel', cancel);
      scene.addEventListener('keydown', key);
      scene.addEventListener('click', click);
      scene._mnlEvidenceCleanup = function () {
        scene.removeEventListener('click', click);
        scene.removeEventListener('pointerdown', down);
        scene.removeEventListener('pointerup', up);
        scene.removeEventListener('pointercancel', cancel);
        scene.removeEventListener('keydown', key);
        mobile.removeEventListener('change', layout);
        animations.forEach(function (animation) { animation.cancel(); });
      };
    });
  }

  function initMindSlider(root) {
    root.querySelectorAll('[data-mnl-mind-slider]').forEach(function (scene) {
      if (scene._mnlMindCleanup) return;
      var quotes = Array.from(scene.querySelectorAll('[data-mnl-mind-quote]'));
      var bars = Array.from(scene.querySelectorAll('[data-mnl-mind-bar]'));
      var current = 0, visible = false, progress = null, reveal = null;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      function stop() {
        if (progress) { progress.cancel(); progress = null; }
        if (reveal) { reveal.cancel(); reveal = null; }
      }
      function start() {
        stop();
        if (!visible || document.hidden) return;
        progress = bars[current].firstElementChild.animate(
          [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
          { duration: 6000, easing: 'linear', fill: 'forwards' }
        );
        progress.onfinish = function () {
          function replaceQuote() {
            quotes[current].hidden = true;
            bars[current].classList.remove('mnl-ins-manimind__bar--wide');
            current = (current + 1) % quotes.length;
            quotes[current].hidden = false;
            bars[current].classList.add('mnl-ins-manimind__bar--wide');
            scene.dataset.activeQuote = String(current + 1);
            if (progress) { progress.cancel(); progress = null; }
            if (reveal) { reveal.cancel(); reveal = null; }
            if (reduced.matches) { start(); return; }
            reveal = quotes[current].animate(
              [{ opacity: 0, filter: 'blur(10px)' }, { opacity: 1, filter: 'blur(0px)' }],
              { duration: 700, easing: 'ease-in-out', fill: 'forwards' }
            );
            reveal.onfinish = start;
          }
          if (reduced.matches) { replaceQuote(); return; }
          reveal = quotes[current].animate(
            [{ opacity: 1, filter: 'blur(0px)' }, { opacity: 0, filter: 'blur(10px)' }],
            { duration: 600, easing: 'ease-in-out', fill: 'forwards' }
          );
          reveal.onfinish = replaceQuote;
        };
      }

      scene.dataset.activeQuote = '1';
      var observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        start();
      }, { threshold: 0 });
      observer.observe(scene.querySelector('.mnl-ins-manimind__bars'));
      document.addEventListener('visibilitychange', start);
      scene._mnlMindCleanup = function () {
        stop(); observer.disconnect(); document.removeEventListener('visibilitychange', start);
      };
    });
  }

  // Five-position drum: keep each signal's value, badge and ring together.
  function initSignals(root) {
    root.querySelectorAll('[data-mnl-signals]').forEach(function (scene) {
      if (scene._mnlSignalsCleanup) return;
      var rows = Array.from(scene.querySelectorAll('.mnl-ins-signals__row'));
      if (rows.length !== 5) return;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      var visible = false, disposed = false, timer = 0, animations = [], clone = null;
      var slots = [
        { x: 219, y: 340, scale: 0.835, blur: 7 },
        { x: 165, y: 413.885, scale: 0.925, blur: 4 },
        { x: 120, y: 495.56, scale: 1, blur: 0 },
        { x: 165, y: 584.56, scale: 0.925, blur: 4 },
        { x: 219, y: 666.235, scale: 0.835, blur: 7 }
      ];
      var desktopSlots = slots;
      var mobile = window.innerWidth <= 767;
      function updateSlots() {
        mobile = window.innerWidth <= 767;
        slots = mobile ? [
          {x:0,y:0,scale:.818,blur:7.5}, {x:0,y:52,scale:.925,blur:4},
          {x:0,y:114,scale:1,blur:0}, {x:0,y:276,scale:.925,blur:4},
          {x:0,y:328,scale:.818,blur:7.5}
        ] : desktopSlots;
      }
      updateSlots();
      function frame(slot, opacity) {
        return { transform: 'translate(' + slot.x + 'px,' + slot.y + 'px) scale(' + slot.scale + ')', filter: 'blur(' + slot.blur + 'px)', opacity: opacity == null ? 1 : opacity };
      }
      function settle() {
        rows.forEach(function (row, i) {
          Object.assign(row.style, frame(slots[i]));
          row.style.left = '0px'; row.style.top = '0px';
          row.style.height = mobile ? '138px' : '89px';
          row.style.borderTop = '0';
          row.style.borderBottomColor = mobile || i === 2 ? 'transparent' : 'var(--mnl-signal-rule)';
          row.dataset.position = String(i);
        });
        scene.dataset.activeSignal = rows[2].querySelector('.mnl-ins-signals__name').textContent;
      }
      function stopMotion() {
        animations.forEach(function (a) { a.cancel(); }); animations = [];
        if (clone) { clone.remove(); clone = null; }
        settle();
      }
      function schedule() {
        clearTimeout(timer);
        var running = visible && !document.hidden && !reduced.matches && !disposed;
        scene.classList.toggle('is-running', running);
        if (running) timer = setTimeout(advance, 2500);
        else stopMotion();
      }
      function advance() {
        var old = rows.slice();
        clone = old[0].cloneNode(true);
        clone.removeAttribute('data-shopify-editor-block');
        clone.setAttribute('aria-hidden', 'true');
        scene.querySelector('.mnl-ins-signals__rows').appendChild(clone);
        var below = mobile ? {x:0,y:380,scale:.75,blur:10} : { x: 260, y: 750, scale: 0.75, blur: 10 };
        var above = mobile ? {x:0,y:-52,scale:.75,blur:10} : { x: 260, y: 265, scale: 0.75, blur: 10 };
        var options = { duration: 700, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' };
        animations = old.map(function (row, i) {
          return row.animate([frame(slots[i]), frame(i ? slots[i - 1] : above, i ? 1 : 0)], options);
        });
        animations.push(clone.animate([frame(below, 0), frame(slots[4])], options));
        if (mobile) {
          // Hide the outgoing details before they reach the neighbouring label;
          // reveal the incoming details only as their row reaches the centre.
          [2, 3].forEach(function (index) {
            old[index].querySelectorAll('.mnl-ins-signals__mid, .mnl-ins-signals__ring').forEach(function (detail) {
              var keys = index === 2
                ? [{opacity:1,offset:0}, {opacity:0,offset:0.2}, {opacity:0,offset:1}]
                : [{opacity:0,offset:0}, {opacity:0,offset:0.55}, {opacity:1,offset:1}];
              animations.push(detail.animate(keys, options));
            });
          });
        }
        rows = old.slice(1).concat(old[0]);
        animations[0].onfinish = function () { stopMotion(); schedule(); };
      }
      settle();
      var observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.4;
        schedule();
      }, { threshold: [0, 0.4] });
      observer.observe(scene);
      function resizeSignals() { updateSlots(); stopMotion(); schedule(); }
      window.addEventListener('resize', resizeSignals);
      document.addEventListener('visibilitychange', schedule);
      reduced.addEventListener('change', schedule);
      scene._mnlSignalsCleanup = function () {
        window.removeEventListener('resize', resizeSignals);
        disposed = true; clearTimeout(timer); stopMotion(); observer.disconnect();
        document.removeEventListener('visibilitychange', schedule);
        reduced.removeEventListener('change', schedule);
      };
    });
  }

  function initDeck(root) {
    root.querySelectorAll('[data-mnl-deck]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');

      var stage = scene.querySelector('[data-mnl-deck-stage]') || scene;
      var items = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-deck-item]'));
      var rails = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-deck-rail]'));
      var images = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-deck-image]'));
      var num = scene.querySelector('[data-mnl-deck-num]');
      var prev = scene.querySelector('[data-mnl-deck-prev]');
      var next = scene.querySelector('[data-mnl-deck-next]');
      if (!items.length) return;

      var cur = 0;
      var interval = Number(scene.getAttribute(window.matchMedia('(max-width: 767px)').matches && scene.hasAttribute('data-mnl-deck-mobile-interval') ? 'data-mnl-deck-mobile-interval' : 'data-mnl-deck-interval')) || 0;
      var badge = scene.querySelector('[data-mnl-deck-badge]');
      var progress = scene.querySelector('[data-mnl-deck-progress]');
      var progressAnimation = null;
      var timer = 0;
      var swapTimer = 0;
      var inView = false;
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      function schedule() {
        clearTimeout(timer);
        if (progressAnimation) progressAnimation.cancel();
        if (interval && items.length > 1 && inView && !document.hidden && scene.isConnected) {
          if (progress) {
            progressAnimation = progress.animate(
              [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
              { duration: interval, easing: 'linear', fill: 'forwards' }
            );
            progressAnimation.onfinish = function () { apply(cur + 1); };
          } else timer = setTimeout(function () { apply(cur + 1); }, interval);
        }
      }

      function apply(i, initial) {

        clearTimeout(timer);
        if (progressAnimation) progressAnimation.cancel();
        clearTimeout(swapTimer);
        cur = (i + items.length) % items.length;
        function reveal() {
          items.forEach(function (el, k) {
            var on = k === cur;
            el.classList.toggle('is-on', on);
            el.setAttribute('aria-hidden', on ? 'false' : 'true');
          });
          images.forEach(function (image, k) {
            image.classList.toggle('is-on', k === cur);
            image.setAttribute('aria-hidden', k === cur ? 'false' : 'true');
          });
          if (badge) badge.textContent = String(cur + 1);
          if (num) num.textContent = String(cur + 1);
          rails.forEach(function (el, k) {
            el.style.setProperty('--mnl-past', k > cur ? '1' : '0');
            el.classList.toggle('is-under', k === cur);
          });
          stage.style.setProperty('--mnl-k', String(cur));
          schedule();
        }
        if (interval && !initial && !reduceMotion.matches) {
          items.forEach(function (el) {
            el.classList.remove('is-on');
            el.setAttribute('aria-hidden', 'true');
          });
          swapTimer = setTimeout(reveal, 180);
        } else reveal();
      }
      apply(0, true);

      if (items.length < 2) return;
      if (prev) prev.addEventListener('click', function () { apply(cur - 1); });
      if (next) next.addEventListener('click', function () { apply(cur + 1); });
      if (interval) {
        var observer = new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.5;
          schedule();
        }, { threshold: [0, 0.5] });
        observer.observe(stage);
        function visibilityChange() { schedule(); }
        document.addEventListener('visibilitychange', visibilityChange);
        function unload(event) {
          if (!event.target.contains(scene)) return;
          clearTimeout(timer);
          clearTimeout(swapTimer);
          if (progressAnimation) progressAnimation.cancel();
          observer.disconnect();
          document.removeEventListener('visibilitychange', visibilityChange);
          document.removeEventListener('shopify:section:unload', unload);
        }
        document.addEventListener('shopify:section:unload', unload);
      }
    });
  }

  /* --- Initialization --- */
  /* Three Membership layers: highlight the name on the left and change the card on the right. Buttons only; no wheel or scroll interception. The script sets the target on click, and CSS transitions animate it. */
  function initLayers(root) {
    root.querySelectorAll('[data-mnl-layers]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');

      var names = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-layers-name]'));
      var panels = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-layers-panel]'));
      var count = Math.min(names.length, panels.length);
      if (count < 2) return;

      // Decode card artwork before its first fade, not during the click.
      var art = Array.from(scene.querySelectorAll('.mnl-mem-layers__shot'));
      function warmCards() {
        art.forEach(function (image) {
          image.loading = 'eager';
          if (image.decode) image.decode().catch(function () {});
        });
      }
      if ('IntersectionObserver' in window) {
        var preload = new IntersectionObserver(function (entries) {
          if (!entries[entries.length - 1].isIntersecting) return;
          warmCards();
          preload.disconnect();
        }, { rootMargin: '600px' });
        preload.observe(scene);
      } else warmCards();

      var current = 0;
      function apply(i) {
        i = (i + count) % count;
        current = i;
        scene.setAttribute('data-layer', String(i));
        for (var k = 0; k < count; k++) {
          names[k].classList.toggle('is-on', k === i);
          names[k].setAttribute('aria-pressed', k === i ? 'true' : 'false');
          panels[k].classList.toggle('is-on', k === i);
          panels[k].setAttribute('aria-hidden', k === i ? 'false' : 'true');
        }
      }
      apply(0);
      var prev = scene.querySelector('[data-mnl-layers-prev]');
      var next = scene.querySelector('[data-mnl-layers-next]');
      if (prev) prev.addEventListener('click', function () { apply(current - 1); });
      if (next) next.addEventListener('click', function () { apply(current + 1); });
      var card = scene.querySelector('.mnl-mem-layers__card');
      var gesture = null;
      card.addEventListener('pointerdown', function (event) {
        if (!window.matchMedia('(max-width: 767px)').matches || !event.isPrimary) return;
        gesture = { x: event.clientX, y: event.clientY };
        card.setPointerCapture(event.pointerId);
      });
      card.addEventListener('pointerup', function (event) {
        if (!gesture) return;
        var dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        gesture = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.3) apply(current + (dx < 0 ? 1 : -1));
      });
      card.addEventListener('pointercancel', function () { gesture = null; });
      card.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        apply(current + (event.key === 'ArrowRight' ? 1 : -1));
      });


      names.forEach(function (button, i) {
        button.addEventListener('click', function () {
          apply(i);
        });
      });
    });
  }

  /* A system that grows with you: one selected phrase, all lines white. Reveal from blur; cancel the previous animation on the next click so rapid clicks cannot leave several phrases visible together. */
  function initGrow(root) {
    root.querySelectorAll('[data-mnl-grow]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');

      var stage = scene.querySelector('[data-mnl-grow-stage]');
      var content = scene.querySelector('[data-mnl-grow-content]');
      var statement = scene.querySelector('[data-mnl-grow-statement]');
      if (animated && stage && content && statement) {
        var statementTitle = statement.querySelector('.mnl-mem-grow__statement-text');
        var fillWords = [];
        if (statementTitle) {
          statementTitle.querySelectorAll(':scope > span').forEach(function (part) {
            Array.prototype.slice.call(part.childNodes).forEach(function (node) {
              if (node.nodeType !== 3) return;
              var fragment = document.createDocumentFragment();
              (node.textContent.match(/\s+|\S+/g) || []).forEach(function (text) {
                if (/^\s+$/.test(text)) { fragment.appendChild(document.createTextNode(text)); return; }
                var word = document.createElement('span');
                word.className = 'mnl-mem-grow__fill-word';
                word.textContent = text;
                var ink = document.createElement('span');
                ink.className = 'mnl-mem-grow__fill-ink';
                ink.textContent = text;
                ink.setAttribute('aria-hidden', 'true');
                word.appendChild(ink);
                fragment.appendChild(word);
                fillWords.push(ink);
              });
              node.replaceWith(fragment);
            });
          });
          statementTitle.classList.add('has-scroll-fill');
        }
        scene.classList.add('is-scroll-statement');
        var frame = 0;
        var lastProgress = -1;
        var zoom = 1;
        var stickyTop = 0;
        function measureStatement() {
          zoom = scene.getBoundingClientRect().width / scene.offsetWidth || 1;
          stickyTop = Math.min(0, window.innerHeight / zoom - content.offsetHeight);
          scene.style.setProperty('--mnl-grow-sticky-top', stickyTop + 'px');
          lastProgress = -1;
          queueStatement();
        }
        function renderStatement() {
          frame = 0;
          var travel = (stickyTop * zoom - scene.getBoundingClientRect().top) / zoom;
          var fill = Math.max(0, Math.min(1, (travel - 775.8) / 862));
          fillWords.forEach(function (ink, index) {
            // Overlap the feather across adjacent words, including the first/last endpoints.
            var amount = Math.round(Math.max(-.6, Math.min(1.6, fill * (fillWords.length + 1.2) - .6 - index)) * 1000) / 1000;
            if (ink._mnlFill === amount) return;
            ink._mnlFill = amount;
            ink.style.setProperty('--fill-edge', (amount * 100).toFixed(2) + '%');
          });
          var progress = Math.max(0, Math.min(1, (travel - 86.2) / 689.6));
          progress = Math.round(progress * 1000) / 1000;
          if (progress === lastProgress) return;
          lastProgress = progress;
          var eased = progress * progress * (3 - 2 * progress);
          content.style.filter = progress ? 'blur(' + (37 * eased).toFixed(2) + 'px)' : '';
          statement.style.opacity = eased;
          content.inert = progress > 0.5;
          statement.setAttribute('aria-hidden', progress < 0.5 ? 'true' : 'false');
        }
        function queueStatement() {
          if (!frame) frame = requestAnimationFrame(renderStatement);
        }
        measureStatement();
        window.addEventListener('scroll', queueStatement, { passive: true });
        window.addEventListener('resize', measureStatement);
        (scene.closest('.shopify-section') || scene).addEventListener('shopify:section:unload', function () {
          cancelAnimationFrame(frame);
          window.removeEventListener('scroll', queueStatement);
          window.removeEventListener('resize', measureStatement);
        });
      }

      var chips = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-grow-chip]'));
      var lines = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-grow-line]'));
      var reel = scene.querySelector('[data-mnl-grow-reel]');
      var n = Math.min(chips.length, lines.length);
      if (!n || !reel) return;
      var home = 1 < n ? 1 : 0;
      var current = home;
      var motion = null;
      var shown = false;
      var swap = parseInt(scene.getAttribute('data-mnl-grow-swap') || '500', 10);

      function reveal() {
        if (motion) motion.cancel();
        motion = null;
        if (!reduced && lines[current].animate) {
          motion = lines[current].animate([
            { opacity: 0, filter: 'blur(3px)' },
            { opacity: 1, filter: 'blur(0px)' }
          ], { duration: swap, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
        }
      }

      function apply(i) {
        if (motion) motion.cancel();
        motion = null;
        current = i;
        for (var k = 0; k < n; k++) {
          chips[k].classList.toggle('is-on', k === i);
          chips[k].setAttribute('aria-pressed', k === i ? 'true' : 'false');
          lines[k].classList.toggle('is-on', k === i);
          lines[k].hidden = k !== i;
        }
      }
      apply(home);

      chips.forEach(function (c, i) {
        c.addEventListener('click', function () {
          shown = true;
          apply(i);
          reveal();
        });
      });

      if (!reduced) {
        var observer = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          if (!shown) { shown = true; reveal(); }
          observer.disconnect();
        }, { threshold: 0.1 });
        observer.observe(reel);
        scene.addEventListener('shopify:section:unload', function () {
          observer.disconnect();
          if (motion) motion.cancel();
        });
      }
    });
  }

  /* Membership Yearly / Monthly switch: changes the price and its caption, showing terms for the selected plan. Values live in data attributes; the script is content-agnostic. */
  function initPlan(root) {
    root.querySelectorAll('[data-mnl-plan]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');
      var tabs = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-plan-tab]'));
      var swaps = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-plan-price],[data-mnl-plan-note]'));
      var price = scene.querySelector('[data-mnl-plan-price]');
      var priceMotion = null;
      var priceFrames = [];
      var selectedPrice = price ? price.textContent.trim() : '';
      function rollPrice(value) {
        if (!price || value === selectedPrice) return;
        var from = selectedPrice;
        if (priceMotion) {
          var progress = priceMotion.effect.getComputedTiming().progress || 0;
          from = priceFrames[Math.round(progress * (priceFrames.length - 1))] || from;
          priceMotion.cancel();
          priceMotion = null;
        }
        selectedPrice = value;
        price.setAttribute('data-current', value);
        var start = from.match(/^(\D*)(\d+)(\D*)$/);
        var end = value.match(/^(\D*)(\d+)(\D*)$/);
        var parent = price.parentElement;
        if (reduced || !price.animate || !start || !end || start[1] !== end[1] || start[3] !== end[3] || from === value) {
          price.textContent = value;
          parent.classList.remove('is-rolling');
          return;
        }
        var before = Number(start[2]);
        var after = Number(end[2]);
        if (before === after) {
          price.textContent = value;
          parent.classList.remove('is-rolling');
          return;
        }
        var steps = Math.min(8, Math.abs(after - before));
        priceFrames = [];
        for (var step = 0; step <= steps; step++) {
          priceFrames.push(start[1] + Math.round(before + (after - before) * step / steps) + start[3]);
        }
        var drum = document.createElement('span');
        drum.className = 'mnl-mem-plan__price-drum';
        drum.setAttribute('aria-hidden', 'true');
        var reel = document.createElement('span');
        reel.className = 'mnl-mem-plan__price-reel';
        var rows = after > before ? priceFrames : priceFrames.slice().reverse();
        rows.forEach(function (text) {
          var row = document.createElement('span');
          row.className = 'mnl-mem-plan__price-value';
          row.textContent = text;
          reel.appendChild(row);
        });
        drum.appendChild(reel);
        var label = document.createElement('span');
        label.className = 'mnl-mem-plan__price-label';
        label.textContent = value;
        price.replaceChildren(label, drum);
        parent.classList.add('is-rolling');
        // Leave space between values so adjacent digits cannot peek
        // through at either end of the scroll. The window retains the price height of 84.
        var distance = steps * 112;
        var motion = reel.animate([
          { transform: 'translateY(' + (after > before ? -14 : -distance - 14) + 'px)' },
          { transform: 'translateY(' + (after > before ? -distance - 14 : -14) + 'px)' }
        ], { duration: 650, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' });
        priceMotion = motion;
        motion.onfinish = function () {
          if (priceMotion !== motion) return;
          price.textContent = value;
          parent.classList.remove('is-rolling');
          priceMotion = null;
          motion.cancel();
        };
      }
      var list = scene.querySelector('[data-mnl-plan-scroll]');
      var listBox = scene.querySelector('[data-mnl-plan-list-box]');
      var track = scene.querySelector('[data-mnl-plan-track]');
      var thumb = scene.querySelector('[data-mnl-plan-thumb]');
      if (list && listBox && track && thumb) {
        var scrollFrame = 0;
        var drag = null;
        function updateScroll() {
          scrollFrame = 0;
          var max = list.scrollHeight - list.clientHeight;
          track.hidden = max <= 0;
          var progress = max > 0 ? Math.max(0, Math.min(1, list.scrollTop / max)) : 1;
          thumb.style.setProperty('--mnl-plan-scroll-y', (progress * (track.clientHeight - thumb.offsetHeight)) + 'px');
          listBox.classList.toggle('is-at-end', max <= 0 || list.scrollTop >= max - 1);
        }
        function scheduleScroll() {
          if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScroll);
        }
        list.addEventListener('scroll', scheduleScroll, { passive: true });
        var listRow = listBox.parentElement;
        function onListWheel(event) {
          if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
          // Page smoothing must not capture wheel input from the nested list.
          smoothStop();
          event.stopPropagation();
          if (list.contains(event.target)) return; // Native wheel scrolling inside the viewport.
          // Wheel input over the track and gap also controls the list.
          event.preventDefault();
          var delta = event.deltaY;
          if (event.deltaMode === 1) delta *= 16;
          else if (event.deltaMode === 2) delta *= list.clientHeight;
          else delta /= list.getBoundingClientRect().width / list.clientWidth;
          list.scrollTop += delta;
        }
        listRow.addEventListener('wheel', onListWheel, { passive: false });
        track.addEventListener('pointerdown', function (event) {
          if (event.button !== 0) return;
          var rect = track.getBoundingClientRect();
          var thumbRect = thumb.getBoundingClientRect();
          var travel = rect.height - thumbRect.height;
          if (travel <= 0) return;
          var max = list.scrollHeight - list.clientHeight;
          if (event.target !== thumb) {
            list.scrollTop = Math.max(0, Math.min(1, (event.clientY - rect.top - thumbRect.height / 2) / travel)) * max;
          }
          drag = { y: event.clientY, scroll: list.scrollTop, scale: max / travel };
          track.setPointerCapture(event.pointerId);
          event.preventDefault();
        });
        track.addEventListener('pointermove', function (event) {
          if (drag) list.scrollTop = drag.scroll + (event.clientY - drag.y) * drag.scale;
        });
        track.addEventListener('pointerup', function () { drag = null; });
        track.addEventListener('pointercancel', function () { drag = null; });
        track.addEventListener('lostpointercapture', function () { drag = null; });
        var listResize = new ResizeObserver(scheduleScroll);
        listResize.observe(list);
        listResize.observe(list.firstElementChild);
        updateScroll();
        var sectionRoot = scene.closest('.shopify-section') || scene;
        sectionRoot.addEventListener('shopify:section:unload', function () {
          cancelAnimationFrame(scrollFrame);
          listResize.disconnect();
          listRow.removeEventListener('wheel', onListWheel);
        });
      }
      if (tabs.length < 2) return;

      function apply(i) {
        var key = tabs[i].getAttribute('data-mnl-plan-tab') || (i === 0 ? 'year' : 'month');
        scene.querySelectorAll('[data-mnl-plan-renew]').forEach(function (note) {
          note.hidden = note.getAttribute('data-mnl-plan-renew') !== key;
        });
        tabs.forEach(function (t, k) {
          t.classList.toggle('is-on', k === i);
          t.setAttribute('aria-pressed', k === i ? 'true' : 'false');
        });
        swaps.forEach(function (el) {
          var v = el.getAttribute('data-' + key);
          if (v === null || v === '') return;
          if (el === price) rollPrice(v);
          else el.textContent = v;
        });
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { apply(i); });
      });
      (scene.closest('.shopify-section') || scene).addEventListener('shopify:section:unload', function () {
        if (priceMotion) priceMotion.cancel();
      });
    });
  }

  /* FAQ: fade out the old answer, then move items with FLIP and reveal the new answer. Do not animate height or overlap text. */
  function initFaq(root) {
    root.querySelectorAll('[data-mnl-faq]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');
      var qs = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-faq-q]'));
      var items = qs.map(function (q) { return q.parentElement; });
      var texts = items.map(function (item) { return item.querySelector('[data-mnl-faq-a] p'); });
      var shown = items.findIndex(function (item) { return item.classList.contains('is-open'); });
      var desired = shown;
      var busy = false;
      var disposed = false;
      var motions = new Set();
      var ease = 'cubic-bezier(.22,1,.36,1)';
      function animate(el, frames, duration) {
        if (reduced || !el.animate) return Promise.resolve();
        var motion = el.animate(frames, { duration: duration, easing: ease, fill: 'both' });
        motions.add(motion);
        return motion.finished.catch(function () {}).then(function () {
          motions.delete(motion);
          motion.cancel();
        });
      }
      function fade(index, visible) {
        var text = texts[index];
        var before = Number(getComputedStyle(text).opacity);
        text.style.opacity = visible ? '1' : '0';
        if (before === (visible ? 1 : 0)) return Promise.resolve();
        return animate(text, [
          { opacity: before, transform: 'translateY(' + (visible ? 6 : 0) + 'px)' },
          { opacity: visible ? 1 : 0, transform: 'translateY(' + (visible ? 0 : -6) + 'px)' }
        ], visible ? 260 : 180);
      }
      async function update() {
        if (busy || disposed) return;
        busy = true;
        try {
          while (!disposed && desired !== shown) {
            if (shown >= 0) await fade(shown, false);
            if (disposed) break;
            var next = desired; // The last click during closing takes priority.
            var before = items.map(function (item) { return item.getBoundingClientRect(); });
            qs.forEach(function (q, i) {
              items[i].classList.toggle('is-open', i === next);
              q.setAttribute('aria-expanded', i === next ? 'true' : 'false');
            });
            if (next >= 0) texts[next].style.opacity = '0';
            shown = next;
            var after = items.map(function (item) { return item.getBoundingClientRect(); });
            await Promise.all(items.map(function (item, i) {
              var scale = after[i].width / item.offsetWidth;
              var dy = (before[i].top - after[i].top) / scale;
              if (Math.abs(dy) < 0.5) return Promise.resolve();
              return animate(item, [
                { transform: 'translateY(' + dy + 'px)' },
                { transform: 'translateY(0px)' }
              ], 360);
            }));
            if (disposed) break;
            if (shown >= 0 && desired === shown) await fade(shown, true);
          }
        } finally {
          busy = false;
        }
      }
      qs.forEach(function (q, i) {
        q.addEventListener('click', function () {
          desired = desired === i ? -1 : i;
          smoothStop();
          update();
        });
      });
      (scene.closest('.shopify-section') || scene).addEventListener('shopify:section:unload', function () {
        disposed = true;
        motions.forEach(function (motion) { motion.cancel(); });
        motions.clear();
      });
    });
  }

  /* Quiz: five photo screens, then a continuous film through processing,
     the quote and pricing. The quote holds frame 290 until scrolling. */
  function initQuiz(root) {
    root.querySelectorAll('[data-mnl-quiz]:not([data-mnl-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-on', '1');
      var steps = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-quiz-step]'));
      var bgs = Array.prototype.slice.call(scene.querySelectorAll('[data-mnl-quiz-bg]'));
      if (!steps.length) return;

      var mobileQuiz = window.matchMedia('(max-width: 749px)');
      // Desktop displacement lenses need decoded images; mobile uses live blur.
      var glassURLs = Array.from(new Set(Array.prototype.map.call(scene.querySelectorAll('feImage'), function (map) {
        return map.getAttribute('href');
      }).filter(Boolean)));
      scene.querySelectorAll('[style*="background-image"]').forEach(function (layer) {
        var matches = layer.style.backgroundImage.matchAll(/url\(["']?([^)"']+)["']?\)/g);
        for (var match of matches) if (glassURLs.indexOf(match[1]) < 0) glassURLs.push(match[1]);
      });
      var glassReady = Promise.all((mobileQuiz.matches ? [] : glassURLs).map(function (url) {
        var image = new Image();
        image.src = url;
        return image.decode ? image.decode().catch(function () {}) : Promise.resolve();
      }));

      // The prompt label uses max-content width; its lens must follow that
      // width instead of the original, shorter Figma label's dimensions.
      var promptLens = scene.querySelector('.mnl-quiz__pill-glass');
      var promptMap = scene.querySelector('filter[id^="quiz-pill-glass-"] feImage');
      function measurePromptLens() {
        if (!promptLens || !promptMap) return;
        promptMap.setAttribute('width', promptLens.offsetWidth);
        promptMap.setAttribute('height', promptLens.offsetHeight);
      }
      var promptObserver = promptLens ? new ResizeObserver(measurePromptLens) : null;
      if (promptObserver) promptObserver.observe(promptLens);
      measurePromptLens();
      if (document.fonts) document.fonts.ready.then(measurePromptLens);

      var writeIcon = scene.getAttribute('data-mnl-quiz-write-icon') || '';
      var answers = [];
      var at = -1;
      var timer = 0;
      var exitTimer = 0;
      var resultTimer = 0;
      var choiceTimer = 0;
      var stackMotions = [];
      var stackPerfTick = 0, stackPerfTimes = [];
      function finishStackPerf() {
        cancelAnimationFrame(stackPerfTick);
        if (stackPerfTimes.length) {
          stackPerfTimes.sort(function (a, b) { return a - b; });
          console.debug('Quiz pill frames', JSON.stringify({ count: stackPerfTimes.length,
            p95: stackPerfTimes[Math.floor(stackPerfTimes.length * .95)],
            over32: stackPerfTimes.filter(function (t) { return t > 32; }).length }));
          stackPerfTimes = [];
        }
      }
      var exampleTimer = 0, exampleMotions = [], exampleTimeline = null;
      var exampleQuotes = [
        '“I keep overthinking everything.”',
        '“I feel like something is missing in my life.”',
        '“Am I with the right person?”',
        '“I know what I want, but I keep holding myself back.”',
        '“I feel like I\'m not reaching my full potential.”',
        '“Am I living the life I truly want?”'
      ];
      function stopExamples() {
        clearTimeout(exampleTimer);
        if (exampleTimeline) { exampleTimeline.kill(); exampleTimeline = null; }
        exampleMotions.forEach(function (motion) { motion.cancel(); });
        exampleMotions = [];
      }
      function startExamples(step) {
        stopExamples();
        var example = step.querySelector('.mnl-quiz__example');
        if (!example) return;
        var index = 0;
        function cycle() {
          if (!scene.isConnected || steps[at] !== step) { stopExamples(); return; }
          stopExamples();
          example.replaceChildren();
          exampleQuotes[index].split(' ').forEach(function (text, n) {
            if (n) example.appendChild(document.createTextNode(' '));
            var word = document.createElement('span');
            word.className = 'mnl-quiz__example-word';
            word.textContent = text;
            example.appendChild(word);
          });
          var words = example.querySelectorAll('span');
          // Four seconds of fully readable text, independent of phrase length.
          var holdDuration = 4000;
          var entranceDuration = reduced ? 0 : 1000 + (words.length - 1) * 55;
          var exitDuration = reduced ? 0 : 850 + (words.length - 1) * 45;
          function nextExample() {
            index = (index + 1) % exampleQuotes.length;
            cycle();
          }
          if (gsap && !reduced) {
            exampleTimeline = gsap.timeline({ onComplete: nextExample });
            exampleTimeline.fromTo(words,
              { opacity: 0, y: 10, filter: 'blur(5px)' },
              { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1, stagger: .055, ease: 'power2.out' }
            ).to(words,
              { opacity: 0, y: -10, filter: 'blur(5px)', duration: .85, stagger: .045, ease: 'power2.inOut' },
              '+=4'
            );
            return;
          }
          if (!reduced) words.forEach(function (word, n) {
            exampleMotions.push(word.animate([
              { opacity: 0, transform: 'translateY(10px)', filter: 'blur(5px)' },
              { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' }
            ], { duration: 1000, delay: n * 55, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' }));
          });
          exampleTimer = setTimeout(function () {
            if (!scene.isConnected || steps[at] !== step) { stopExamples(); return; }
            if (!reduced) words.forEach(function (word, n) {
              exampleMotions.push(word.animate([
                { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
                { opacity: 0, transform: 'translateY(-10px)', filter: 'blur(5px)' }
              ], { duration: 850, delay: n * 45, easing: 'ease-in-out', fill: 'forwards' }));
            });
            exampleTimer = setTimeout(nextExample, exitDuration);
          }, entranceDuration + holdDuration);
        }
        cycle();
      }
      var quoteMotions = [], quoteReadyAt = 0, quotePerfTick = 0;
      // Preserve authored line breaks and spaces while animating whole words.
      scene.querySelectorAll('.mnl-quiz__quote-a, .mnl-quiz__quote-b').forEach(function (part) {
        Array.from(part.childNodes).forEach(function (node) {
          if (node.nodeType !== Node.TEXT_NODE) return;
          var fragment = document.createDocumentFragment();
          node.textContent.split(/(\s+)/).forEach(function (text) {
            if (!text) return;
            if (/^\s+$/.test(text)) fragment.appendChild(document.createTextNode(text));
            else {
              var word = document.createElement('span');
              word.className = 'mnl-quiz__quote-word';
              word.textContent = text;
              fragment.appendChild(word);
            }
          });
          node.replaceWith(fragment);
        });
      });
      function animateQuote(step, leaving) {
        quoteMotions.forEach(function (motion) { motion.cancel(); });
        quoteMotions = [];
        var offset = leaving ? 0 : 160;
        step.querySelectorAll('.mnl-quiz__quote-a, .mnl-quiz__quote-b').forEach(function (part) {
          if (leaving) {
            // Both paragraphs leave as whole blocks, on the same frame.
            if (!reduced) quoteMotions.push(part.animate([
              { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
              { opacity: 0, transform: 'translateY(-22px)', filter: 'blur(6px)' }
            ], { duration: 440, delay: 0, easing: 'cubic-bezier(.4,0,.6,1)', fill: 'both' }));
            return;
          }
          var words = part.querySelectorAll('.mnl-quiz__quote-word');
          words.forEach(function (word, index) {
            if (reduced) return;
            quoteMotions.push(word.animate([
              { opacity: 0, transform: 'translateY(24px)', filter: 'blur(6px)' },
              { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' }
            ], { duration: 650, delay: offset + index * 55,
              easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' }));
          });
          offset += Math.max(0, words.length - 1) * 55 + 650 + 140;
        });
        var duration = reduced ? 0 : leaving ? 440 : offset - 140;
        if (!leaving) quoteReadyAt = performance.now() + duration;
        cancelAnimationFrame(quotePerfTick);
        if (location.hostname === '127.0.0.1' && new URLSearchParams(location.search).has('quiz_perf')) {
          var start = performance.now(), last = start, frames = [];
          quotePerfTick = requestAnimationFrame(function sample(now) {
            frames.push(now - last); last = now;
            if (now - start < duration) quotePerfTick = requestAnimationFrame(sample);
            else {
              frames.sort(function (a, b) { return a - b; });
              console.debug('Quiz quote frames', JSON.stringify({ leaving: leaving, count: frames.length,
                p95: frames[Math.floor(frames.length * .95)], over32: frames.filter(function (t) { return t > 32; }).length }));
              if (!leaving) { quoteMotions.forEach(function (motion) { motion.cancel(); }); quoteMotions = []; }
            }
          });
        } else if (!leaving && quoteMotions.length) {
          Promise.all(quoteMotions.map(function (motion) { return motion.finished; })).then(function () {
            if (!resultLeaving) { quoteMotions.forEach(function (motion) { motion.cancel(); }); quoteMotions = []; }
          }).catch(function () {});
        }
        return duration;
      }
      var resultProgress = 0;
      var resultLeaving = false;
      var touchY = null;
      var events = new AbortController();

      var video = scene.querySelector('[data-mnl-quiz-video]');
      var surface = scene.querySelector('[data-mnl-quiz-surface]');
      var surfaceContext = surface && surface.getContext('2d');
      var ranges = { 5: [0,130], 6: [130,245], 7: [245,290], 8: [290,478] };
      var fps = 30, videoTick = 0, segmentEnd = 0, segmentHeld = true;
      var videoStep = -1, lenses = [], lastPaint = -1, sourceRect, videoObjectURL;
      function measureVideo() {
        if (!surface || !video || at < 5) return;
        var rect = surface.getBoundingClientRect();
        var width = video.videoWidth || 1920, height = video.videoHeight || 1080;
        var scale = Math.max(rect.width / width, rect.height / height);
        var cropX = window.matchMedia('(max-width:749px)').matches && at >= 5 && at <= 7 ? .22 : .5;
        sourceRect = { x: (width - rect.width / scale) * cropX, y: (height - rect.height / scale) / 2,
          w: rect.width / scale, h: rect.height / scale, rect: rect, scale: scale };
        // Assigning canvas dimensions clears its frame, even if unchanged.
        var surfaceWidth = Math.round(Math.min(rect.width * devicePixelRatio, 1440));
        var surfaceHeight = Math.round(surfaceWidth * rect.height / rect.width);
        if (surface.width !== surfaceWidth) surface.width = surfaceWidth;
        if (surface.height !== surfaceHeight) surface.height = surfaceHeight;
        lenses = [];
        var step = steps[at];
        if (!step) return;
        step.querySelectorAll('.mnl-quiz__glass > span, .mnl-quiz__card-glass > span, .mnl-quiz__field-glass > span, .mnl-quiz__pill-glass > span, .mnl-quiz__chip-glass > span').forEach(function (span) {
          var canvas = span.querySelector('canvas');
          if (!canvas) { canvas = document.createElement('canvas'); span.appendChild(canvas); }
          var r = span.getBoundingClientRect();
          var resolution = span.closest('.is-flowing') ? .5 : 1;
          canvas.width = Math.max(1, Math.round(r.width * resolution)); canvas.height = Math.max(1, Math.round(r.height * resolution));
          lenses.push({ canvas: canvas, ctx: canvas.getContext('2d'), x: (r.left - rect.left) / scale + sourceRect.x,
            y: (r.top - rect.top) / scale + sourceRect.y, w: r.width / scale, h: r.height / scale });
        });
        step.querySelectorAll('.mnl-quiz__chip').forEach(function (chip, i) {
          var filter = scene.querySelector('[id="quiz-chip-glass-' + scene.id.replace('mnl-quiz-', '') + '-' + (i + 1) + '"]');
          if (!filter) return;
          var map = filter.querySelector('feImage');
          map.setAttribute('width', chip.offsetWidth);
          map.setAttribute('height', chip.offsetHeight);
          filter.querySelector('feGaussianBlur').setAttribute('stdDeviation', 6 * [1.218, 1.088, .968, .842][i]);
        });
        lastPaint = -1;
        paintVideo();
      }
      function paintVideo() {
        if (!video || at < 5 || video.readyState < 2 || video.seeking || !sourceRect || lastPaint === Math.floor(video.currentTime * fps)) return;
        lastPaint = Math.floor(video.currentTime * fps);
        var r = sourceRect;
        surfaceContext.drawImage(video, r.x, r.y, r.w, r.h, 0, 0, surface.width, surface.height);
        scene.classList.add('has-film-frame');
        lenses.forEach(function (l) {
          // Overscan covers the short drift without layout reads on each frame.
          l.ctx.drawImage(video, l.x, l.y, l.w, l.h, 0, 0, l.canvas.width, l.canvas.height);
        });
      }
      function stopVideoTick() {
        cancelAnimationFrame(videoTick); videoTick = 0;
      }
      function revealFinal() {
        if (at !== 8 || !segmentHeld || video.seeking || video.currentTime < segmentEnd - 1 / fps) return;
        var panel = scene.querySelector('[data-mnl-quiz-final]');
        if (scene.classList.contains('is-film-ended')) return;
        scene.classList.add('is-film-ended');
        if (location.hostname === '127.0.0.1' && new URLSearchParams(location.search).has('quiz_perf')) {
          var start = performance.now(), last = start, times = [];
          requestAnimationFrame(function sample(now) {
            times.push(now - last); last = now;
            if (now - start < 1000) requestAnimationFrame(sample);
            else {
              times.sort(function (a, b) { return a - b; });
              console.debug('Quiz final reveal frames', JSON.stringify({count:times.length,
                p95:times[Math.floor(times.length * .95)], over32:times.filter(function (t) { return t > 32; }).length}));
            }
          });
        }
        if (panel) { panel.inert = false; panel.setAttribute('aria-hidden', 'false'); }
      }
      function watchVideo() {
        videoTick = 0;
        if (video.currentTime >= segmentEnd && !segmentHeld) {
          if ((at === 5 || at === 6) && !changing) {
            // Processing screens share one uninterrupted film. Only the quote
            // and the final frame should stop playback.
            videoAt(at + 1);
            show(at + 1);
          } else if (at === 5 || at === 6) {
            paintVideo();
          } else {
            segmentHeld = true;
            video.pause();
            // Seek to the exact boundary instead of retaining a late RAF frame.
            video.currentTime = segmentEnd;
            if (at === 7 && resultProgress >= 70) scrollResult(0);
          }
        } else paintVideo();
        if (!video.paused && !document.hidden) videoTick = requestAnimationFrame(watchVideo);
      }
      function resumeVideo() {
        if (!video || at < 5 || segmentHeld || video.seeking || document.hidden) return;
        video.play().then(function () {
          stopVideoTick(); videoTick = requestAnimationFrame(watchVideo);
        }).catch(function () {});
      }
      function videoAt(i) {
        scene.classList.toggle('has-film', i >= 5);
        if (i !== 8) {
          scene.classList.remove('is-film-ended');
          var finalPanel = scene.querySelector('[data-mnl-quiz-final]');
          if (finalPanel) { finalPanel.inert = true; finalPanel.setAttribute('aria-hidden', 'true'); }
        }
        if (!video) return;
        if (i < 5) {
          video.pause(); stopVideoTick(); segmentHeld = true; videoStep = -1;
          return;
        }
        if (video.readyState < 1) { segmentHeld = false; return; }
        if (videoStep === i) return;
        var previous = videoStep;
        videoStep = i;
        segmentEnd = Math.min(ranges[i][1] / fps, video.duration - 1 / fps);
        segmentHeld = reduced;
        if (reduced || previous < 5 || i < previous) {
          video.pause();
          video.currentTime = reduced ? segmentEnd : ranges[i][0] / fps;
        }
        resumeVideo();
      }
      if (video && surfaceContext) {
        video.muted = true;
        video.addEventListener('loadedmetadata', function () { videoAt(at); measureVideo(); }, { signal: events.signal });
        video.addEventListener('loadeddata', function () { measureVideo(); resumeVideo(); }, { signal: events.signal });
        video.addEventListener('seeked', function () { paintVideo(); revealFinal(); resumeVideo(); }, { signal: events.signal });
        video.addEventListener('timeupdate', paintVideo, { signal: events.signal });
        video.addEventListener('playing', function () { stopVideoTick(); videoTick = requestAnimationFrame(watchVideo); }, { signal: events.signal });
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) { video.pause(); stopVideoTick(); } else resumeVideo();
        }, { signal: events.signal });
        window.addEventListener('resize', measureVideo, { signal: events.signal });
        scene.addEventListener('pointerdown', resumeVideo, { signal: events.signal });
        // Load this small film once. Blob playback supports exact Back seeks even
        // when the theme preview proxy does not support HTTP byte ranges.
        fetch(video.dataset.src, { signal: events.signal }).then(function (response) {
          if (!response.ok) throw new Error('Quiz video unavailable');
          return response.blob();
        }).then(function (blob) {
          if (events.signal.aborted) return;
          videoObjectURL = URL.createObjectURL(blob);
          video.src = videoObjectURL;
        }).catch(function () {
          if (!events.signal.aborted) video.src = video.dataset.src;
        });

      }

      // Hold the fully revealed quote for ten seconds; scroll can leave earlier.
      function scheduleResultAdvance() {
        clearTimeout(resultTimer);
        resultTimer = setTimeout(function () { scrollResult(70); },
          Math.max(0, quoteReadyAt - performance.now()) + 10000);
      }
      function scrollResult(delta) {
        var step = steps[at];
        if (!step || step.getAttribute('data-mnl-quiz-type') !== 'result') return;
        if (resultLeaving) return;
        resultProgress = Math.max(0, resultProgress + delta);
        if (resultProgress < 70) return;
        if (performance.now() < quoteReadyAt || changing) {
          clearTimeout(exitTimer);
          exitTimer = setTimeout(function () { scrollResult(0); }, Math.max(60, quoteReadyAt - performance.now()));
          return;
        }
        resultLeaving = true;
        clearTimeout(resultTimer);
        var quoteExitDuration = animateQuote(step, true);
        videoAt(8);
        step.style.setProperty('--mnl-result-opacity', 0);
        step.style.setProperty('--mnl-result-y', '-80px');
        exitTimer = setTimeout(function () { show(at + 1); }, quoteExitDuration);
      }
      scene.addEventListener('wheel', function (e) {
        if (steps[at].getAttribute('data-mnl-quiz-type') !== 'result' || e.ctrlKey) return;
        e.preventDefault();
        scrollResult(e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1));
      }, { passive: false, signal: events.signal });
      scene.addEventListener('touchstart', function (e) {
        touchY = e.touches.length === 1 ? e.touches[0].clientY : null;
      }, { passive: true, signal: events.signal });
      scene.addEventListener('touchmove', function (e) {
        if (touchY === null || e.touches.length !== 1 || steps[at].getAttribute('data-mnl-quiz-type') !== 'result') return;
        e.preventDefault();
        var nextY = e.touches[0].clientY;
        scrollResult((touchY - nextY) * 2);
        touchY = nextY;
      }, { passive: false, signal: events.signal });
      document.addEventListener('keydown', function (e) {
        if (steps[at].getAttribute('data-mnl-quiz-type') !== 'result') return;
        var delta = { ArrowDown: 100, ArrowUp: -100, PageDown: 500, PageUp: -500, End: 500, Home: -500, ' ': e.shiftKey ? -500 : 500 }[e.key];
        if (delta === undefined) return;
        e.preventDefault();
        scrollResult(delta);
      }, { signal: events.signal });
      (scene.closest('.shopify-section') || scene).addEventListener('shopify:section:unload', function () {
        stopVideoTick();
        cancelAnimationFrame(quotePerfTick);
        quoteMotions.forEach(function (motion) { motion.cancel(); });
        finishStackPerf();
        stackMotions.forEach(function (motion) { motion.cancel(); });
        if (video) video.pause();
        if (videoObjectURL) URL.revokeObjectURL(videoObjectURL);
        events.abort();
        if (promptObserver) promptObserver.disconnect();
        scene.querySelectorAll('[data-mnl-quiz-orb-video]').forEach(function (orb) { orb.pause(); });
        clearTimeout(timer);
        clearTimeout(exitTimer);
        clearTimeout(resultTimer);
        clearTimeout(choiceTimer);
      }, { once: true });

      function collect() {
        var out = [];
        steps.forEach(function (step, i) {
          var a = answers[i];
          if (a && a.label) out.push(a);
        });
        return out;
      }

      /* Waiting-frame tile stack: the same node at four scales, matching the design (item 81). The latest answer is on top. */
      function paintStack(step) {
        var box = step.querySelector('[data-mnl-quiz-stack]');
        if (!box) return;
        var collected = collect();
        // Reflection, chosen identity, current state, then friction (Figma).
        var list = [collected[3], collected[1], collected[0], collected[2]].filter(Boolean);
        var scales = [1.218, 1.088, 0.968, 0.842];
        var fades = [0.8, 0.6, 0.4, 0.2];
        var faded = box.classList.contains('is-faded');
        if (location.hostname === '127.0.0.1' && new URLSearchParams(location.search).has('quiz_perf')) {
          var last = performance.now();
          function sample(now) {
            stackPerfTimes.push(now - last); last = now;
            stackPerfTick = requestAnimationFrame(sample);
          }
          stackPerfTick = requestAnimationFrame(sample);
        }
        box.classList.toggle('is-flowing', !reduced);
        box.textContent = '';
        list.forEach(function (a, i) {
          var chip = document.createElement('div');
          chip.className = 'mnl-quiz__chip';
          chip.style.setProperty('--mnl-s', scales[i]);
          chip.style.zIndex = String(4 - i);
          var glass = document.createElement('span');
          glass.className = 'mnl-quiz__chip-glass';
          glass.setAttribute('aria-hidden', 'true');
          glass.appendChild(document.createElement('span'));
          chip.appendChild(glass);
          if (faded) chip.style.opacity = fades[i];
          if (a.icon) {
            var img = document.createElement('img');
            img.src = a.icon; img.alt = '';
            chip.appendChild(img);
          }
          var span = document.createElement('span');
          span.className = 'mnl-quiz__chip-text';
          span.textContent = a.label;
          chip.appendChild(span);
          box.appendChild(chip);
          if (!reduced) {
            // Compact stack: gentle stagger, a readable hold, then a short drift.
            var duration = 3000;
            var delay = 160 + i * 220;
            var motion = chip.animate([
              { opacity: 0, transform: 'translateY(24px)', filter: 'blur(5px)', offset: 0 },
              { opacity: 1, transform: 'translateY(0px)', filter: 'blur(0px)', offset: .24 },
              { opacity: 1, transform: 'translateY(-3px)', filter: 'blur(0px)', offset: .66 },
              { opacity: .65, transform: 'translateY(-14px)', filter: 'blur(3px)', offset: .86 },
              { opacity: 0, transform: 'translateY(-30px)', filter: 'blur(9px)', offset: 1 }
            ], { duration: duration, delay: delay, easing: 'linear', fill: 'both' });
            stackMotions.push(motion);
          }
        });
      }

      /* Decode the next frame's background ahead of time: decoding a 2880 × 1724 JPEG during its display frame can cost up to 100 ms and cause a visible hitch. */
      function warm(i) {
        // All mobile question screens use the same current-state photograph.
        var last = mobileQuiz.matches ? 1 : i + 1;
        if (mobileQuiz.matches) i = 1;
        for (var k = i; k <= last && k < bgs.length; k++) {
          var img = bgs[k];
          if (img && !img.dataset.mnlWarm && img.decode) {
            img.dataset.mnlWarm = '1';
            img.decode().catch(function () {});
          }
        }
      }

      var changing = false;
      var backgroundAnimation = null;
      async function show(i) {
        if (changing || i < 0 || i >= steps.length || i === at) return;
        clearTimeout(choiceTimer);
        if (at < 0 || reduced) { commitStep(i); return; }
        // Opacity on a backdrop-filter ancestor creates a new backdrop root:
        // the cards then blur an empty layer until the fade finishes (Safari).
        // Mobile photo screens share one scene; switch without fading/moving
        // their glass surfaces or waiting for unrelated SVG displacement maps.
        if (mobileQuiz.matches && i < 5 && at < 5) {
          var mobileStarted = performance.now();
          var mobileFrom = at;
          commitStep(i);
          if (location.hostname === '127.0.0.1' && new URLSearchParams(location.search).has('quiz_perf')) {
            var commitMs = performance.now() - mobileStarted;
            var mobileFrames = [], previousFrame = 0;
            requestAnimationFrame(function measureMobileFrame(stamp) {
              if (previousFrame) mobileFrames.push(stamp - previousFrame);
              previousFrame = stamp;
              if (mobileFrames.length < 30) { requestAnimationFrame(measureMobileFrame); return; }
              mobileFrames.sort(function (a, b) { return a - b; });
              console.debug('Quiz mobile transition', JSON.stringify({ from: mobileFrom, to: i,
                commitMs: commitMs, p95: mobileFrames[28],
                over32: mobileFrames.filter(function (t) { return t > 32; }).length }));
            });
          }
          return;
        }
        changing = true;
        // Local preview diagnostic; never enabled on the storefront.
        var frameTimes = [], frameTick = 0, lastFrame = performance.now();
        var measureFrames = location.hostname === '127.0.0.1' && new URLSearchParams(location.search).has('quiz_perf');
        function sampleFrame(now) {
          frameTimes.push(now - lastFrame); lastFrame = now;
          frameTick = requestAnimationFrame(sampleFrame);
        }
        if (measureFrames) frameTick = requestAnimationFrame(sampleFrame);
        var outgoing = steps[at];
        var incoming = steps[i];
        var cardEntrances = [];
        scene.classList.add('is-changing');
        try {
          // Keep the current screen intact until the next photograph is decoded.
          var nextBg = bgs[i];
          await Promise.all([mobileQuiz.matches ? Promise.resolve() : glassReady, nextBg && nextBg.decode ? nextBg.decode().catch(function () {}) : Promise.resolve()]);
          if (!scene.isConnected) return;
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
          outgoing.inert = true;
          outgoing.style.willChange = 'opacity';
          var fadeOut = outgoing.animate([{ opacity: 1 }, { opacity: 0 }],
            { duration: 180, easing: 'ease-in', fill: 'forwards' });
          await fadeOut.finished;
          commitStep(i);
          incoming.querySelectorAll('[data-mnl-quiz-option]').forEach(function (card, index) {
            card.style.willChange = 'transform, opacity';
            var motion = card.animate([
              { opacity: 0, transform: 'translate3d(0, 20px, 0)' },
              { opacity: 1, transform: 'translate3d(0, 0, 0)' }
            ], { duration: 680, delay: 100 + index * 70,
              easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both' });
            cardEntrances.push({ card: card, motion: motion });
          });
          fadeOut.cancel();
          outgoing.style.willChange = '';
          incoming.inert = true;
          incoming.style.willChange = 'opacity';
          var fadeIn = incoming.animate([{ opacity: 0 }, { opacity: 1 }],
            { duration: 420, easing: 'cubic-bezier(.2,.7,.2,1)' });
          await Promise.all([fadeIn.finished].concat(cardEntrances.map(function (entry) { return entry.motion.finished; })));
          if (backgroundAnimation) await backgroundAnimation.finished.catch(function () {});
        } finally {
          cardEntrances.forEach(function (entry) {
            entry.motion.cancel();
            entry.card.style.willChange = '';
          });
          incoming.inert = false;
          incoming.style.willChange = '';
          outgoing.style.willChange = '';
          changing = false;
          if (measureFrames) {
            cancelAnimationFrame(frameTick);
            frameTimes.sort(function (a, b) { return a - b; });
            console.debug('Quiz transition frames', JSON.stringify({ from: steps.indexOf(outgoing), to: i,
              count: frameTimes.length, median: frameTimes[Math.floor(frameTimes.length / 2)],
              p95: frameTimes[Math.floor(frameTimes.length * .95)],
              over32: frameTimes.filter(function (t) { return t > 32; }).length }));
          }
          scene.classList.remove('is-changing');
          var input = incoming.querySelector('[data-mnl-quiz-input]');
          if (input && at === i) input.focus({ preventScroll: true });
        }
      }

      function commitStep(i) {
        if (i < 0 || i >= steps.length || i === at) return;
        clearTimeout(timer);
        clearTimeout(exitTimer);
        clearTimeout(resultTimer);
        clearTimeout(choiceTimer);
        finishStackPerf();
        stackMotions.forEach(function (motion) { motion.cancel(); });
        stackMotions = [];
        resultProgress = 0;
        resultLeaving = false;
        warm(i + 1);
        at = i;
        videoAt(i);
        var step = steps[i];
        var type = step.getAttribute('data-mnl-quiz-type');
        startExamples(step);
        scene.querySelectorAll('[data-mnl-quiz-orb-video]').forEach(function (orb) {
          if (step.contains(orb) && !reduced) {
            orb.muted = true;
            var playing = orb.play();
            if (playing) playing.catch(function () {});
          } else orb.pause();
        });
        steps.forEach(function (s, k) {
          s.classList.toggle('is-on', k === i);
          s.inert = k !== i;
        });
        var previousBg = scene.querySelector('.mnl-quiz__bg.is-on');
        var nextBg = bgs[i];
        if (changing && !mobileQuiz.matches && (previousBg || nextBg) && previousBg !== nextBg && !reduced) {
          // Composite the incoming photo over an opaque outgoing photo, avoiding
          // the dark dip caused by fading both layers at the same time.
          var animatedBg = nextBg || previousBg;
          animatedBg.style.zIndex = '1';
          backgroundAnimation = animatedBg.animate(nextBg ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
            { duration: 1100, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' });
          backgroundAnimation.finished.then(function () {
            bgs.forEach(function (b) { b.classList.toggle('is-on', b === nextBg); });
            animatedBg.style.zIndex = '';
            backgroundAnimation.cancel();
            backgroundAnimation = null;
          }).catch(function () {});
        } else {
          bgs.forEach(function (b) { b.classList.toggle('is-on', b === nextBg); });
        }
        scene.classList.toggle('is-tall', type === 'offer');
        requestAnimationFrame(measureVideo);

        if (type === 'wait') paintStack(step);
        if (type === 'result') {
          animateQuote(step, false);
          scheduleResultAdvance();
          step.style.setProperty('--mnl-result-opacity', 1);
          step.style.setProperty('--mnl-result-y', '0px');
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        }
        if (type === 'wait') {
          var hold = parseInt(step.getAttribute('data-mnl-quiz-hold'), 10);
          if (!(hold > 0)) hold = 2600;
          if (video && ranges[i]) hold = Math.max(hold, (ranges[i][1] - ranges[i][0]) / fps * 1000);
          timer = setTimeout(function advanceWhenReady() {
            if (video && !video.error && !reduced) { timer = setTimeout(advanceWhenReady, 150); return; }
            show(i + 1);
          }, hold);
        }
        if (type === 'write') {
          var input = step.querySelector('[data-mnl-quiz-input]');
          if (input && !changing) input.focus({ preventScroll: true });
        }
      }

      steps.forEach(function (step, i) {
        var type = step.getAttribute('data-mnl-quiz-type');
        var go = step.querySelector('[data-mnl-quiz-next]');
        var back = step.querySelector('[data-mnl-quiz-back]');

        if (type === 'question') {
          var cards = Array.prototype.slice.call(step.querySelectorAll('[data-mnl-quiz-option]'));
          var gestureX = 0, gestureY = 0, draggedChoice = false;
          step.addEventListener('pointerdown', function (event) {
            gestureX = event.clientX; gestureY = event.clientY; draggedChoice = false;
          }, { passive: true });
          step.addEventListener('pointermove', function (event) {
            if (Math.abs(event.clientX - gestureX) > 10 || Math.abs(event.clientY - gestureY) > 10) draggedChoice = true;
          }, { passive: true });
          step.addEventListener('pointercancel', function () { draggedChoice = true; });
          cards.forEach(function (card) {
            card.addEventListener('click', function (event) {
              if (changing || at !== i || (event.detail !== 0 && draggedChoice)) return;
              clearTimeout(choiceTimer);
              cards.forEach(function (c) {
                c.classList.toggle('is-on', c === card);
                c.setAttribute('aria-checked', c === card ? 'true' : 'false');
              });
              answers[i] = {
                label: card.getAttribute('data-mnl-quiz-label') || '',
                icon: card.getAttribute('data-mnl-quiz-icon') || ''
              };
              step.classList.add('is-answered');
              if (go) go.disabled = false;
              // Briefly show the selected state before advancing automatically.
              choiceTimer = setTimeout(function () {
                if (at === i) show(i + 1);
              }, reduced ? 0 : 220);
            });
          });
        }

        if (type === 'write') {
          var input = step.querySelector('[data-mnl-quiz-input]');
          var field = step.querySelector('.mnl-quiz__field');
          if (input) {
            input.addEventListener('input', function () {
              var has = input.value.trim().length > 0;
              if (field) field.classList.toggle('is-filled', has);
              answers[i] = { label: input.value.trim(), icon: writeIcon };
            });
            input.addEventListener('keydown', function (e) {
              if (e.key === 'Enter' && go && !go.disabled) go.click();
            });
          }
        }

        if (type === 'wait') {
          step.addEventListener('click', function (e) { if (e.target.closest('a')) return; if (!video || video.error || segmentHeld) show(i + 1); });
        }

        if (type === 'offer') {
          var tabs = Array.prototype.slice.call(step.querySelectorAll('[data-mnl-quiz-tab]'));
          var swaps = Array.prototype.slice.call(
            step.querySelectorAll('[data-mnl-quiz-price],[data-mnl-quiz-note],[data-mnl-quiz-renew]'));
          tabs.forEach(function (tab, k) {
            tab.addEventListener('click', function () {
              var key = k === 0 ? 'year' : 'month';
              tabs.forEach(function (o, j) {
                o.classList.toggle('is-on', j === k);
                o.setAttribute('aria-pressed', j === k ? 'true' : 'false');
              });
              swaps.forEach(function (el) {
                var v = el.getAttribute('data-' + key);
                if (v !== null && v !== '') el.textContent = v;
              });
            });
          });
        }

        if (go) go.addEventListener('click', function () { show(i + 1); });
        if (back) back.addEventListener('click', function () { show(i - 1); });
      });

      var waitlist = scene.querySelector('[data-mnl-waitlist]');
      var waitlistTrigger = scene.querySelector('[data-mnl-waitlist-open]');
      if (waitlist && waitlistTrigger) {
        var waitlistCard = waitlist.querySelector('[role="dialog"]');
        var waitlistSiblings = Array.prototype.slice.call(scene.children).filter(function (el) { return el !== waitlist; });
        var previousInert = [];
        function closeWaitlist() {
          waitlist.hidden = true;
          waitlistSiblings.forEach(function (el, i) { el.inert = previousInert[i]; });
          waitlistTrigger.focus({ preventScroll: true });
        }
        var openWaitlist = function () {
          previousInert = waitlistSiblings.map(function (el) { return el.inert; });
          waitlistSiblings.forEach(function (el) { el.inert = true; });
          waitlist.hidden = false;
          waitlist.scrollTop = 0;
          waitlist.scrollLeft = 0;
          if (location.hostname === '127.0.0.1' && new URLSearchParams(location.search).has('quiz_perf')) {
            var openedAt = performance.now(), lastFrame = openedAt, popupFrames = [];
            requestAnimationFrame(function samplePopup(now) {
              popupFrames.push(now - lastFrame); lastFrame = now;
              if (now - openedAt < 500) requestAnimationFrame(samplePopup);
              else {
                popupFrames.sort(function (a, b) { return a - b; });
                console.debug('Quiz popup frames', JSON.stringify({count:popupFrames.length,
                  p95:popupFrames[Math.floor(popupFrames.length * .95)],over32:popupFrames.filter(function (t) { return t > 32; }).length}));
              }
            });
          }
          if (!reduced) waitlistCard.animate([{opacity:0,transform:'translateY(12px)'},{opacity:1,transform:'translateY(0)'}], {duration:320,easing:'ease-out'});
          waitlistCard.focus({ preventScroll: true });
        };
        waitlistTrigger.addEventListener('click', openWaitlist, { signal: events.signal });
        waitlist.querySelectorAll('[data-mnl-waitlist-dismiss]').forEach(function (button) {
          button.addEventListener('click', closeWaitlist, { signal: events.signal });
        });
        waitlist.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeWaitlist(); }
          if (event.key !== 'Tab') return;
          var controls = Array.prototype.slice.call(waitlistCard.querySelectorAll('input:not([type="hidden"]),button,a[href]'));
          var first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && (document.activeElement === first || document.activeElement === waitlistCard)) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }, { signal: events.signal });
        waitlist.querySelector('form').addEventListener('submit', function () {
          waitlist.querySelector('[data-mnl-waitlist-tags]').value = waitlist.querySelector('[data-mnl-waitlist-tags]').dataset.consentBase +
            (waitlist.querySelector('[data-mnl-waitlist-marketing]').checked ? ',newsletter,product-updates' : '');
        }, { signal: events.signal });
      }
      warm(0);
      show(0);
      if (openWaitlist && waitlist.querySelector('[data-mnl-waitlist-feedback]')) openWaitlist();
    });
  }

  function initCaptureNote(root) {
    root.querySelectorAll('[data-mnl-capture-note]:not([data-mnl-note-on])').forEach(function (note) {
      note.setAttribute('data-mnl-note-on', '1');
      if (reduced || !('IntersectionObserver' in window)) return;
      var card = note.closest('.mnl-exp-cards__card');
      if (!card) return;
      var visible = false;
      var fontReady = !document.fonts;
      var played = false;
      var disposed = false;
      note.classList.add('is-ready');

      function play() {
        if (disposed || played || !visible || !fontReady) return;
        played = true;
        note.classList.add('is-playing');
      }

      var noteObserver = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.6;
        play();
      }, { threshold: 0.6, rootMargin: '-56px 0px 0px' });
      var cardObserver = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) {
          visible = false;
          played = false;
          note.classList.remove('is-playing');
        }
      }, { threshold: 0 });
      noteObserver.observe(note);
      cardObserver.observe(card);

      // Do not play the reveal with a fallback font before Photograph loads.
      if (document.fonts) {
        document.fonts.ready.then(function () {
          fontReady = true;
          play();
        });
      }
      note._mnlCaptureCleanup = function () {
        disposed = true;
        noteObserver.disconnect();
        cardObserver.disconnect();
        note.classList.remove('is-ready', 'is-playing');
      };
    });
  }

  // The dot and its highlight share the SVG ribbon's centreline, including
  // its horizontal reflection. No independent x/y tween can drift off it.
  function initMoment(root) {
    root.querySelectorAll('[data-mnl-moment]:not([data-mnl-moment-on])').forEach(function (scene) {
      scene.setAttribute('data-mnl-moment-on', '1');
      var path = scene.querySelector('[data-mnl-moment-path]');
      var dot = scene.querySelector('.mnl-hiw-moment__dot');
      var stamp = scene.querySelector('.mnl-hiw-moment__stamp');
      var light = scene.querySelector('.mnl-hiw-moment__curve radialGradient');
      var mobileLight = scene.querySelector('[data-mnl-moment-mobile-light]');
      var frames = Array.from(scene.querySelectorAll('[data-mnl-moment-frame]'));
      var runway = scene.querySelector('.mnl-hiw-moment__runway');
      var length = path.getTotalLength();
      var mobile = window.matchMedia("(max-width: 767px)");
      var stage = scene.querySelector(".mnl-hiw-moment__stage");
      var timeline = scene.querySelector('.mnl-hiw-moment__line');
      var zoom = 1, top = 0, travel = 806, pending = 0, last = -1, current = -1;
      var sceneWidth = 402, viewportWidth = 0, viewportHeight = 0;
      function atX(x) {
        var lo = 0, hi = length;
        for (var i = 0; i < 24; i++) {
          var mid = (lo + hi) / 2;
          if (1623.833 - path.getPointAtLength(mid).x > x) lo = mid;
          else hi = mid;
        }
        return (lo + hi) / 2;
      }
      var start = atX(parseFloat(scene.dataset.startX) || 439);
      var end = atX(1320);
      function render() {
        pending = 0;
        var progress = Math.max(0, Math.min(1, (top - scene.getBoundingClientRect().top / zoom) / travel));
        if (Math.abs(progress - last) < 0.00001) return;
        last = progress;
        var x, y;
        if (mobile.matches) {
          x = sceneWidth * (.29 + .49 * progress);
          var curveX = x / sceneWidth * 402;
          var t = (curveX + 80) / 562;
          y = 44 + 136 * t * (1 - t);
          if (mobileLight) {
            mobileLight.setAttribute('cx', curveX);
            mobileLight.setAttribute('cy', y);
          }
          var curveScale = timeline.offsetHeight / 110;
          dot.style.transform = 'translate(' + x + 'px,' + (y * curveScale) + 'px)';
          var lineTop = timeline.offsetTop;
          var labelX = Math.max(80, Math.min(sceneWidth - 80, x + 25));
          stamp.style.transform = 'translate(' + labelX + 'px,' + (lineTop + (y - 62) * curveScale) + 'px) translateX(-50%)';
        } else {
          var p = path.getPointAtLength(start + (end - start) * progress);
          x = 1623.833 - p.x; y = 100.505 + p.y;
          scene.style.setProperty('--moment-glow-shift', (progress * 120) + 'px');
          light.setAttribute('cx', p.x);
          light.setAttribute('cy', p.y);
          dot.style.transform = 'translate(' + x + 'px,' + y + 'px)';
          stamp.style.transform = 'translate(' + x + 'px,' + (y - 80.106) + 'px) translateX(-50%)';
        }
        var index = Math.min(frames.length - 1, Math.floor(progress * frames.length));
        if (index === current) return;
        current = index;
        frames.forEach(function (frame, i) {
          frame.classList.toggle('is-active', i === index);
          frame.setAttribute('aria-hidden', i === index ? 'false' : 'true');
          frame.inert = i !== index;
        });
        stamp.querySelector('.mnl-hiw-moment__stamp-num').textContent = frames[index].dataset.number;
        stamp.querySelector('.mnl-hiw-moment__stamp-name').textContent = frames[index].dataset.label;
      }
      function queue() { if (!pending) pending = requestAnimationFrame(render); }
      function measure() {
        // Safari's collapsing address bar fires resize while scrolling.
        // Keep the sticky geometry stable until the viewport width changes.
        var width = document.documentElement.clientWidth;
        if (mobile.matches && width === viewportWidth) return;
        viewportWidth = width;
        viewportHeight = window.innerHeight;
        sceneWidth = scene.clientWidth;
        zoom = scene.getBoundingClientRect().width / scene.offsetWidth || 1;
        if (mobile.matches) {
          scene.style.setProperty('--moment-steps', frames.length);
          scene.style.setProperty('--moment-swipe-height', window.innerHeight + 'px');
          // Keep the complete outcome above the fixed access button, including
          // the heading. Fit the reference composition to the available screen.
          var header = document.querySelector('[data-mnl-header]');
          var headerHeight = header ? header.getBoundingClientRect().height / zoom : 68;
          var available = viewportHeight / zoom - headerHeight - 76;
          var fit = Math.min(1, sceneWidth / 402, available / 730);
          fit = Math.max(0.35, fit);
          scene.style.setProperty('--moment-fit', fit);
          scene.style.setProperty('--moment-spacing', fit * fit);
          scene.style.setProperty('--moment-heading-top', headerHeight + 'px');
          var rowTop = headerHeight + 325 * fit;
          scene.style.setProperty('--moment-row-top', rowTop + 'px');
          frames.forEach(function (frame) {
            var column = frame.querySelector('.mnl-hiw-moment__col');
            var room = viewportHeight / zoom - 76 - rowTop - 35 * fit * fit - column.offsetHeight;
            frame.style.setProperty('--moment-photo-height', Math.max(1, Math.min(193 * fit, room)) + 'px');
          });
        }
        top = Math.min(0, window.innerHeight / zoom - stage.offsetHeight);
        if (mobile.matches) {
          top = 0;
        }
        travel = runway.offsetHeight || 806;
        scene.style.setProperty('--moment-top', top + 'px');
        last = -1;
        queue();
      }
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', measure);
      measure();
      scene._mnlMomentCleanup = function () {
        cancelAnimationFrame(pending);
        window.removeEventListener('scroll', queue);
        window.removeEventListener('resize', measure);
      };
    });
  }

  var unicornSDKPromise;
  function initUnicorn(root) {
    root.querySelectorAll('[data-mnl-unicorn]:not([data-mnl-unicorn-on])').forEach(function (host) {
      host.setAttribute('data-mnl-unicorn-on', '1');
      if (reduced) return;
      var instance, disposed = false, started = false;
      function load() {
        if (started || disposed) return;
        started = true;
        if (!unicornSDKPromise) {
          unicornSDKPromise = new Promise(function (resolve, reject) {
            if (window.UnicornStudio) { resolve(window.UnicornStudio); return; }
            var script = document.createElement('script');
            script.src = host.dataset.sdkUrl;
            script.async = true;
            script.onload = function () { resolve(window.UnicornStudio); };
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        unicornSDKPromise.then(function (sdk) {
          if (disposed) return;
          return sdk.addScene({
            elementId: host.id, filePath: host.dataset.sceneUrl,
            fps: 60, dpi: 1, scale: 1,
            interactivity: { mouse: { disabled: true } }
          });
        }).then(function (scene) {
          if (!scene) return;
          if (disposed) { scene.destroy(); return; }
          instance = scene;
          host.classList.add('is-ready');
        }).catch(function (error) {
          // Keep the original artwork if WebGL or an asset is unavailable.
          console.warn('MANILAI scene could not load', error);
        });
      }
      var observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) load();
      }, { rootMargin: '300px' });
      observer.observe(host);
      host._mnlUnicornCleanup = function () {
        disposed = true;
        observer.disconnect();
        if (instance) instance.destroy();
      };
    });
  }

  function initMobileSystem(root) {
    root.querySelectorAll('.mnl-system:not([data-mobile-system])').forEach(function (scene) {
      scene.dataset.mobileSystem = '1';
      var view = scene.querySelector('.mnl-system__viewport');
      var cards = Array.from(scene.querySelectorAll('.mnl-system__card'));
      var dots = Array.from(scene.querySelectorAll('[data-system-dot]'));
      var media = window.matchMedia('(max-width: 767px)'), active = 0, visible = false;
      function timing() { scene.classList.toggle('is-timing', media.matches && visible && !document.hidden); }
      function select(i, move) {
        active = (i + cards.length) % cards.length;
        dots.forEach(function (dot, n) {
          dot.classList.toggle('is-active', n === active);
          dot.setAttribute('aria-current', n === active ? 'true' : 'false');
        });
        if (move) view.scrollTo({ left: cards[active].offsetLeft - cards[0].offsetLeft, behavior: reduced ? 'instant' : 'smooth' });
      }
      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { select(i, true); });
        dot.firstElementChild.addEventListener('animationend', function () { if (media.matches) select(active + 1, true); });
      });
      scene.querySelector('[data-system-prev]').addEventListener('click', function () { select(active - 1, true); });
      scene.querySelector('[data-system-next]').addEventListener('click', function () { select(active + 1, true); });
      view.addEventListener('scrollend', function () {
        if (!media.matches) return;
        var index = Math.round(view.scrollLeft / (view.clientWidth + 15));
        if (index !== active) select(index, false);
      });
      new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; timing(); }, { threshold: .5 }).observe(view);
      document.addEventListener('visibilitychange', timing);
      media.addEventListener('change', timing);
      select(0, false);
    });
  }

  function initWaitlistFeedback(root) {
    (root || document).querySelectorAll('.mnl-waitlist__form').forEach(function (form) {
      var pending = form.querySelector('[data-mnl-waitlist-pending]');
      var submit = form.querySelector('button[type="submit"]');
      if (!pending || !submit || form.dataset.mnlFeedbackReady) return;
      form.dataset.mnlFeedbackReady = 'true';
      var labelNode = submit.querySelector('.mnl-button-label') || submit;
      var label = labelNode.textContent;
      function waiting() {
        if (!form.checkValidity()) return;
        pending.hidden = false;
        labelNode.textContent = 'Checking…';
      }
      // Shopify can pause submission for CAPTCHA before navigating to its response.
      form.addEventListener('submit', waiting, true);
      submit.addEventListener('click', waiting);
      form.addEventListener('input', function () {
        pending.hidden = true;
        labelNode.textContent = label;
      });
    });
  }

  function initAll(root) {
    initMobileSystem(root);
    root = root || document;
    initReveal(root);
    initScrollText(root);
    initTrack(root);
    initPopup(root);
    initScrub(root);
    initApp(root);
    initClock(root);
    initDrag(root);
    initSteps(root);
    initVideoStops(root);
    initNorthVideo(root);
    initVideo(root);
    initStepper(root);
    initMobileRitual(root);
    initTelling(root);
    initDays(root);
    initInsightSlider(root);
    initMoment(root);
    initLayers(root);
    initGrow(root);
    initUnicorn(root);
    initPlan(root);
    initFaq(root);
    initQuiz(root);
    initWaitlistFeedback(root);
    initFill(root);
    initDeck(root);
    initEvidenceWheel(root);
    initSignals(root);
    initMindSlider(root);
    initLoop(root);
    initQuotes(root);
    initCaptureNote(root);
  }

  function boot() {
    function syncCaptchaScale() {
      // Native mobile sections reset body zoom to 1; desktop uses the design grid.
      var zoom = parseFloat(getComputedStyle(document.body).zoom) || 1;
      document.documentElement.style.setProperty('--mnl-captcha-zoom', String(1 / zoom));
    }
    syncCaptchaScale();
    window.addEventListener('resize', syncCaptchaScale, { passive: true });
    initAll(document);
    initHeaderScheme();
    initHeaderHide();
    // Intentionally after initAll: step-controller handlers must be registered
    // before ours so captured wheel events never reach us.
    initSmoothScroll();
    if (gsap && ScrollTrigger) {
      // Lazy-loaded images change page height. Recalculate trigger
      // boundaries after loading to keep them aligned.
      window.addEventListener('load', function () { ScrollTrigger.refresh(); });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: reinitialize the section after rerendering. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    if (gsap && ScrollTrigger) ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-mnl-unicorn]').forEach(function (host) {
      if (host._mnlUnicornCleanup) host._mnlUnicornCleanup();
    });
    e.target.querySelectorAll('[data-mnl-moment]').forEach(function (scene) {
      if (scene._mnlMomentCleanup) scene._mnlMomentCleanup();
    });
    e.target.querySelectorAll('[data-mnl-video-stops], [data-mnl-north-video]').forEach(function (video) {
      if (video._mnlNorthCleanup) video._mnlNorthCleanup();
      if (video._mnlStopsCleanup) video._mnlStopsCleanup();
    });
    e.target.querySelectorAll('[data-mnl-evidence-wheel]').forEach(function (scene) {
      if (scene._mnlEvidenceCleanup) scene._mnlEvidenceCleanup();
    });
    e.target.querySelectorAll('[data-mnl-mind-slider]').forEach(function (scene) {
      if (scene._mnlMindCleanup) scene._mnlMindCleanup();
    });
    e.target.querySelectorAll('[data-mnl-signals]').forEach(function (scene) {
      if (scene._mnlSignalsCleanup) scene._mnlSignalsCleanup();
    });
    e.target.querySelectorAll('[data-mnl-capture-note]').forEach(function (note) {
      if (note._mnlCaptureCleanup) note._mnlCaptureCleanup();
    });
  });

  window.MANILAI = { init: initAll };
})();
